// ============================================================================
// IMPORTS SECTION
// ============================================================================
// React core hooks and utilities
import React, { useState, useRef, useEffect, useMemo, useCallback } from "react";
// Form management
import { useFormik, FormikProps } from "formik";
import * as Yup from "yup";
import { phoneValidation } from "validationSchemas/common";
// UI Components
import Select from "react-select";
import { AsyncPaginate, LoadOptions } from "react-select-async-paginate";
import { components } from "react-select";
// Routing
import { useNavigate, useParams, useLocation } from "react-router-dom";
// Toast notifications
import { showSuccess, showError } from "toast/toast";
// Product selection
import ProductPickerModal, {
    type StudioBatchVariantPreviewMap,
    type StudioCustomizationFlowProps,
} from "components/ordercheckoutpop";
import { downloadWorkDriveFile, getCustomizationBatch } from "api/realesyStudioApi";
// Custom UI components
import { UIInputPro, UIReactSelectPro } from "components/OrdersSelectInput";
// Image handling
import { getImageSrc, useVariantImageUrlMap } from "components/Tableimageshelpers";
import { batchPresignUrls } from "components/Imageshelper";
// Assets
import ProductSelection from "assets/images/brands/ProductSelection.png";
import imagecustomRounded from "assets/images/imagecustomrounded.png";
// HTTP client
import {
    fetchAgentsByOfficeId,
    fetchBrokerageFirms as fetchBrokerageFirmsApi,
    fetchGeneralSettings,
    fetchOfficeByBrokerageId,
    fetchOrderById,
    fetchShippingCosts as fetchShippingCostsApi,
    fetchSignageAgentsSelect2,
    fetchSignageInventoryProductsList,
    fetchUserAddresses,
    resolveMembershipAddressesRouteId,
    resolvePaymentMethodsMembershipId,
    resolveSalesOrderTeamMemberMembershipId,
    resolveSalesOrderBrokerageFirmOrgId,
    resolveSalesOrderAgentMembershipId,
    resolveSalesOrderAddressId,
    isSalesOrderSavedAddress,
    mergeSalesOrderSavedAddresses,
    buildSalesAddressSelectOptions,
    patchOrderTax,
    salesOrderRequest,
    searchUsers,
    SALES_ORDER_API_BASE,
    salesOrderUrls,
} from "api/salesOrderApi";
import {
    normalizeCustomizationBatchContexts,
    studioBatchVariantPreviewsFromBatchItems,
    studioBatchVariantPreviewsFromCartLines,
    studioRestoreAddressBindings,
} from "utils/signageStudioBatchRestore";
import { buildSignageEditOrderStudioSnapshot } from "utils/signageEditOrderStudioSnapshot";
// Type definitions
import {
    OrderItem,
    Product,
    Agent,
    Office,
    Order,
    License,
    Address,
    ShippingCost,
} from "types";
// Tax and shipping components
import ShippingOptions, { type ShippingOptionsV2Handle, type ShipSelectedIds } from "components/Tax/ShippingOptionsui";
import SpecialFeePicker, { type SpecialFeePickerV2Handle } from "components/Tax/SpecialFeeui";
import ProjectGetCard from "components/projectgetcard";
import { DiscountAmountDisplay, getSignageOrderLinePricing } from "components/DiscountLabelBadge";
import CustomItemModal from "components/CustomItemModal";
import { GENERAL_SETTINGS_KEYS } from "pages/signage/SignageGeneralSettings";
// TaxCloud integration
import {
    GetVersionOrdernumber,
    Refund,
    type TaxCloudAddress
} from "components/specialFeeTaxCloud";
// Email service
// Error handling
import { handleApiError } from "utils/errorHandler";
import { safeConsole, createSafeNavigate } from "utils/envUtils";
import { getAuthCookie } from "../../utils/authCookies";
import type {
    TeamMemberOption,
    OfficeAgentOption,
    SelectedProduct,
    ProductObj,
    PickerPayload,
    ShipPayload,
    FeePayload,
} from "components/SignageEditOrder/types";
import { pickImages, n, r2, fetchNewUniqueId } from "components/SignageEditOrder/signageEditOrderHelpers";
import { buildSignageEditOrderUpdatePayload } from "components/SignageEditOrder/buildSignageEditOrderUpdatePayload";
import { runSignageEditOrderAfterSuccessfulPatch } from "components/SignageEditOrder/runSignageEditOrderAfterSuccessfulPatch";
import { SignageEditOrderInstructionsAndTotals } from "components/SignageEditOrder/SignageEditOrderInstructionsAndTotals";
import { uploadOrderDocumentsToOrder } from "components/SignageCreateOrder/SignageCreateOrderInstructionsAndTotals";
import PONumber from "components/PONumber";
import CompactApprovedProofCard from "components/CompactApprovedProofCard";
import {
    fetchProjectApprovedMessage,
    resolveApprovedProofVersionSummary,
} from "components/proejctsandquoteapicalls";
import { isSignagePortalRoute } from "utils/isSignagePortalRoute";
import { getVisibleCustomInformationRowsForDisplay } from "utils/installationProductCustomizationDisplay";
import {
    postSalesOrderResolve,
    buildSalesResolvePayload,
    pickSalesResolveBuyerContext,
    applySalesResolveResponse,
    mergeSelectedProductsFromResolved,
    overlaySalesSubmitFromResolved,
    remapSalesResolveLineMaps,
    getSalesOrderDiscountFieldDisplay,
    type ResolvedSalesOrder,
} from "orderResolve";
import {
    readResolveTaxable,
    resolveCatalogProductLineTaxable,
    resolveShippingTaxableForPayload,
    resolveSpecialFeeTaxableForPayload,
} from "orderResolve/utils/readResolveTaxable";
import { getSalesResolveDiscountCode } from "utils/salesResolveDiscountCode";
import {
  collectOrderLineObjectIds,
  newOrderLineObjectId,
  normalizeOrderLineObjectId,
  salesLineIdentityKey,
} from "utils/orderLineObjectId";
import {
    applyUserLockedShippingAmounts,
    mergeResolvedShippingTaxIntoLocal,
    shippingOptionIdsKey,
    sumShippingAmount,
} from "orderResolve/utils/shippingAmountSync";

const formatSkuDisplay = (sku?: string | null) => {
    const raw = String(sku ?? "").trim();
    if (!raw) return "-";
    if (/^custom_sku(?:_|$)/i.test(raw)) return "Custom SKU";
    return raw.includes("_") ? raw.split("_").slice(0, -1).join("_") || raw : raw;
};

const SignageEditOrder = () => {
    // ========================================================================
    // ROUTING & PARAMETERS
    // ========================================================================
    const { orderId } = useParams<{ orderId: string }>();
    const navigate = useNavigate();
    const safeNavigate = createSafeNavigate(navigate);
    const location = useLocation();
    const isSignageTeammember = location.pathname.includes('/signageteammember');
    const baseRoute = isSignageTeammember ? '/signageteammember' : '/signage';
    const projectIdFromQuery = useMemo(
        () => new URLSearchParams(location.search).get("project_id") || "",
        [location.search]
    );
    const studioReturnBatchId = useMemo(
        () => new URLSearchParams(location.search).get("batch_id") || "",
        [location.search]
    );
    const studioReturnContextId = useMemo(
        () => new URLSearchParams(location.search).get("context_id") || "",
        [location.search]
    );
    const [linkedProjectId, setLinkedProjectId] = useState<string | null>(null);
    const [linkedProjectNumber, setLinkedProjectNumber] = useState<string | null>(null);
    const userClearedProjectRef = React.useRef(false);

    // console.log("Edit Order Params:", { orderId, isSignageTeammember });

    // ========================================================================
    // AUTHENTICATION & USER INFO
    // ========================================================================
    const token = getAuthCookie("sessionToken");
    const userId = getAuthCookie("userId");
    const userName = getAuthCookie("userName");
    const licenseType = getAuthCookie("licenseType");
    const cartId = localStorage.getItem("cart_id");
    const cartItems = JSON.parse(localStorage.getItem("cartItems") || "[]");
    const organizationId = getAuthCookie("organizationId");
    const effectiveSignageOrgId = organizationId || userId || "";
    const effectiveSignageOrgName = userName || "";
    const membershipId = getAuthCookie("membershipId");
    const globalUserId = getAuthCookie("globalUserId");
    // ========================================================================
    // STATE MANAGEMENT - UI & Form States
    // ========================================================================
    const [order, setOrder] = useState<Order | null>(null);
    const sellerOrgId =
      organizationId ||
      String((order as any)?.seller_org_id ?? (order as any)?.signage_id ?? "");
    const [approvedMessageData, setApprovedMessageData] = useState<any>(null);
    const [hasApprovedMessage, setHasApprovedMessage] = useState(false);
    const [formSubmitting, setFormSubmitting] = useState(false);
    const [uploadProgress, setUploadProgress] = useState<number>(0); // Track upload progress
    const [error, setError] = useState<string>("");
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (userClearedProjectRef.current) return;
        const pid = (order as any)?.project_id;
        if (pid) {
            setLinkedProjectId(String(pid));
            const pnum = (order as any)?.project_number;
            if (pnum != null && pnum !== "") setLinkedProjectNumber(String(pnum));
            return;
        }
        if (projectIdFromQuery) setLinkedProjectId(projectIdFromQuery);
    }, [order?.id, (order as any)?.project_id, projectIdFromQuery]);

    // ========================================================================
    // STATE MANAGEMENT - Buyer & Organization Selection
    // ========================================================================
    const [addressObjects, setAddressObjects] = useState<any[]>([]);
    const addressObjectsRef = React.useRef<any[]>([]);
    addressObjectsRef.current = addressObjects;
    const [brokerageFirms, setBrokerageFirms] = useState<License[]>([]);
    const [officeAgents, setOfficeAgents] = useState<Agent[]>([]);
    const [offices, setOffices] = useState<Office[]>([]);

    // ========================================================================
    // STATE MANAGEMENT - Order Info
    // ========================================================================
    const order_type = "sales";
    const [orderIds, setOrderId] = useState<string>("");
    const lastOrderNumberForVersionRef = useRef<string | null>(null);

    useEffect(() => {
        const orderNumber = order?.order_number;
        if (!token || !orderNumber) return;
        if (lastOrderNumberForVersionRef.current === orderNumber) return;

        const orderNum: string = orderNumber;
        lastOrderNumberForVersionRef.current = orderNum;
        let cancelled = false;

        async function fetchOrderId() {
            try {
                const order_id = await GetVersionOrdernumber(
                    token || "",
                    orderNum as string,
                    order_type
                );
                if (cancelled) return;
                const versionOrderId = (order_id as any)?.full_number || (order_id as any)?.order_number || "";
                setOrderId(versionOrderId);
            } catch (err) {
                if (cancelled) return;
                lastOrderNumberForVersionRef.current = null;
                safeConsole.error("Failed to get version order number:", err);
            }
        }

        fetchOrderId();
        return () => { cancelled = true; };
    }, [token, order?.order_number, order_type]);


    // ========================================================================
    // STATE MANAGEMENT - Busy States
    // ========================================================================
    // Centralized busy state tracking for async operations
    const [busyStates, setBusyStates] = useState({
        shipping: false,      // Shipping options are being calculated
        specialFee: false,    // Special fees are being calculated
        productTax: false,   // Product tax is being calculated
        mainForm: false,      // Main form is being submitted
    });

    // ---------- HYDRATION GUARD ----------
    // ✅ Declare hydratingRef early so it can be used in isAnythingBusy
    const hydratingRef = React.useRef(true);
    // ✅ Track if initial page load is complete (order data fetched)
    const [initialLoadComplete, setInitialLoadComplete] = React.useState(false);

    // ✅ Track initial form values to detect changes
    const initialFormValuesRef = React.useRef<any>(null);
    // ✅ Ignore busy states until order data + child payloads are hydrated
    const isAnythingBusy = React.useMemo(() => {
        if (!initialLoadComplete) return true;
        return formSubmitting || Object.values(busyStates).some(b => b);
    }, [formSubmitting, busyStates, initialLoadComplete]);

    // Callbacks for child components to report their busy state

    const handleShippingBusyChange = React.useCallback((busy: boolean) => {
        setBusyStates(prev => (prev.shipping === busy ? prev : { ...prev, shipping: busy }));
    }, []);

    const handleSpecialFeeBusyChange = React.useCallback((busy: boolean) => {
        setBusyStates(prev => (prev.specialFee === busy ? prev : { ...prev, specialFee: busy }));
    }, []);

    // ✅ Safety timeout: Reset special fee busy state if it's been stuck for more than 10 seconds
    React.useEffect(() => {
        if (!busyStates.specialFee) return;

        const timeout = setTimeout(() => {
            safeConsole.warn("[EDIT:SPECIAL_FEE] Busy state stuck for 10+ seconds, forcing reset");
            setBusyStates(prev => (prev.specialFee ? { ...prev, specialFee: false } : prev));
        }, 10000); // 10 seconds timeout

        return () => clearTimeout(timeout);
    }, [busyStates.specialFee]);


    const [products, setProducts] = useState<Product[]>([]);
    const [ProductObjs, setProductObjs] = useState<ProductObj[]>([]);
    const [selectedProductItem, setSelectedProductItem] = useState<ProductObj | null>(null);
    // const [taxRate, setTaxRate] = useState(order?.tax_details.tax_rate || 0);
    const [selectedOfficeId, setSelectedOfficeId] = useState("");
    const [selectedBrokerageFirmId, setSelectedBrokerageFirmId] = useState("");
    const [shippingCosts, setShippingCosts] = useState<ShippingCost[]>([]);
    const [selectedTaxRate, setSelectedTaxRate] = useState(0);
    const [buyerType, setBuyerType] = useState<string>("");
    const [getstatusTaxType, setGetStatusTaxType] = React.useState("");
    // State for AsyncPaginate selected options
    const [initialSelectedShipIds, setInitialSelectedShipIds] = React.useState<ShipSelectedIds | null>(null);
    const [selectedTeamMemberOption, setSelectedTeamMemberOption] = useState<TeamMemberOption | null>(null);
    const [shipPayload, setShipPayload] = React.useState<ShipPayload>({ shipping_option: [], isUps: false, isManual: false });
    const shipPayloadRef = React.useRef<ShipPayload>({
        shipping_option: [],
        isUps: false,
        isManual: false,
    });
    const createEmptyFeePayload = React.useCallback(
        (): FeePayload => ({
            specialfee: [],
            selected_fee_ids: [],
            total_amount: 0,
            total_tax_amount: 0,
            total_tax_percentage: 0,
        }),
        []
    );
    const [feePayload, setFeePayload] = React.useState<FeePayload>(() => createEmptyFeePayload());
    const feePayloadRef = React.useRef<FeePayload>(createEmptyFeePayload());
    const specialFeeCatalogRef = React.useRef<unknown[]>([]);
    const [editSpecialFeeInitialIds, setEditSpecialFeeInitialIds] = React.useState<string[] | null>(null);
    const [liveShipping, setLiveShipping] = React.useState(0);
    const [liveSpecialFee, setLiveSpecialFee] = React.useState(0);
    // When false → UI shows saved (initialTotals). When true → UI shows live totals from the engine.
    // ✅ Start as false in Edit mode to show saved values first
    const [useLiveTotals, setUseLiveTotals] = React.useState(false);
    const [perItemCouponMap, setPerItemCouponMap] = React.useState<Record<string, number>>({}); // for Product-table "Discount" column
    useEffect(() => {
        let cancelled = false;
        const pid = String(linkedProjectId || "").trim();
        if (!token || !pid) {
            setHasApprovedMessage(false);
            setApprovedMessageData(null);
            return;
        }
        (async () => {
            try {
                const approved = await fetchProjectApprovedMessage(token, pid);
                if (cancelled) return;
                const hasMessage = Boolean(approved?.has_approved_message);
                setHasApprovedMessage(hasMessage);
                setApprovedMessageData(hasMessage ? approved?.data ?? null : null);
            } catch {
                if (cancelled) return;
                setHasApprovedMessage(false);
                setApprovedMessageData(null);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [linkedProjectId, token]);
    // tax per product row
    const [productTaxPctMap, setProductTaxPctMap] = React.useState<Record<string, number>>({});
    const [showTaxDetails, setShowTaxDetails] = React.useState(false);

    // ✅ Manual shipping edit state
    const [isEditingShipping, setIsEditingShipping] = useState(false);
    const [manualShippingAmount, setManualShippingAmount] = useState<string>("");
    // state
    const [userAppliedCoupon, setUserAppliedCoupon] = React.useState(false);

    const [productTotals, setProductTotals] = useState({
        subTotal: 0,     // discounted products subtotal (tax base)
        discount: 0,     // total product discount applied
        productTax: 0,   // product-only tax total
    });
    const [productTaxMap, setProductTaxMap] = useState<Record<string, number>>({});
    // ✅ Store effective prices from API
    const [effectiveMap, setEffectiveMap] = useState<Record<string, number>>({});
    // Key: productId:variantId — base_price from resolve-assignment-discount API
    const [basePriceMap, setBasePriceMap] = useState<Record<string, number>>({});
    // ✅ Track manually edited unit prices
    const [manualUnitPriceMap, setManualUnitPriceMap] = useState<Record<string, number>>({});

    // ========== SALES ORDER RESOLVE ==========
    const lastResolvedSalesRef = React.useRef<ResolvedSalesOrder | null>(null);
    const isRecomputingRef = React.useRef(false);
    const recomputeRequestIdRef = React.useRef(0);
    const pendingRecomputeRef = React.useRef(false);
    const [isRecomputingSales, setIsRecomputingSales] = React.useState(false);
    const [couponInput, setCouponInput] = React.useState("");
    const [couponResolveError, setCouponResolveError] = React.useState("");
    const [resolvedTotalsTick, setResolvedTotalsTick] = React.useState(0);
    const [liveDiscountDisplay, setLiveDiscountDisplay] = React.useState({ code: "", amount: 0 });
    const userClearedCouponRef = React.useRef(false);
    const liveResolvedDiscountRef = React.useRef({ code: "", amount: 0 });
    const recomputeTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
    const scheduleSalesRecomputeRef = React.useRef<
        ((override?: { discountCode?: string; force?: boolean; feePayload?: FeePayload }) => void) | null
    >(null);
    const formikValuesRef = React.useRef<Record<string, unknown>>({});
    const USE_SALES_ORDER_RESOLVE = true;



    // stable helpers
    const asNum = (v: any) => Number(v || 0);
    const money = (n: any) => asNum(n).toFixed(2);

    // for re-render control
    const lastShipJsonRef = React.useRef<string>("");
    const lastFeeJsonRef = React.useRef<string>("");
    // Only update shipping charge when user selects/changes shipping option in UI, not when ShippingOptions re-runs after product add
    const lastKnownShippingAmountRef = React.useRef<number>(0);
    const lastShipOptionIdsRef = React.useRef<string>(""); // sorted option ids to detect "same selection, recalculated amount"
    /** User edited shipping in Order Totals card — qty/rate recalc must not overwrite amount. */
    const userEditedShippingAmountRef = React.useRef(false);

    /** Use this instead of setUseLiveTotals(true) anywhere */
    const enableLiveTotals = React.useCallback(() => {
        if (hydratingRef.current) return;   // 🚫 block during initial mount + GET seed
        setUseLiveTotals(true);
    }, []);

    // build a compact "row key"
    const keyForRow = (p: {
        _id?: string;
        line_item_id?: string;
        productId?: string;
        variantId?: string;
        product_id?: string;
        variant_id?: string;
    }) => salesLineIdentityKey(p);
    const keyFor = (r: {
        _id?: string;
        line_item_id?: string;
        productId?: string;
        variantId?: string;
        product_id?: string;
        variant_id?: string;
    }) => salesLineIdentityKey(r);
    const getCustomProductKey = (
        cp: { _id?: string; line_item_id?: string; product_id: string; variant_id: string },
        idx: number
    ) => {
        const byId = salesLineIdentityKey(cp);
        if (byId && !byId.includes(":")) return byId;
        if (cp.product_id && cp.variant_id && cp.product_id !== "000000000000000000000000") {
            return keyForRow({ ...cp, productId: cp.product_id, variantId: cp.variant_id });
        }
        return `custom:${idx}`;
    };

    // ✅ Get unit price to use: manual > effective > original
    const getUnitPriceToUse = React.useCallback((row: any) => {
        safeConsole.log(row, "changedunit")
        // console.log(manualUnitPriceMap, "manualmap", effectiveMap, "effectivemap" , row )
        const key = keyFor(row);
        if (manualUnitPriceMap[key] !== undefined) {
            return manualUnitPriceMap[key];
        }
        if (effectiveMap[key] !== undefined) {
            return effectiveMap[key];
        }
        const hasChangedUnit = row?.changed_unit_price !== undefined && row?.changed_unit_price !== null;
        const hasUnitDiscount = row?.discount_on_unit_quantity !== undefined && row?.discount_on_unit_quantity !== null;
        const changedNum = hasChangedUnit ? Number(row?.changed_unit_price ?? 0) : 0;
        const unitDiscNum = hasUnitDiscount ? Number(row?.discount_on_unit_quantity ?? 0) : 0;

        // When changed unit / per-unit discount aren't ready (common right after adding a new line),
        // fall back to the original unit price so coupons still allocate correctly.
        if (hasChangedUnit || hasUnitDiscount) {
            const computed = changedNum + unitDiscNum;
            if (Number.isFinite(computed) && computed !== 0) return computed;
        }

        const fallback = Number(row?.unitPrice ?? 0);
        return Number.isFinite(fallback) ? fallback : 0;
    }, [manualUnitPriceMap, effectiveMap]);

    const getEffectiveUnitPrice = React.useCallback((row: any) => {
        const key = keyFor(row);
        if (effectiveMap[key] !== undefined) {
            return effectiveMap[key];
        }
        return Number(row?.unitPrice ?? 0);
    }, [effectiveMap]);

    const getBaseUnitPrice = React.useCallback(
        (row: any) => {
            const key = keyFor(row);
            if (basePriceMap[key] !== undefined) return Number(basePriceMap[key]);
            const productObj = ProductObjs?.find((p) => p.product_id === row.productId);
            const catalogRetail = Number(productObj?.product?.retail_price || 0);
            if (catalogRetail > 0) return catalogRetail;
            if (manualUnitPriceMap[key] === undefined) {
                return Number(row?.unitPrice ?? 0);
            }
            return catalogRetail;
        },
        [basePriceMap, ProductObjs, manualUnitPriceMap]
    );

    // Case 1 (qty → assignment discount): actual base price. Case 2 (manual edit): user-entered price.
    const getUnitRetailPriceForPayload = React.useCallback(
        (row: any) => {
            const key = keyFor(row);
            if (manualUnitPriceMap[key] !== undefined) {
                return Number(manualUnitPriceMap[key] || 0);
            }
            return getBaseUnitPrice(row);
        },
        [manualUnitPriceMap, getBaseUnitPrice]
    );

    const getChangedUnitPriceForPayload = React.useCallback(
        (productId: string, variantId: string, originalUnitPrice: number, lineId?: string) =>
            getUnitPriceToUse({ productId, variantId, unitPrice: originalUnitPrice, _id: lineId, line_item_id: lineId }),
        [getUnitPriceToUse]
    );

    // ⬇️ add near other state
    const shipRef = React.useRef<ShippingOptionsV2Handle>(null);
    const feeRef = React.useRef<SpecialFeePickerV2Handle>(null);
    const studioRestoreGuardRef = React.useRef(false);
    const restoredStudioReturnKeyRef = React.useRef("");
    const buildEditOrderSnapshotForStudioRef = React.useRef<
        ((values: any) => Record<string, unknown>) | null
    >(null);
    // // expose a stable pull fn (call after children recompute)
    // const pullChildPayloads = React.useCallback(() => {
    //     const s = shipRef.current?.getPayload?.();
    //     if (s) setShipPayload(s as ShipPayload);
    //     const f = feeRef.current?.getPayload?.();
    //     if (f) setFeePayload(f as FeePayload);
    // }, []);
    // Helper function and constants for AsyncPaginate
    const normalizeId = (row: any) => String(row?.id ?? row?._id ?? "");
    const normalizeOrderLineItemId = (item: any): string | undefined => {
        const id =
            typeof item?._id === "object"
                ? item._id?.$oid || item._id?.toString?.() || ""
                : String(item?._id || "");
        return id ? String(id) : undefined;
    };
    const PAGE_SIZE = 10;

    // Custom dropdown indicator component
    const DropdownIndicator = (props: any) => {
        return (
            <components.DropdownIndicator {...props}>
                <i className="ri-arrow-down-s-line fs-5" style={{ color: '#6c757d' }}></i>
            </components.DropdownIndicator>
        );
    };

    // Custom styles to ensure dropdown indicator is always visible
    const customSelectStyles = {
        dropdownIndicator: (provided: any, state: any) => ({
            ...provided,
            display: 'flex !important',
            opacity: 1,
            visibility: 'visible',
        }),
        indicatorSeparator: (provided: any, state: any) => ({
            ...provided,
            display: 'none',
        }),
        control: (provided: any, state: any) => ({
            ...provided,
            backgroundColor: state.isDisabled ? '#f8f9fa' : provided.backgroundColor,
            cursor: state.isDisabled ? 'not-allowed' : provided.cursor,
        }),
        singleValue: (provided: any, state: any) => ({
            ...provided,
            color: state.isDisabled ? '#000000' : provided.color,
        }),
        placeholder: (provided: any, state: any) => ({
            ...provided,
            color: state.isDisabled ? '#6c757d' : provided.color,
        }),
    };

    // ===== Team Member Loader =====
    const loadTeamMemberOptions: LoadOptions<TeamMemberOption, any, { page: number }> = useCallback(
        async (inputValue, _loadedOptions, additional) => {
            const page = additional?.page ?? 1;
            // console.log("TeamMemberLoader firing with:", { page, inputValue });

            // HARD GUARD: without brokerage firm ID, do nothing
            const brokerageFirmOrgId = resolveSalesOrderBrokerageFirmOrgId(order);
            if (!brokerageFirmOrgId) {
                // console.log("❌ No brokerage firm ID, return empty");
                return { options: [], hasMore: false, additional: { page: 1 } };
            }

            try {
                const q = (inputValue || "").trim();

                const params = new URLSearchParams({
                    licenseType: "BROKERAGE_TEAM_MEMBER",
                    brokerageFirmID: brokerageFirmOrgId,
                    page: String(page),
                    limit: String(PAGE_SIZE),
                });
                if (q) params.set("search", q);

                const json = await searchUsers(token, params.toString());
                // console.log('Team Member API response:', { page, q, json });
                // 
                const list = Array.isArray(json) ? json : json.data ?? json.results ?? [];

                const options: TeamMemberOption[] = (list || []).map((u: any) => ({
                    value: normalizeId(u),
                    label: `${u?.firstName || ""} ${u?.lastName || ""}`.trim() || "(no name)",
                    firstName: u?.firstName || "",
                    lastName: u?.lastName || "",
                    email: u?.email || "",
                    phone: u?.phone1 || u?.phone2 || "",
                    offices: u?.offices || [],
                }));

                const hasMore = Array.isArray(json)
                    ? options.length === PAGE_SIZE
                    : json?.pagination?.more ??
                    (json?.total ? page * (json?.limit ?? PAGE_SIZE) < json.total : options.length === PAGE_SIZE);

                return {
                    options,
                    hasMore,
                    additional: { page: page + 1 },
                };
            } catch (err) {
                safeConsole.error("Failed to load team member options:", err);
                return { options: [], hasMore: false, additional: { page } };
            }
        },
        [order, token]
    );

    // ===== Agent loader using Select2 endpoint =====
    const loadAgentOptions: LoadOptions<OfficeAgentOption, any, { page: number }> = useCallback(
        async (inputValue, _loadedOptions, additional) => {
            const page = additional?.page ?? 1;

            try {
                const q = (inputValue || "").trim();
                const signageId = effectiveSignageOrgId;

                if (!signageId) {
                    return { options: [], hasMore: false, additional: { page: 1 } };
                }

                const query =
                    `?page=${page}&limit=${PAGE_SIZE}` +
                    (q ? `&search=${encodeURIComponent(q)}` : "");

                const json = await fetchSignageAgentsSelect2(token, signageId, query);

                const list = Array.isArray(json) ? json : json.data ?? json.results ?? [];

                const options: OfficeAgentOption[] = list.map((u: any) => ({
                    value: normalizeId(u),
                    label: u?.name || `${u?.firstName || ""} ${u?.lastName || ""}`.trim() || "(no name)",
                    firstName: u?.firstName || "",
                    lastName: u?.lastName || "",
                    email: u?.email || "",
                    phone: u?.phone1 || "",
                }));

                const hasMore = Array.isArray(json)
                    ? options.length === PAGE_SIZE
                    : json?.pagination?.more ??
                    (json?.total ? page * (json?.limit ?? PAGE_SIZE) < json.total : options.length === PAGE_SIZE);

                return {
                    options,
                    hasMore,
                    additional: { page: page + 1 },
                };
            } catch (err) {
                safeConsole.error("Failed to load agent options:", err);
                return { options: [], hasMore: false, additional: { page } };
            }
        },
        [userId, isSignageTeammember, effectiveSignageOrgId, token]
    );

    const [selectedBrokerageFirm, setSelectedBrokerageFirm] = useState<{
        value: string;
        label: string;
        firstName: string;
        lastName: string;
        email: string;
        phone: string;
    } | null>(null);

    const [selectedOfficeAgent, setSelectedOfficeAgent] = useState<{
        value: string;
        label: string;
        firstName: string;
        lastName: string;
        email: string;
        phone: string;
    } | null>(null);

    const [selectedAgent, setSelectedAgent] = useState<{
        value: string;
        label: string;
        firstName: string;
        lastName: string;
        email: string;
        phone: string;
    } | null>(null);

    const [selectedOffice, setSelectedOffice] = useState<{
        value: string;
        label: string;
        firstName: string;
        lastName: string;
        email: string;
        phone: string;
    } | null>(null);

    const [selectedProducts, setSelectedProducts] = useState<SelectedProduct[]>(
        []
    );
    const [showCustomModal, setShowCustomModal] = useState(false);
    const [customProductSettingEnabled, setCustomProductSettingEnabled] = useState(false);
    const [customproducts, setCustomproducts] = useState<Array<{
        _id?: string;
        product_id: string;
        variant_id: string;
        line_item_id?: string;
        item_name: string;
        sku?: string;
        quantity: number;
        Price: number;
        is_taxable: boolean;
        custom_information: string;
        custom_attributes?: Record<string, string>;
        length: number;
        width: number;
        height: number;
        weight: number;
    }>>([]);
    const PRODUCT_FIELD_MIN = 1;
    const purchasedQtyMinMsg = "Quantity must be at least 1.";
    const purchasedUnitPriceMinMsg = "Unit price must be at least 1.";
    type PurchasedProductFieldErrors = { quantity?: string; unitPrice?: string };
    const [purchasedProductFieldErrors, setPurchasedProductFieldErrors] = useState<
        Record<string, PurchasedProductFieldErrors>
    >({});
    const purchasedProductErrorKey = (kind: "catalog" | "custom", id: string) => `${kind}:${id}`;
    const validatePurchasedQuantityValue = (raw: string | number | undefined): string | undefined => {
        if (raw === "" || raw === undefined || raw === null) return purchasedQtyMinMsg;
        const n = Number(raw);
        if (!Number.isFinite(n) || n < PRODUCT_FIELD_MIN) return purchasedQtyMinMsg;
        return undefined;
    };
    const validatePurchasedUnitPriceValue = (raw: string | number | undefined): string | undefined => {
        if (raw === "" || raw === undefined || raw === null) return purchasedUnitPriceMinMsg;
        const trimmed = String(raw).trim();
        if (trimmed === "" || trimmed === ".") return purchasedUnitPriceMinMsg;
        const n = Number(trimmed);
        if (!Number.isFinite(n) || n < PRODUCT_FIELD_MIN) return purchasedUnitPriceMinMsg;
        return undefined;
    };
    const patchPurchasedProductFieldError = (
        errorKey: string,
        field: keyof PurchasedProductFieldErrors,
        message?: string
    ) => {
        setPurchasedProductFieldErrors((prev) => {
            const next = { ...prev };
            const row = { ...(next[errorKey] || {}) };
            if (message) row[field] = message;
            else delete row[field];
            if (Object.keys(row).length === 0) delete next[errorKey];
            else next[errorKey] = row;
            return next;
        });
    };
    const validateAllPurchasedProductFieldsRef = useRef<() => boolean>(() => true);


    // ✅ Track previous product count to detect removals
    const prevProductCountRef = React.useRef<number>(0);

    // ✅ Initialize and track product count changes to detect removals
    // This prevents special fee busy state from getting stuck when products are removed
    React.useEffect(() => {
        const currentCount = selectedProducts.length;
        const prevCount = prevProductCountRef.current;

        // Initialize on first render
        if (prevCount === 0) {
            prevProductCountRef.current = currentCount;
            return;
        }

        // When products count decreases and special fee is busy, reset it after a delay
        // This handles the case where removing a product causes SpecialFeePicker to get stuck
        if (currentCount < prevCount && busyStates.specialFee) {
            // Product was removed and special fee is busy - reset it after a short delay
            // Give SpecialFeePicker 2 seconds to complete its recalculation, then force reset
            const timeout = setTimeout(() => {
                setBusyStates(prev => (prev.specialFee ? { ...prev, specialFee: false } : prev));
            }, 2000);

            prevProductCountRef.current = currentCount;
            return () => clearTimeout(timeout);
        }

        prevProductCountRef.current = currentCount;
    }, [selectedProducts.length, busyStates.specialFee]);

    // console.log("selectedProducts", selectedProducts)
    const [selectedBillingAddress, setSelectedBillingAddress] =
        useState<Address | null>(null);

    const [selectedShippingAddress, setSelectedShippingAddress] =
        useState<Address | null>(null);
    const [selectedOfficeBillingAddress, setSelectedOfficeBillingAddress] =
        useState<Address | null>(null);

    const [selectedOfficeShippingAddress, setSelectedOfficeShippingAddress] =
        useState<Address | null>(null);

    const applyResolvedSalesToUi = React.useCallback(
        (resolved: ResolvedSalesOrder, requestedDiscountCode?: string) => {
            const rawSync = applySalesResolveResponse(resolved, {
                requestedDiscountCode,
                userClearedCoupon: userClearedCouponRef.current,
                existingFeeRows: feePayloadRef.current?.specialfee,
                existingShipRows: shipPayloadRef.current?.shipping_option,
                specialFeeCatalog: specialFeeCatalogRef.current,
                shippingCostCatalog: shippingCosts,
                userEditedShippingAmount: userEditedShippingAmountRef.current,
            });
            const sync = remapSalesResolveLineMaps(rawSync, resolved, [
                ...(selectedProducts || []),
                ...(customproducts || []),
            ]);
            setProductTaxMap(sync.productTaxMap);
            setProductTaxPctMap(sync.productTaxPctMap);
            setTaxBreakdown(sync.taxBreakdown);
            setProductTotals(sync.productTotals);
            setEffectiveMap((prev) => ({ ...prev, ...sync.effectiveMap }));
            setBasePriceMap((prev) => ({ ...prev, ...sync.basePriceMap }));
            if (sync.feePayloadPatch?.specialfee?.length) {
                const resolvedFees = sync.feePayloadPatch.specialfee;
                const nextFeePayload: FeePayload = {
                    specialfee: resolvedFees,
                    selected_fee_ids: (resolvedFees as Array<{ specialfee_id?: string }>).map((f) =>
                        String(f.specialfee_id ?? "")
                    ).filter(Boolean),
                    total_amount: sync.feePayloadPatch.total_amount,
                    total_tax_amount: sync.feePayloadPatch.total_tax_amount,
                    total_tax_percentage: 0,
                };
                feePayloadRef.current = nextFeePayload;
                setFeePayload(nextFeePayload);
            }
            if (sync.shipPayloadPatch?.shipping_option?.length) {
                setShipPayload((prev) => {
                    const existing =
                        (Array.isArray(shipPayloadRef.current?.shipping_option) &&
                        shipPayloadRef.current.shipping_option.length
                            ? shipPayloadRef.current.shipping_option
                            : Array.isArray(prev?.shipping_option) && prev.shipping_option.length
                                ? prev.shipping_option
                                : []) ?? [];
                    const merged = mergeResolvedShippingTaxIntoLocal(
                        existing,
                        sync.shipPayloadPatch!.shipping_option as Record<string, unknown>[]
                    );
                    const next = {
                        ...(prev ?? shipPayloadRef.current ?? {}),
                        shipping_option: merged,
                    };
                    shipPayloadRef.current = next;
                    return next;
                });
            }
            setSelectedProducts((prev) =>
                mergeSelectedProductsFromResolved(prev, resolved, manualUnitPriceMap)
            );
            liveResolvedDiscountRef.current = sync.discountDisplay;
            setLiveDiscountDisplay(sync.discountDisplay);
            setResolvedTotalsTick((t) => t + 1);
            enableLiveTotals();
        },
        [manualUnitPriceMap, enableLiveTotals, shippingCosts, selectedProducts, customproducts]
    );

    const recomputeSalesOrder = React.useCallback(
        async (override?: { discountCode?: string; force?: boolean; feePayload?: FeePayload }) => {
            if (!token) return false;
            if (isRecomputingRef.current && !override?.force) {
                pendingRecomputeRef.current = true;
                return false;
            }

            const requestId = ++recomputeRequestIdRef.current;

            const buyer = pickSalesResolveBuyerContext({
                values: formikValuesRef.current as any,
                selectedOfficeAgent,
                selectedAgent,
                selectedOffice,
                offices,
                selectedBrokerageFirm,
                selectedTeamMemberOption,
                selectedBillingAddress,
                selectedShippingAddress,
                selectedOfficeBillingAddress,
                selectedOfficeShippingAddress,
            });
            const customerId =
                String(buyer.customerId || "").trim() ||
                String((order as any)?.buyer_membership_id ?? "").trim() ||
                String((order as any)?.customer_membership_id ?? "").trim() ||
                String((order as any)?.customer_id ?? "").trim() ||
                resolveSalesOrderAgentMembershipId(order) ||
                resolveSalesOrderTeamMemberMembershipId(order) ||
                String(formikValuesRef.current?.teamMemberId ?? "").trim();
            const sellerId =
                String(sellerOrgId || "").trim() ||
                String((order as any)?.seller_org_id ?? (order as any)?.signage_id ?? "").trim();

            const hasLineItems = !!(
                selectedProducts?.length ||
                customproducts?.length ||
                (order as any)?.items?.length
            );

            if (!sellerId || !customerId || !hasLineItems) {
                return false;
            }

            const buyerForResolve = { ...buyer, customerId };

            const discountCode = getSalesResolveDiscountCode({
                override: override?.discountCode,
                userClearedCoupon: userClearedCouponRef.current,
                couponInput,
                liveDiscountCode: liveDiscountDisplay.code,
                savedDiscountCode: order?.discount_code,
            });

            isRecomputingRef.current = true;
            setIsRecomputingSales(true);
            setCouponResolveError("");

            try {
                const payload = buildSalesResolvePayload({
                    sellerOrgId: sellerId,
                    sellerMembershipId: membershipId || undefined,
                    sellerGlobalUserId: globalUserId || undefined,
                    buyerContext: buyerForResolve,
                    signageId: sellerOrgId,
                    signageCompanyName: (isSignageTeammember && effectiveSignageOrgName) ? effectiveSignageOrgName! : userName!,
                    bothAddressSame: !!formikValuesRef.current.isSameAsBilling,
                    discountCode,
                    cartId: order?.cart_id ?? null,
                    orderId: order?.id ?? orderId ?? null,
                    orderNumber: order?.order_number ?? null,
                    orderTaxStatus: order?.order_tax_summary?.tax_status ?? "",
                    selectedProducts,
                    customproducts,
                    productObjs: ProductObjs,
                    shipPayload: shipPayloadRef.current ?? shipPayload,
                    feePayload: override?.feePayload ?? feePayloadRef.current ?? feePayload,
                    shippingCosts,
                    specialFeeCatalog: specialFeeCatalogRef.current,
                    fallbackShippingRows: order?.order_charges?.[0]?.shipping_option,
                    fallbackFeeRows: order?.order_charges?.[0]?.specialfee,
                    manualUnitPriceMap,
                    getUnitRetailPriceForPayload: (productId, variantId, original, lineId) =>
                        getUnitRetailPriceForPayload({ productId, variantId, unitPrice: original, _id: lineId, line_item_id: lineId }),
                    getChangedUnitPriceForPayload,
                });

                const resolved = await postSalesOrderResolve(token, {
                    order_type: "order",
                    action: "draft",
                    payload,
                });

                if (requestId !== recomputeRequestIdRef.current) return true;

                lastResolvedSalesRef.current = resolved;
                applyResolvedSalesToUi(resolved, discountCode);
                if (discountCode) {
                    setUserAppliedCoupon(true);
                    userClearedCouponRef.current = false;
                }
                return true;
            } catch (err: any) {
                if (requestId !== recomputeRequestIdRef.current) return false;
                const msg = String(err?.message ?? "Failed to recalculate order");
                setCouponResolveError(msg);
                showError(msg);
                return false;
            } finally {
                if (requestId === recomputeRequestIdRef.current) {
                    isRecomputingRef.current = false;
                    setIsRecomputingSales(false);
                    if (pendingRecomputeRef.current) {
                        pendingRecomputeRef.current = false;
                        void recomputeSalesRef.current?.({ force: true });
                    }
                }
            }
        },
        [
            token,
            isSignageTeammember,
            effectiveSignageOrgId,
            userId,
            effectiveSignageOrgName,
            userName,
            selectedOfficeAgent,
            selectedAgent,
            selectedOffice,
            offices,
            selectedBrokerageFirm,
            selectedTeamMemberOption,
            selectedBillingAddress,
            selectedShippingAddress,
            selectedOfficeBillingAddress,
            selectedOfficeShippingAddress,
            couponInput,
            liveDiscountDisplay.code,
            order,
            orderId,
            selectedProducts,
            customproducts,
            ProductObjs,
            shipPayload,
            feePayload,
            shippingCosts,
            manualUnitPriceMap,
            getUnitRetailPriceForPayload,
            getChangedUnitPriceForPayload,
            applyResolvedSalesToUi,
        ]
    );

    const recomputeSalesRef = React.useRef(recomputeSalesOrder);
    recomputeSalesRef.current = recomputeSalesOrder;

    const scheduleSalesRecompute = React.useCallback(
        (override?: { discountCode?: string; force?: boolean; feePayload?: FeePayload }) => {
            if (hydratingRef.current) return;
            if (isRecomputingRef.current && !override?.force) return;
            if (recomputeTimerRef.current) clearTimeout(recomputeTimerRef.current);
            // Always call latest recompute via ref — debounce must not use a stale
            // manualUnitPriceMap / selectedProducts closure (e.g. typed "9" then "99").
            recomputeTimerRef.current = setTimeout(() => {
                void recomputeSalesRef.current?.(override);
            }, 300);
        },
        []
    );

    scheduleSalesRecomputeRef.current = scheduleSalesRecompute;

    const [orderItems, setOrderItems] = useState<SelectedProduct[]>([]);
    const [shippingCost, setShippingCost] = useState(0);
    const [pickerOpen, setPickerOpen] = useState(false);
    const [studioBatchVariantPreviews, setStudioBatchVariantPreviews] =
        React.useState<StudioBatchVariantPreviewMap | null>(null);
    const [subTotal, setSubTotal] = useState(0);
    const [tax, setTax] = useState(0);
    const [totalAmount, setTotalAmount] = useState(0);
    const [taxBreakdown, setTaxBreakdown] = useState({ product: 0, shipping: 0, specialFee: 0 });

    // const addProductRow = () => setPickerOpen(true);
    const closePicker = () => setPickerOpen(false);
    const pickerExistingLineKeys = React.useMemo(
        () =>
            (selectedProducts || [])
                .map((r) => {
                    const pid = String(r.productId ?? "").trim();
                    const vid = String(r.variantId ?? "").trim();
                    return pid && vid ? `${pid}:${vid}` : "";
                })
                .filter((k): k is string => Boolean(k)),
        [selectedProducts]
    );
    const handleAddFromPicker = (payload: any) => {
        // console.log("Picker payload:", payload);
        let shouldClosePicker = true;
        try {
            if (!payload || !payload.product_id || !payload.variant_id) {
                // console.warn("Picker payload incomplete:", payload);
                setPickerOpen(false);
                return;
            }

            // Prevent adding the exact same product+variant twice (supports both row shapes)
            const incomingPid = String(payload.product_id);
            const incomingVid = String(payload.variant_id);
            const alreadyAdded = (selectedProducts || []).some((r: any) => {
                const pid = String(r?.productId ?? r?.product_id ?? "");
                const vid = String(r?.variantId ?? r?.variant_id ?? "");
                return pid === incomingPid && vid === incomingVid;
            });
            // if (alreadyAdded) {
            //     showError("This product is already added.");
            //     shouldClosePicker = false; // keep modal open so user can pick another product
            //     return;
            // }

            const productObj = ProductObjs?.find(p => p.product_id === payload.product_id);
            if (!productObj) {
                safeConsole.warn("Product not found for product_id:", payload.product_id);
                setPickerOpen(false);
                return;
            }

            const variants = productObj.product?.variants || [];
            const selectedVariant = variants.find(v => v.variant_id === payload.variant_id);
            if (!selectedVariant) {
                safeConsole.warn("Variant not found for variant_id:", payload.variant_id);
                setPickerOpen(false);
                return;
            }

            // Build attributesList entry same as your table expects
            const attributesList = [{
                variantId: selectedVariant.variant_id,
                attributes: selectedVariant.attributes ?? {},
                sku: selectedVariant.sku,
                unitPrice: Number(
                    payload.unit_retail_price ??
                    selectedVariant.retail_price ??
                    productObj.product?.retail_price ??
                    0
                ),
                warehouseId: selectedVariant.inventory?.[0]?.warehouse_id || "",
            }];

            const quantity = Number(payload.quantity ?? 1);
            const unitPrice = Number(
                payload.unit_retail_price ??
                selectedVariant.retail_price ??
                productObj.product?.retail_price ??
                0
            );
            const total = unitPrice * quantity;
            const imgs = pickImages(selectedVariant, productObj, payload.images);
            const firstImgPath = (imgs?.[0] && (typeof imgs[0] === "string" ? imgs[0] : (imgs[0] as any).url)) as string | undefined;
            const payloadDesignId = String(payload.design_id ?? payload.studio_design_id ?? "").trim();
            const payloadPreviewId = String(payload.preview_url ?? payload.studio_preview_id ?? "").trim();

            const isTaxable = resolveCatalogProductLineTaxable({
                pickerPayload: payload as Record<string, unknown>,
                catalogProduct: productObj.product as Record<string, unknown>,
            });
            const isProduction =
                typeof payload.is_production === "boolean"
                    ? payload.is_production
                    : !!productObj.product?.is_production;

            setProductObjs((prev) => {
                const idx = prev.findIndex((o) => o.product_id === payload.product_id);
                if (idx < 0) return prev;
                const next = [...prev];
                next[idx] = {
                    ...next[idx],
                    product: {
                        ...(next[idx].product || {}),
                        is_taxable: isTaxable,
                        is_production: isProduction,
                    },
                };
                return next;
            });

            const lineId = newOrderLineObjectId(collectOrderLineObjectIds(selectedProducts, customproducts));
            const newRow = {
                _id: lineId,
                line_item_id: lineId,
                productId: productObj.product_id,
                variantId: selectedVariant.variant_id,
                ProductName: selectedVariant?.variant_name || productObj?.product?.name,
                // discount_on_total_quantity?.item
                sku: selectedVariant.sku || productObj.product?.sku || "",
                quantity,
                unitPrice,
                total,
                is_taxable: isTaxable,
                tax_rule: productObj.product?.tax_rule || null,
                tax_group: productObj.product?.tax_group || null,
                attributesList,
                is_production: isProduction,
                product_type: productObj.product?.product_type,
                customizations: payload.customizations ?? undefined,
                images: imgs,                                       // keep for later use
                image_url: buildImageUrl(firstImgPath),
                ...(payloadDesignId || payloadPreviewId
                    ? {
                        design_id: payloadDesignId,
                        design_name: String(payload.design_name ?? "").trim(),
                        preview_url: payloadPreviewId,
                    }
                    : {}),
            };
            const updated = [...selectedProducts, newRow];

            setSelectedProducts(updated);
            markUserEdited();
            scheduleSalesRecomputeRef.current?.();
        } finally {
            if (shouldClosePicker) setPickerOpen(false);
        }
    };
    useEffect(() => {
        const fetchShippingCosts = async () => {
            try {
                const data = await fetchShippingCostsApi(token);
                // console.log("Shipping Costs Response:", data);
                setShippingCosts(data);
            } catch (err) {
                setError(
                    err instanceof Error ? err.message : "An unknown error occurred"
                );
            } finally {
                setLoading(false);
            }
        };

        fetchShippingCosts();
    }, [token]);

    const [selectedShippingMethod, setSelectedShippingMethod] = useState<{
        value: string;
        label: string;
        cost: number;
    } | null>(null);


    const addProductRow = () => {
        const emptyId = newOrderLineObjectId(collectOrderLineObjectIds(selectedProducts, customproducts));
        setSelectedProducts([
            ...selectedProducts,
            { _id: emptyId, line_item_id: emptyId, productId: "", sku: "", quantity: 1, unitPrice: 0, total: 0, is_taxable: false, tax_rule: null, tax_group: null, variantId: "", ProductName: "", is_production: false, images: [] },
        ]);
    };

    const removeProductRow = (index: number) => {
        markUserEdited();
        const removed = selectedProducts[index];
        const updatedProducts = selectedProducts?.filter((_, i) => i !== index);
        setSelectedProducts(updatedProducts);
        if (removed) {
            const removedKey = keyFor(removed);
            const removedLineId = String(removed._id ?? removed.line_item_id ?? "").trim();
            if (removedLineId) {
                // Only drop the matching saved line — never all SKU twins.
                setOrderItems((prev) =>
                    (prev || []).filter((item: any) => {
                        const id = normalizeOrderLineItemId(item);
                        return id !== removedLineId;
                    })
                );
            }
            setManualUnitPriceMap((m) => {
                if (m[removedKey] === undefined) return m;
                const next = { ...m };
                delete next[removedKey];
                return next;
            });
            setEffectiveMap((m) => {
                if (m[removedKey] === undefined) return m;
                const next = { ...m };
                delete next[removedKey];
                return next;
            });
            setBasePriceMap((m) => {
                if (m[removedKey] === undefined) return m;
                const next = { ...m };
                delete next[removedKey];
                return next;
            });
            const errorKey = purchasedProductErrorKey("catalog", removedKey);
            setPurchasedProductFieldErrors((prev) => {
                const next = { ...prev };
                delete next[errorKey];
                return next;
            });
        }

        // ✅ Reset special fee busy state when product is removed (prevent stuck "Processing..." button)
        // The SpecialFeePicker might be recalculating and get stuck, so we force reset it
        setBusyStates(prev => (prev.specialFee ? { ...prev, specialFee: false } : prev));
    };

    // Fetch order details

    // ========================================================================
    // API FUNCTIONS - Order Data Fetching
    // ========================================================================
    // Fetches complete order data including buyer info, products, addresses, etc.
    // This is the main data loading function for edit mode.
    // Pass optional signal to support abort on effect cleanup (avoids double fetch in Strict Mode).
    const fetchOrderData = async (signal?: AbortSignal) => {
        try {
            setLoading(true);
            setInitialLoadComplete(false);
            hydratingRef.current = true;
            const data = await fetchOrderById(token, orderId!, { signal });
            if (signal?.aborted) return;
            if (data) {
                setOrder(data);
                const brokerageFirmOrgId = resolveSalesOrderBrokerageFirmOrgId(data);
                const agentMembershipId = resolveSalesOrderAgentMembershipId(data);
                setSelectedBrokerageFirmId(brokerageFirmOrgId);

                // ✅ Store initial form values for change detection
                initialFormValuesRef.current = {
                    userType: data?.buyer_type === "agent" ? "agent" : "brokerageFirm",
                    brokerageFirmId: brokerageFirmOrgId,
                    teamMemberId: resolveSalesOrderTeamMemberMembershipId(data),
                    officeId: data?.office?._id,
                    agentId: agentMembershipId,
                    customerFirstName: data?.customer_first_name,
                    customerLastName: data?.customer_last_name,
                    customerEmail: data?.customer_email,
                    customerPhone: data?.customer_phone,
                    billingAddressId:
                        resolveSalesOrderAddressId(data?.billing_address) ||
                        (data?.both_address_same
                            ? resolveSalesOrderAddressId(data?.shipping_address)
                            : ""),
                    shippingAddressId: resolveSalesOrderAddressId(data?.shipping_address),
                    notes: data?.notes,
                    items: JSON.stringify(data?.items || []),
                    shippingAmount: data?.shipping_amount,
                    taxAmount: data?.tax_amount,
                    subtotalAmount: data?.subtotal_amount,
                    totalAmount: data?.total_amount,
                };
                setSelectedOfficeId(data?.office?._id ?? "");
                if (brokerageFirmOrgId && data?.brokerage_firm_name) {
                    setSelectedBrokerageFirm({
                        value: brokerageFirmOrgId,
                        label: data.brokerage_firm_name,
                        firstName: "",
                        lastName: "",
                        email: "",
                        phone: "",
                    });
                }
                const savedTeamMemberId = resolveSalesOrderTeamMemberMembershipId(data);
                if (savedTeamMemberId && data?.team_member_name) {
                    const tmFirst = data.team_member_name.split(" ")[0] || "";
                    const tmLast = data.team_member_name.split(" ").slice(1).join(" ") || "";
                    setSelectedTeamMemberOption({
                        value: savedTeamMemberId,
                        label: data.team_member_name,
                        firstName: tmFirst,
                        lastName: tmLast,
                        email: data.customer_email || "",
                        phone: data.customer_phone || "",
                        offices: data.office ? [data.office] : [],
                    });
                }
                const billingAddressFromApi: Address | null = data?.billing_address ?? null;
                const shippingAddressFromApi: Address | null = data?.shipping_address ?? null;
                const billingAddressId = resolveSalesOrderAddressId(billingAddressFromApi);
                const shippingAddressId = resolveSalesOrderAddressId(shippingAddressFromApi);

                if (isSalesOrderSavedAddress(billingAddressFromApi)) {
                    setSelectedBillingAddress(billingAddressFromApi);
                    setSelectedOfficeBillingAddress(billingAddressFromApi);
                    formik.setFieldValue("billingAddressId", billingAddressId);
                } else if (data?.both_address_same && isSalesOrderSavedAddress(shippingAddressFromApi)) {
                    setSelectedBillingAddress(shippingAddressFromApi);
                    setSelectedOfficeBillingAddress(shippingAddressFromApi);
                    formik.setFieldValue("billingAddressId", shippingAddressId);
                } else {
                    setSelectedBillingAddress(null);
                    setSelectedOfficeBillingAddress(null);
                    formik.setFieldValue("billingAddressId", "");
                }

                if (isSalesOrderSavedAddress(shippingAddressFromApi)) {
                    setSelectedShippingAddress(shippingAddressFromApi);
                    setSelectedOfficeShippingAddress(shippingAddressFromApi);
                    formik.setFieldValue("shippingAddressId", shippingAddressId);
                } else {
                    setSelectedShippingAddress(null);
                    setSelectedOfficeShippingAddress(null);
                    formik.setFieldValue("shippingAddressId", "");
                }

                setAddressObjects((prev) =>
                    mergeSalesOrderSavedAddresses(
                        prev,
                        billingAddressFromApi,
                        shippingAddressFromApi
                    )
                );

                const rawItems = data?.items || [];
                const catalogItems = rawItems.filter(
                    (it: any) => it.product_id && it.variant_id && it.inventory_source !== "custom"
                );
                const customItems = rawItems.filter(
                    (it: any) => it.inventory_source === "custom" || !it.product_id
                );

                setOrderItems(catalogItems);
                formik.setFieldValue("productId", catalogItems[0]?.product_id || "");
                // console.log("Catalog items from API:", customItems);

                setCustomproducts(
                    customItems.map((it: any) => ({
                        product_id: String(it.product_id || "000000000000000000000000"),
                        variant_id: String(it.variant_id || "000000000000000000000000"),
                        _id: normalizeOrderLineItemId(it),
                        line_item_id: normalizeOrderLineItemId(it),
                        item_name: it.product_name || "Custom Item",
                        sku: it.sku || "",
                        quantity: Number(it.quantity) || 1,
                        Price: Number(it.unit_price ?? it.changed_unit_price ?? 0),
                        // Preserve taxable exactly from API: prefer item_tax_info.taxable, then is_taxable.
                        is_taxable:
                            typeof it?.item_tax_info?.taxable === "boolean"
                                ? it.item_tax_info.taxable
                                : typeof it?.is_taxable === "boolean"
                                  ? it.is_taxable
                                  : undefined,
                        item_tax_info: {
                            ...(typeof it?.item_tax_info?.taxable === "boolean"
                                ? { taxable: it.item_tax_info.taxable }
                                : {}),
                        },
                        custom_information: it.custom_information || "",
                        custom_attributes: it.custom_attributes || {},
                        length: Number(it.attributes?.length ?? 0),
                        width: Number(it.attributes?.width ?? 0),
                        height: Number(it.attributes?.height ?? 0),
                        weight: Number(it.attributes?.weight ?? 0),
                    }))
                );

                // ✅ setSelectedProducts is done by useEffect when orderItems (catalogItems) changes

                const savedProductTax = rawItems.reduce(
                    (s: number, it: any) => s + n(it?.item_tax_info?.total_product_tax_amount),
                    0
                );

                const shipRowsFromGet = (data?.order_charges?.[0]?.shipping_option ?? []);
                const feeRowsFromGet = (data?.order_charges?.[0]?.specialfee ?? []);

                const savedShipTax = shipRowsFromGet.reduce(
                    (s: number, r: any) => s + n(r?.shipping_tax_amount),
                    0
                );
                const savedFeeTax = feeRowsFromGet.reduce(
                    (s: number, f: any) => s + n(f?.specialfee_tax_amount),
                    0
                );

                setTaxBreakdown({
                    product: r2(savedProductTax),
                    shipping: r2(savedShipTax),
                    specialFee: r2(savedFeeTax),
                });

                const initialTaxMap: Record<string, number> = {};
                const initialTaxPctMap: Record<string, number> = {};
                const initialManualPriceMap: Record<string, number> = {};
                catalogItems.forEach((it: any) => {
                    const key = salesLineIdentityKey(it);
                    initialTaxMap[key] = n(it?.item_tax_info?.total_product_tax_amount);
                    initialTaxPctMap[key] = n(it?.item_tax_info?.tax_percentage);
                    if (it.is_price_overridden === true) {
                        initialManualPriceMap[key] = Number(
                            it.changed_unit_price ?? it.unit_retail_price ?? 0
                        );
                    }
                });
                customItems.forEach((it: any, idx: number) => {
                    const key = getCustomProductKey(
                        {
                            _id: normalizeOrderLineItemId(it),
                            line_item_id: normalizeOrderLineItemId(it),
                            product_id: String(it?.product_id || ""),
                            variant_id: String(it?.variant_id || ""),
                        },
                        idx
                    );
                    initialTaxMap[key] = n(it?.item_tax_info?.total_product_tax_amount);
                    initialTaxPctMap[key] = n(it?.item_tax_info?.tax_percentage);
                });
                setProductTaxMap(initialTaxMap);
                setProductTaxPctMap(initialTaxPctMap);
                if (Object.keys(initialManualPriceMap).length > 0) {
                    setManualUnitPriceMap(initialManualPriceMap);
                }

                // ✅ and *stay* in saved mode on load
                setUseLiveTotals(false);



                const catalogSub = catalogItems.reduce(
                    (acc: number, it: any) => acc + n(it?.item_sub_total ?? it?.total_price ?? it?.quantity * (it?.changed_unit_price ?? it?.unit_retail_price ?? 0)),
                    0
                );
                const customSub = customItems.reduce(
                    (acc: number, it: any) => acc + n(it?.item_sub_total ?? it?.total_price ?? it?.quantity * (it?.unit_price ?? it?.changed_unit_price ?? 0)),
                    0
                );
                const subTotalFromProducts = r2(catalogSub + customSub);
                setSubTotal(subTotalFromProducts);
                setShippingCost(Number(data.shipping_amount || 0));
                setTax(Number(data.tax_amount || 0));
                setTotalAmount(
                    subTotalFromProducts +
                    Number(data.shipping_amount || 0) +
                    Number(data.tax_amount || 0)
                );
                const initialPerItemMap: Record<string, number> = {};
                catalogItems.forEach((it: any) => {
                    const key = salesLineIdentityKey(it);
                    initialPerItemMap[key] = n(it?.discount_on_total_quantity);
                });
                setPerItemCouponMap(initialPerItemMap);

                // ✅ Initialize coupon from existing order discount
                if (data?.discount_code) {
                    const code = String(data.discount_code).trim();
                    setCouponInput(code);
                    setLiveDiscountDisplay({
                        code,
                        amount: n(data?.discount_amount || 0),
                    });
                }
                // ✅ Initialize productTotals from saved order data
                const savedSubTotal = (data?.subtotal_amount != null ? [data] : []).reduce((s: number, it: any) =>
                    s + n(it?.subtotal_amount), 0);
                setProductTotals({
                    subTotal: r2(savedSubTotal),
                    discount: n(data?.discount_amount || 0),
                    productTax: r2(savedProductTax),
                });

                // ✅ Initialize feePayload from order.order_charges
                if (data?.order_charges?.[0]?.specialfee?.length > 0) {
                    const specialFees = data.order_charges[0].specialfee;
                    const hydratedFeePayload: FeePayload = {
                        specialfee: specialFees,
                        selected_fee_ids: specialFees.map((f: any) => f.specialfee_id),
                        total_amount: specialFees.reduce((s: number, f: any) => s + n(f.specialfee_amount), 0),
                        total_tax_amount: specialFees.reduce((s: number, f: any) => s + n(f.specialfee_tax_amount), 0),
                        total_tax_percentage: 0,
                    };
                    feePayloadRef.current = hydratedFeePayload;
                    setFeePayload(hydratedFeePayload);
                    lastFeeJsonRef.current = JSON.stringify(specialFees);
                    setEditSpecialFeeInitialIds(
                        specialFees
                            .map((f: any) => String(f.specialfee_id || ""))
                            .filter(Boolean)
                    );
                }
                // console.log("[EDIT:INIT] Initialized productTotals from order:", {
                //     subTotal: r2(savedSubTotal),
                //     discount: n(data?.discount_amount || 0),
                //     productTax: r2(savedProductTax),
                // });

                // safeConsole.log("brokerageFirm is it ok")

                if (data?.buyer_type === "brokerageFirm") {
                    // console.log("brokerageFirm")
                    // ✅ Set selected office
                    if (data?.office?._id) {
                        // console.log("Inside the Brokerage Firm and Office condition");
                        setSelectedOffice({
                            value: data.office._id,
                            label: data.office.officeName,
                            firstName: data.office.officeName,
                            lastName: data.office.officeName,
                            email: "", // fallback or set as per API
                            phone: data.office.officeAddress?.phone || "",
                        });
                    }

                    // ✅ Set selected office agent (foundation agent_membership_id or legacy agent_id)
                    if (agentMembershipId) {
                        const agentLabel =
                            data.agent_name ||
                            `${data.customer_first_name || ""} ${data.customer_last_name || ""}`.trim();
                        setSelectedOfficeAgent({
                            value: agentMembershipId,
                            label: agentLabel,
                            firstName: data.customer_first_name,
                            lastName: data.customer_last_name,
                            email: data.customer_email || "",
                            phone: data.customer_phone || "",
                        });
                    }
                }
                else {
                    if (agentMembershipId) {
                        const agentLabel =
                            data.agent_name ||
                            `${data.customer_first_name || ""} ${data.customer_last_name || ""}`.trim();
                        setSelectedAgent({
                            value: agentMembershipId,
                            label: agentLabel,
                            firstName: data.customer_first_name,
                            lastName: data.customer_last_name,
                            email: data.customer_email || "",
                            phone: data.customer_phone || "",
                        });
                    }
                }
                // ✅ Initialize shipPayload and feePayload from order data
                const shippingRows = (data?.order_charges?.[0]?.shipping_option ?? []).map((r: any) => ({
                    shipping__id: String(r.shipping_id ?? r.shipping__id ?? ""),
                    shipping_name: String(r.shipping_name ?? ""),
                    shipping_type: String(r.shipping_type ?? ""),
                    is_customer_pickup: r.is_customer_pickup === true,
                    shipping_amount: Number(r.shipping_amount ?? 0),
                    cost: Number(r.cost ?? r.shipping_amount ?? 0),
                    shipping_tax_percentage: Number(r.shipping_tax_percentage ?? 0),
                    shipping_tax_amount: Number(r.shipping_tax_amount ?? 0),
                    shipping_taxable: readResolveTaxable(r, ["shipping_taxable", "taxable", "is_taxable"], true),
                    shipping_delivarydate: String(r.shipping_delivarydate ?? r.shipping_delivery_date ?? ""),
                    shipping_delivery_time: String(r.shipping_delivery_time ?? ""),
                }));

                const feeRows = (data?.order_charges?.[0]?.specialfee ?? []).map((f: any) => ({
                    specialfee_id: String(f.specialfee_id ?? ""),
                    specialfee_name: String(f.specialfee_name ?? ""),
                    specialfee_amount: Number(f.specialfee_amount ?? 0),
                    specialfee_tax_percentage: Number(f.specialfee_tax_percentage ?? 0),
                    specialfee_tax_amount: Number(f.specialfee_tax_amount ?? 0),
                    specialfee_taxable: readResolveTaxable(f, ["specialfee_taxable", "taxable", "is_taxable"], true),
                    required: f?.required === true,
                }));

                if (shippingRows.length > 0) {
                    const first = shippingRows[0] as { shipping__id?: string; shipping_type?: string };
                    const rawId = String(first?.shipping__id ?? "");
                    const isUpsInit =
                        (rawId.length > 0 &&
                            /^[0-9A-Z]{2,3}$/.test(rawId) &&
                            rawId.length <= 3 &&
                            rawId !== "custom-manual") ||
                        /^ups$/i.test(String(first?.shipping_type ?? "").trim());
                    const initShipPayload = {
                        shipping_option: shippingRows as any,
                        isUps: isUpsInit,
                        isManual: !isUpsInit,
                    };
                    shipPayloadRef.current = initShipPayload;
                    setShipPayload(initShipPayload);
                    lastShipJsonRef.current = JSON.stringify(shippingRows);
                    const ids = shippingRows
                        .map((r: any) => String(r?.shipping__id ?? r?.shipping_id ?? ""))
                        .filter(Boolean)
                        .sort()
                        .join(",");
                    lastShipOptionIdsRef.current = ids;
                    lastKnownShippingAmountRef.current = shippingRows.reduce(
                        (s: number, r: any) => s + n(r?.shipping_amount),
                        0
                    );
                }

                const hydratedFeePayload: FeePayload = {
                    specialfee: feeRows,
                    selected_fee_ids: feeRows.map((f: any) => f.specialfee_id),
                    total_amount: feeRows.reduce((s: number, f: any) => s + Number(f.specialfee_amount || 0), 0),
                    total_tax_amount: feeRows.reduce((s: number, f: any) => s + Number(f.specialfee_tax_amount || 0), 0),
                    total_tax_percentage: 0,
                };
                feePayloadRef.current = hydratedFeePayload;
                setFeePayload(hydratedFeePayload);
                lastFeeJsonRef.current = JSON.stringify(feeRows);
                setEditSpecialFeeInitialIds(
                    feeRows.map((f: any) => String(f.specialfee_id || "")).filter(Boolean)
                );
                requestAnimationFrame(() => {
                    hydratingRef.current = false;
                    setInitialLoadComplete(true);
                    void recomputeSalesRef.current?.({ force: true });
                });

                // also default the "live totals" switch to show server values first
                setUseLiveTotals(false);

            }
        } catch (err) {
            if (err instanceof Error && err.name === "AbortError") return;
            safeConsole.error("Error fetching order data:", err);
        } finally {
            if (!signal?.aborted) setLoading(false);
        }
    };
    useEffect(() => {
        // Do not gate on legacy userId cookie — foundation signage login uses organizationId.
        if (!orderId || !token) return;
        const controller = new AbortController();
        fetchOrderData(controller.signal);
        return () => controller.abort();
    }, [token, orderId]);

    const fetchBrokerageFirms = async (userType: string) => {
        formik.setFieldValue("userType", userType);
        try {
            const data: License[] = await fetchBrokerageFirmsApi(token);
            // console.log(data);
            setBrokerageFirms(data);
        } catch (err) {
            setError(
                err instanceof Error ? err.message : "An unknown error occurred"
            );
        } finally {
            setLoading(false);
        }
    };

    // Removed fetchBrokerageTeamMembers - now using AsyncPaginate

    const fetchBrokerageOffices = async (brokerage: any) => {
        setSelectedBrokerageFirm(brokerage);
        let brokerageId = brokerage?.value;

        try {
            const data = await fetchOfficeByBrokerageId(token, brokerageId);
            if (Array.isArray(data)) {
                setOffices(data);
            } else {
                setOffices([]);
                const message =
                    typeof data?.error === "string"
                        ? data.error
                        : "Could not load offices for this brokerage.";
                setError(message);
            }
        } catch (err) {
            setOffices([]);
            setError(
                err instanceof Error ? err.message : "An unknown error occurred"
            );
        } finally {
            setLoading(false);
        }
    };

    const fetchBrokerageOfficesById = async (brokerageId: string) => {
        // setSelectedBrokerageFirm(brokerage);
        // let brokerageId = brokerage?.value;

        try {
            const data = await fetchOfficeByBrokerageId(token, brokerageId);
            // console.log("fefetchBrokerageOfficesById :", data)
            if (Array.isArray(data)) {
                setOffices(data);
            } else {
                setOffices([]);
                const message =
                    typeof data?.error === "string"
                        ? data.error
                        : "Could not load offices for this brokerage.";
                setError(message);
            }
        } catch (err) {
            setOffices([]);
            setError(
                err instanceof Error ? err.message : "An unknown error occurred"
            );
        } finally {
            setLoading(false);
        }
    };

    const fetchOfficeAgents = async (office: any) => {
        setSelectedOffice(office);
        let officeId = office?.value;

        try {
            const data = await fetchAgentsByOfficeId(token, officeId);
            setOfficeAgents(data);
        } catch (err) {
            setError(
                err instanceof Error ? err.message : "An unknown error occurred"
            );
        } finally {
            setLoading(false);
        }
    };

    const fetchOfficeAgentsById = async (officeId: string) => {
        // setSelectedOffice(office);
        // let officeId = office?.value;

        try {
            const data = await fetchAgentsByOfficeId(token, officeId);
            setOfficeAgents(data);
        } catch (err) {
            setError(
                err instanceof Error ? err.message : "An unknown error occurred"
            );
        } finally {
            setLoading(false);
        }
    };

    // Removed fetchAgents - now using AsyncPaginate with Select2 endpoint

    const buyerOptions: { value: string; label: string }[] = [
        { value: "brokerageFirm", label: "Brokerage Firm" },
        { value: "agent", label: "Agent" },
    ];

    const brokerageOptions = useMemo(() => {
        const base = (brokerageFirms || []).map((office) => ({
            value: office.id,
            label: office?.firmDetails?.companyName + "(" + office?.firstName + ")",
            firstName: office?.firstName,
            lastName: office?.lastName,
            email: office?.email,
            phone: office?.phone1,
        })) as {
            value: string;
            label: string;
            firstName?: string;
            lastName?: string;
            email?: string;
            phone?: string;
        }[];
        const savedId = resolveSalesOrderBrokerageFirmOrgId(order);
        if (!savedId || base.some((opt) => String(opt.value) === savedId)) return base;
        return [
            {
                value: savedId,
                label: String(order?.brokerage_firm_name || savedId),
                firstName: "",
                lastName: "",
                email: "",
                phone: "",
            },
            ...base,
        ];
    }, [brokerageFirms, order?.brokerage_firm_org_id, order?.brokerage_firm_id, order?.brokerage_firm_name]);

    const officeOptions = useMemo(() => {
        const base = (Array.isArray(offices) ? offices : []).map((office) => ({
            value: office?._id,
            label: office?.officeName,
            firstName: office?.officeName,
            lastName: office?.officeName,
            email: "",
            phone: office?.officeAddress?.[0]?.phone,
        }));
        const savedOffice = order?.office;
        const savedId = String(savedOffice?._id ?? "").trim();
        if (!savedId || base.some((opt) => String(opt.value) === savedId)) return base;
        const officePhone = Array.isArray(savedOffice?.officeAddress)
            ? savedOffice?.officeAddress?.[0]?.phone
            : (savedOffice?.officeAddress as { phone?: string } | undefined)?.phone;
        return [
            {
                value: savedId,
                label: String(savedOffice?.officeName || savedId),
                firstName: savedOffice?.officeName || "",
                lastName: savedOffice?.officeName || "",
                email: "",
                phone: officePhone || "",
            },
            ...base,
        ];
    }, [offices, order?.office]);

    const officeAgentOptions = useMemo(() => {
        const base = (officeAgents || []).map((agent) => ({
            value: agent.id,
            label: `${agent.firstName || ""} ${agent.lastName || ""}`.trim(),
            firstName: agent.firstName,
            lastName: agent.lastName,
            email: agent.email,
            phone: agent.phone1,
        }));
        const savedId = resolveSalesOrderAgentMembershipId(order);
        if (!savedId || base.some((opt) => String(opt.value) === savedId)) return base;
        const first =
            order?.customer_first_name || order?.agent_name?.split(" ")?.[0] || "";
        const last =
            order?.customer_last_name ||
            (order?.agent_name?.split(" ")?.slice(1)?.join(" ") || "");
        return [
            {
                value: savedId,
                label: order?.agent_name || `${first} ${last}`.trim() || savedId,
                firstName: first,
                lastName: last,
                email: order?.customer_email || "",
                phone: order?.customer_phone || "",
            },
            ...base,
        ];
    }, [
        officeAgents,
        order?.agent_membership_id,
        order?.agent_id,
        order?.agent_name,
        order?.customer_first_name,
        order?.customer_last_name,
        order?.customer_email,
        order?.customer_phone,
        order?.customer_membership_id,
        order?.customer_id,
    ]);

    // Removed agentOptions - now using AsyncPaginate

    const addressOptions = useMemo(
        () =>
            buildSalesAddressSelectOptions(addressObjects, [
                order?.billing_address,
                order?.shipping_address,
            ]),
        [addressObjects, order?.billing_address, order?.shipping_address]
    );

    const [selectedPaymentMethod, setSelectedPaymentMethod] =
        useState<string>("");
    const [showAddressForm, setShowAddressForm] = useState(false);

    const getAddressDetails = (addressId: string) => {
        if (!addressId) {
            safeConsole.error("Address ID is missing.");
            return null;
        }

        // Ensure `addressObjects` is an array before calling `.find()`
        if (!Array.isArray(addressObjects) || addressObjects.length === 0) {
            safeConsole.error("Address objects are not loaded or empty.");
            return null;
        }

        const selectedAddress = addressObjects?.find(
            (address) => address._id === addressId
        );

        if (!selectedAddress) {
            safeConsole.error(`Address with ID ${addressId} not found.`);
            return null;
        }
        return selectedAddress;
    };

    // console.log("formik values on submit", selectedProducts);

    const formik = useFormik({
        enableReinitialize: true,
        initialValues: {
            userType: order?.buyer_type === "agent" ? "agent" : "brokerageFirm",
            brokerageFirmIddd: resolveSalesOrderBrokerageFirmOrgId(order),
            teamMemberId: resolveSalesOrderTeamMemberMembershipId(order),
            teamMemberName: order?.team_member_name,
            officeIddd: order?.office?._id,
            officeAgentId: resolveSalesOrderAgentMembershipId(order),
            agentName: order?.agent_name,
            userFirstName: order?.customer_first_name,
            userLastName: order?.customer_last_name,
            userEmail: order?.customer_email,
            userPhone: order?.customer_phone,
            billingAddressId:
                resolveSalesOrderAddressId(order?.billing_address) ||
                (order?.both_address_same
                    ? resolveSalesOrderAddressId(order?.shipping_address)
                    : ""),
            shippingAddressId: resolveSalesOrderAddressId(order?.shipping_address),
            isSameAsBilling:
                order?.shipping_address?._id === order?.billing_address?._id,
            shippingMethod: null,
            productId: "",
            sku: "",
            quantity: 1,
            unitPrice: 0,
            total: 0,
            shipping_amount: order?.shipping_amount,
            manual_shipping_method: {
                _id: order?.manual_shipping_method?._id,
                name: order?.manual_shipping_method?.name,
                cost: order?.manual_shipping_method?.cost
            },
            // tax_amount: order?.tax_details.tax_amount,
            notes: order?.notes,
            label_of_address: "",
            firstName: "",
            lastName: "",
            company: "",
            addressLine1: "",
            addressLine2: "",
            country: "",
            county: "",
            state: "",
            zipcode: "",
            phone: "",
        },
        validationSchema: Yup.object({
            userType: Yup.string().required("Buyer type is required"),
            userEmail: Yup.string()
                .email("Invalid email")
                .required("Email is required"),
            userPhone: phoneValidation,
        }),
        // ========================================================================
        // FORM SUBMISSION HANDLER
        // ========================================================================
        onSubmit: async (values) => {
            setFormSubmitting(true);
            try {
                if (!validateAllPurchasedProductFieldsRef.current()) {
                    showError("Quantity and unit price must be at least 1 for all products.");
                    return;
                }
                let refundSuccess = false;
                const isTaxComplete =
                    String(order?.order_tax_summary?.tax_status ?? "").toLowerCase() === "complete";

                if (isTaxComplete) {
                    try {
                        await recomputeSalesRef.current?.({ force: true });

                        const refundOrderId = String(orderIds || "").trim();
                        if (!token || !refundOrderId) {
                            safeConsole.warn(
                                "[SALES:ORDER_SUBMIT] tax refund skipped: missing token or version order id"
                            );
                        } else {
                            refundSuccess = await Refund(token, refundOrderId);
                            if (!refundSuccess) {
                                safeConsole.warn("[SALES:ORDER_SUBMIT] tax refund did not succeed");
                            } else {
                                await recomputeSalesRef.current?.({ force: true });
                            }
                        }
                    } catch (err) {
                        safeConsole.error("[SALES:ORDER_SUBMIT] tax refund failed", err);
                    }
                }

                const resolveOk = await recomputeSalesRef.current?.({ force: true });
                if (!lastResolvedSalesRef.current) {
                    showError("Could not recalculate order before save. Please try again.");
                    return;
                }
                if (!resolveOk) {
                    safeConsole.warn(
                        "[SIGNAGE:EDIT_ORDER:SAVE] Recompute returned false; using last resolved snapshot"
                    );
                }
                const resolvedTaxStatus = lastResolvedSalesRef.current?.order_tax_summary?.tax_status;
                const nextTaxStatus =
                    resolvedTaxStatus != null
                        ? String(resolvedTaxStatus)
                        : order?.order_tax_summary?.tax_status || "draft";

                const { updatePayload, buyer } = buildSignageEditOrderUpdatePayload(values, {
                    nextTaxStatus,
                    order,
                    taxBreakdown,
                    selectedProducts,
                    ProductObjs,
                    customproducts,
                    perRowDiscountMapToShow,
                    shipPayload: shipPayloadRef.current ?? shipPayload,
                    feePayload: feePayloadRef.current ?? feePayload,
                    shippingCosts,
                    specialFeeCatalog: specialFeeCatalogRef.current,
                    productTaxMap,
                    getUnitPriceToUse,
                    getEffectiveUnitPrice,
                    getUnitRetailPriceForPayload,
                    getCustomProductKey,
                    selectedBillingAddress,
                    selectedShippingAddress,
                    selectedOfficeBillingAddress,
                    selectedOfficeShippingAddress,
                    selectedOfficeAgent,
                    selectedAgent,
                    selectedOffice,
                    offices,
                    selectedBrokerageFirm,
                    userAppliedCoupon,
                    couponDiscounts: couponInput.trim() ? [{ id: couponInput.trim() }] : [],
                    selectedShippingMethod,
                    linkedProjectId,
                    linkedProjectNumber,
                    isSignageTeammember,
                    effectiveSignageOrgId,
                    effectiveSignageOrgName,
                    userId,
                    userName,
                });

                const finalUpdatePayload = overlaySalesSubmitFromResolved(
                    updatePayload,
                    lastResolvedSalesRef.current
                );

                const response = await salesOrderRequest(salesOrderUrls.orderUpdate(orderId!), token, {
                    method: "PATCH",
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${token}`,
                    },
                    body: JSON.stringify(finalUpdatePayload),
                });

                const data = await response.json();

                await runSignageEditOrderAfterSuccessfulPatch({
                    response,
                    data,
                    refundSuccess,
                    token,
                    order,
                    orderId: orderId ?? "",
                    orderIds,
                    values,
                    selectedProducts,
                    shippingCost,
                    tax,
                    userClearedProject: userClearedProjectRef.current,
                    linkedProjectId,
                    projectIdFromQuery,
                    isSignageTeammember,
                    effectiveSignageOrgId,
                    effectiveSignageOrgName,
                    userId,
                    userName,
                    baseRoute,
                    selectedOfficeAgent,
                    selectedAgent,
                    effectiveCouponCreatedById,
                    initialFormValuesRef,
                    buyer,
                    safeNavigate,
                });
            } catch (error: any) {
                safeConsole.error(error);
                showError(`Error: ${error.message}`);
                await handleApiError(error, {
                    module: "sales_order",
                    action: "update_order",
                    userId: userId || undefined,
                    licenseType: licenseType || undefined,
                    userName: userName || undefined,
                    url: `${SALES_ORDER_API_BASE}${salesOrderUrls.orderUpdate(orderId!)}`,
                    method: "PATCH",
                    additionalInfo: {
                        orderId: orderId,
                        orderNumber: order?.order_number
                    }
                }, token);
            } finally {
                setFormSubmitting(false);
            }
        },
    });

    formikValuesRef.current = formik.values;

    const canProceedToSave = React.useMemo(() => {
        const hasShippingAddress = !!(
            selectedShippingAddress ||
            selectedOfficeShippingAddress ||
            String(formik.values.shippingAddressId || "").trim()
        );

        const specialFees =
            (feePayload?.specialfee?.length
                ? feePayload.specialfee
                : order?.order_charges?.[0]?.specialfee) || [];
        const selectedFeeIds = feePayload?.selected_fee_ids || [];
        const requiredFees = specialFees.filter((f: any) => f.required === true);

        if (requiredFees.length === 0) {
            return hasShippingAddress;
        }

        const allRequiredFeesSelected = requiredFees.every((rf: any) =>
            selectedFeeIds.includes(rf.specialfee_id)
        );

        return hasShippingAddress && allRequiredFeesSelected;
    }, [
        selectedShippingAddress,
        selectedOfficeShippingAddress,
        formik.values.shippingAddressId,
        feePayload,
        order?.order_charges,
    ]);

    useEffect(() => {
        if (formik.submitCount < 1) return;

        formik.setTouched(
            {
                ...formik.touched,
                userEmail: true,
                userPhone: true,
            },
            false
        );
    }, [formik.submitCount]);

    useEffect(() => {
        if (formik.values.userType === "brokerageFirm") {
            fetchBrokerageFirms(formik.values.userType);
        }
    }, [formik.values.userType]);

    useEffect(() => {
        const savedTeamMemberId = resolveSalesOrderTeamMemberMembershipId(order);
        if (savedTeamMemberId && order?.team_member_name) {
            const teamMemberOption: TeamMemberOption = {
                value: savedTeamMemberId,
                label: order.team_member_name,
                firstName: order.team_member_name.split(' ')[0] || "",
                lastName: order.team_member_name.split(' ').slice(1).join(' ') || "",
                email: order.customer_email || "",
                phone: order.customer_phone || "",
                offices: order.office ? [order.office] : [],
            };
            setSelectedTeamMemberOption(teamMemberOption);
        }
    }, [
        (order as any)?.team_member_membership_id,
        order?.team_member_id,
        order?.team_member_name,
        order?.customer_email,
        order?.customer_phone,
        order?.office,
    ]);

    // Fetch offices when order loads with brokerage firm ID
    useEffect(() => {
        const brokerageFirmOrgId = resolveSalesOrderBrokerageFirmOrgId(order);
        if (brokerageFirmOrgId) {
            fetchBrokerageOfficesById(brokerageFirmOrgId);
        }
    }, [order?.brokerage_firm_org_id, order?.brokerage_firm_id]);

    useEffect(() => {
        if (formik.values.officeIddd) {
            fetchOfficeAgentsById(formik.values.officeIddd);
        }
    }, [formik.values.officeIddd]);

    // useEffect(() => {
    //     if (!formik.values.productId) return; // Ensure productId is set

    //     const selectedProductt = ProductObjs?.find(
    //         (item) => item.product_id === formik.values.productId
    //     );

    //     setSelectedProductItem(selectedProductt || null);
    // }, [formik.values.productId, ProductObjs]); // Runs when productId or ProductObjs change

    useEffect(() => {
        if (order?.manual_shipping_method) {
            setSelectedShippingMethod({
                value: order.manual_shipping_method._id,
                label: order.manual_shipping_method.name,
                cost: order.manual_shipping_method.cost,
            });
            setShippingCost(order.manual_shipping_method.cost || 0);
        }
    }, [order]);


    // Fetch products
    useEffect(() => {
        const fetchProducts = async () => {
            const signageId = effectiveSignageOrgId;
            const signageName = isSignageTeammember && effectiveSignageOrgName ? effectiveSignageOrgName : userName;
            if (!organizationId) {
                safeConsole.error("User ID is missing. Skipping API call.");
                return;
            }
            try {
                const response = await fetchSignageInventoryProductsList(token, organizationId);

                if (!response.ok) throw new Error("Failed to fetch products");
                const data = await response.json();
                setProductObjs(data);
            } catch (error) {
                safeConsole.error("Error fetching products:", error);
                showError("Failed to fetch products.");
                // Send error email notification
                await handleApiError(error, {
                    module: "sales_order",
                    action: "fetch_products",
                    userId: signageId || undefined,
                    licenseType: licenseType || undefined,
                    userName: signageName || undefined,
                    url: `${SALES_ORDER_API_BASE}${salesOrderUrls.signageInventoryProducts(organizationId)}`,
                    method: "GET"
                }, token);
            }
        };

        if (token && organizationId) fetchProducts();
    }, [organizationId, token]);

    const productOptions = ProductObjs?.flatMap((productObj) =>
        productObj.product.variants?.map((variant) => ({
            value: variant.variant_id,
            label: variant.variant_name,
            productId: productObj.product_id,
        }))
    );
    // normalize (string | {url}) → id string
    const idOf = (x?: any): string => {
        if (!x) return "";
        if (typeof x === "string") return x;
        if (typeof x?.url === "string") return x.url;
        return "";
    };

    // gather ALL ids we might need to display (product row image + custom file fields)
    const allImageIds = React.useMemo(() => {
        const ids = new Set<string>();

        // product rows (use first image or image_url id if present)
        (selectedProducts || []).forEach((row) => {
            // row.images may be: string[] | {url}[]
            const firstId = idOf(row?.images?.[0]);
            if (firstId) ids.add(firstId);
            const pv = String(row?.preview_url || row?.studio_preview_id || "").trim();
            if (pv) ids.add(pv);

            // file-type custom fields in this row
            const fields = row?.customizations?.fields || [];
            fields.forEach((f: any) => {
                if (f?.field_type === "file" && typeof f?.value === "string" && f.value.trim()) {
                    ids.add(f.value.trim());
                }
            });
        });

        return Array.from(ids).sort(); // Sort for stable reference
    }, [selectedProducts]);

    const pickerStudioBatchVariantPreviews = React.useMemo(() => {
        const fromCart = studioBatchVariantPreviewsFromCartLines(selectedProducts || []);
        const fromBatch = studioBatchVariantPreviews;
        if (!fromBatch || Object.keys(fromBatch).length === 0) return fromCart;
        return { ...fromBatch, ...fromCart };
    }, [selectedProducts, studioBatchVariantPreviews]);

    const dataForMap = React.useMemo(() => {
        if (allImageIds.length === 0) return [];
        return [
            {
                product: {
                    variants: [
                        { images: allImageIds.map((url) => ({ url })) },
                    ],
                },
            },
        ];
    }, [allImageIds]);

    // presigned urls map
    const imageUrlMap = useVariantImageUrlMap(dataForMap, token);

    // safe fallback for legacy URLs (keeps your old behavior as backup)
    const buildImageUrl = (raw?: string) => {
        if (!raw) return "";
        const cleaned = raw.replace(/\\/g, "/");
        if (/^https?:\/\//i.test(cleaned)) return cleaned;
        const base = (process.env.REACT_APP_BASE_URL || "").replace(/\/+$/, "");
        const path = cleaned.replace(/^\/+/, "");
        return `${base}/${encodeURI(path)}`;
    };

    useEffect(() => {
        if (formik.values.userType === "office" && selectedOffice?.firstName) {
            formik.setFieldValue("userFirstName", selectedOffice.firstName);
        } else if (
            formik.values.userType !== "office" &&
            selectedAgent?.firstName
        ) {
            formik.setFieldValue("userFirstName", selectedAgent.firstName);
        }

        if (formik.values.userType === "office" && selectedOffice?.firstName) {
            formik.setFieldValue("userLastName", " ");
        } else if (formik.values.userType !== "office" && selectedAgent?.lastName) {
            formik.setFieldValue("userLastName", selectedAgent.lastName);
        }

        if (formik.values.userType === "office" && selectedOffice?.firstName) {
            formik.setFieldValue("userEmail", " ");
        } else if (formik.values.userType !== "office" && selectedAgent?.email) {
            formik.setFieldValue("userEmail", selectedAgent.email);
        }

        if (formik.values.userType === "office" && selectedOffice?.phone) {
            formik.setFieldValue("userPhone", selectedOffice.phone);
        } else if (formik.values.userType !== "office" && selectedAgent?.phone) {
            formik.setFieldValue("userPhone", selectedAgent.phone);
        }
    }, [formik.values.userType, selectedOffice, selectedAgent]);

    const fetchAddresses = async () => {
        const routeId = resolveMembershipAddressesRouteId({
            selectedOfficeAgent,
            selectedAgent,
            selectedTeamMember: selectedTeamMemberOption,
            sessionMembershipId:
                (order as any)?.buyer_membership_id ??
                (order as any)?.customer_membership_id ??
                (order as any)?.team_member_membership_id ??
                (order as any)?.team_member_id ??
                (order as any)?.agent_id ??
                membershipId,
        });
        if (!routeId) {
            safeConsole.error("Membership ID is missing. Skipping address fetch.");
            return;
        }

        try {
            const data = await fetchUserAddresses(token, routeId);
            const rows = Array.isArray(data) ? data : [];
            setAddressObjects(
                mergeSalesOrderSavedAddresses(
                    rows,
                    order?.billing_address,
                    order?.shipping_address
                )
            );
        } catch (err) {
            setError(
                err instanceof Error ? err.message : "An unknown error occurred"
            );
        } finally {
            setLoading(false);
        }
    };
    const lastAddressesForRef = useRef<string | null>(null);

    useEffect(() => {
        const routeId =
            resolvePaymentMethodsMembershipId(order) ||
            resolveMembershipAddressesRouteId({
                selectedOfficeAgent,
                selectedAgent,
                selectedTeamMember: selectedTeamMemberOption,
                sessionMembershipId:
                    (order as any)?.buyer_membership_id ??
                    (order as any)?.customer_membership_id ??
                    (order as any)?.team_member_membership_id ??
                    (order as any)?.team_member_id ??
                    (order as any)?.agent_id ??
                    membershipId,
            });

        if (routeId && lastAddressesForRef.current !== routeId) {
            lastAddressesForRef.current = routeId;
            fetchAddresses();
        }
    }, [
        order,
        order?.buyer_type,
        selectedOfficeAgent,
        selectedAgent,
        selectedTeamMemberOption,
        membershipId,
    ]);

    // ========================================================================
    // EVENT HANDLERS - Product Quantity Change
    // ========================================================================
    // Handles quantity input changes for products in the table
    // When quantity changes, fetches new effective price from API
    const handleQuantityChange = (
        index: number,
        e: React.ChangeEvent<HTMLInputElement>
    ) => {
        markUserEdited();
        const rawValue = e.target.value;

        // Allow empty string for backspace/delete
        if (rawValue === "") {
            const updatedProducts = [...selectedProducts];
            updatedProducts[index] = {
                ...updatedProducts[index],
                quantity: 0,
                total: 0,
            };
            setSelectedProducts(updatedProducts);
            return;
        }

        const quantity = Number(rawValue);

        // Prevent negative values
        if (quantity < 0) {
            return;
        }

        const updatedProducts = [...selectedProducts];
        const row = updatedProducts[index];
        const key = keyFor(row);
        const manualUnit = manualUnitPriceMap[key];
        const unitForTotal =
            manualUnit !== undefined ? manualUnit : updatedProducts[index].unitPrice;

        updatedProducts[index] = {
            ...updatedProducts[index],
            quantity,
            total: unitForTotal * quantity,
        };

        setSelectedProducts(updatedProducts);

        // ✅ Recalculate via resolve when quantity changes
        if (quantity > 0) {
            if (manualUnit === undefined) {
                setManualUnitPriceMap(m => {
                    const next = { ...m };
                    delete next[key];
                    return next;
                });
            }
            scheduleSalesRecompute();
        }
    };

    // ========================================================================
    // EVENT HANDLERS - Product Unit Price Change
    // ========================================================================
    // Handles unit price input changes for products in the table
    // Allows manual override of effective price from API
    const handleUnitPriceChange = (
        index: number,
        e: React.ChangeEvent<HTMLInputElement>
    ) => {
        markUserEdited();
        const rawValue = e.target.value;

        // Allow empty string for backspace/delete
        if (rawValue === "") {
            const updatedProducts = [...selectedProducts];
        updatedProducts[index] = {
            ...updatedProducts[index],
            unitPrice: 0,
            unit_retail_price: 0,
            changed_unit_price: 0,
            total: 0,
        };
            setSelectedProducts(updatedProducts);
            return;
        }

        const price = Number(rawValue);

        // Prevent negative values
        if (price < 0) {
            return;
        }

        const updatedProducts = [...selectedProducts];
        const row = updatedProducts[index];
        const key = keyFor(row);

        updatedProducts[index] = {
            ...updatedProducts[index],
            unitPrice: price,
            unit_retail_price: price,
            changed_unit_price: price,
            total: price * updatedProducts[index].quantity,
        };

        setSelectedProducts(updatedProducts);

        // ✅ Store manual price and clear effective price
        setManualUnitPriceMap(m => ({ ...m, [key]: price }));
        setEffectiveMap(m => {
            const next = { ...m };
            delete next[key];
            return next;
        });

        // ✅ Switch to live totals when unit price changes (so tax calculations use new price)
        if (!useLiveTotals) {
            // console.log("[EDIT:UNIT_PRICE] Switching to live totals because unit price changed:", { price, index });
            setUseLiveTotals(true);
        }
        scheduleSalesRecompute();
    };
    useEffect(() => {
        if (!orderId) {
            const updatedOrderItems = orderItems?.map((item) => {
                if (item.productId === formik.values.productId) {
                    const productObj = ProductObjs?.find(
                        (p) => p.product_id === formik.values.productId
                    );
                    const variant = productObj?.product.variants.find(
                        (v) => v.variant_id === item.variantId
                    );

                    if (!productObj || !variant) return item;

                    return {
                        productId: item.productId,
                        variantId: variant.variant_id,
                        ProductName: variant?.variant_name || "",
                        sku: variant?.sku || "",
                        quantity: item.quantity,
                        images: variant.images,
                        unitPrice: Number(variant.retail_price),
                        total: Number(variant.retail_price) * Number(item.quantity),
                        is_taxable: resolveCatalogProductLineTaxable({
                            catalogProduct: productObj?.product as Record<string, unknown> | undefined,
                        }),
                        tax_rule: productObj?.product?.tax_rule || null,
                        tax_group: productObj?.product?.tax_group || null,
                        attributesList: [
                            {
                                variantId: variant.variant_id,
                                attributes: variant.attributes ?? {},
                                sku: variant.sku,
                                unitPrice: Number(variant.retail_price),
                                warehouseId: variant.inventory?.[0]?.warehouse_id || "",
                            },
                        ],
                        is_production: item.is_production || false,
                    };
                }

                return item;
            });

            setOrderItems(updatedOrderItems);
        }
    }, [formik.values.productId, ProductObjs]);


    const isFieldEditable = (status: number, field: string): boolean => {
        const editableMap: Record<number, string[]> = {
            100: [
                // Draft
                "BasicDetails",
                "BillingAddress",
                "ShippingAddress",
                "Items",
                "PaymentMethod",
                "ShippingMethod",
                "Notes",
            ],
            200: [
                // Payment Pending
                "BillingAddress",
                "ShippingAddress",
                "Items",
                "ShippingMethod",
                "Notes",
            ],
            201: [
                // Partial Payment - same as 200
                "BillingAddress",
                "ShippingAddress",
                "ShippingMethod",
                "Items",
                "Notes",
            ],
            101: [
                // order confirmed
                "BillingAddress",
                "ShippingAddress",
                "ShippingMethod",
                "Items",
                "Notes",
            ],
            202: [
                // Payment Accepted
                "ShippingAddress",
                "Notes",
                "Items",
                "ShippingMethod",
            ],
            300: [
                // Shipped
                "Notes",
            ],
            301: [
                // Delivered
                "Notes",
            ],
            302: [
                // Completed (optional)
                "Notes",
            ],
        };

        const nonEditableStatuses = [400]; // Canceled

        if (nonEditableStatuses.includes(status)) return false;
        const editableFields = editableMap[status] || [];
        return editableFields.includes(field);
    };

    useEffect(() => {
        if (studioRestoreGuardRef.current) return;
        if (orderItems?.length > 0) {
            setSelectedProducts((prev) => {
                const prevByKey = new Map<string, SelectedProduct>();
                (prev || []).forEach((r) => {
                    const id = salesLineIdentityKey(r);
                    if (id) prevByKey.set(id, r);
                });
                const mappedProducts: SelectedProduct[] = (orderItems || []).map((item: any) => {
                    const lineId = normalizeOrderLineObjectId(item?._id) || normalizeOrderLineItemId(item) || "";
                    const prevRow = (lineId && prevByKey.get(lineId)) || undefined;
                    const productObj = ProductObjs?.find((p) => p.product_id === item.product_id);
                    const variant = productObj?.product?.variants?.find(
                        (v: any) => v.variant_id === item.variant_id
                    );

                    return {
                        _id: lineId || prevRow?._id,
                        productId: item.product_id,
                        variantId: item.variant_id,
                        line_item_id: lineId || prevRow?.line_item_id,
                        ProductName: variant?.variant_name || productObj?.product?.name || item?.product_name || "",
                        sku: item.sku || variant?.sku || "",
                        quantity: item.quantity,
                        unitPrice: item.unit_retail_price,
                        total: item.total_price,
                        discount_on_total_quantity: item.discount_on_total_quantity,
                        discount_on_unit_quantity: item.discount_on_unit_quantity,
                        changed_unit_price: item.changed_unit_price,
                        item_sub_total: item.item_sub_total,
                        total_price: item.total_price,
                        total_product_tax_amount: item.item_tax_info?.total_product_tax_amount,
                        images: item?.images || [],
                        is_taxable: resolveCatalogProductLineTaxable({
                            catalogProduct: productObj?.product as Record<string, unknown> | undefined,
                            row: item as Record<string, unknown>,
                            savedTaxInfo: item?.item_tax_info as Record<string, unknown> | undefined,
                        }),
                        tax_rule: productObj?.product?.tax_rule || null,
                        tax_group: productObj?.product?.tax_group || null,
                        attributesList: variant
                            ? [{
                                variantId: variant.variant_id,
                                attributes: variant.attributes ?? {},
                                sku: variant.sku,
                                unitPrice: Number(variant.retail_price),
                                warehouseId: variant.inventory?.[0]?.warehouse_id || "",
                            }]
                            : [],
                        is_production: item.is_production || false,
                        product_type:
                            (item?.product_type ??
                                productObj?.product?.product_type ??
                                prevRow?.product_type ??
                                "") as string,
                        design_id: item?.design_id || prevRow?.design_id || prevRow?.studio_design_id || "",
                        design_name: item?.design_name || prevRow?.design_name || prevRow?.studio_design_name || "",
                        preview_url: item?.preview_url || prevRow?.preview_url || prevRow?.studio_preview_id || "",
                        studio_design_id: prevRow?.studio_design_id,
                        studio_preview_id: prevRow?.studio_preview_id,
                        studio_design_name: prevRow?.studio_design_name,
                        studio_context_id: prevRow?.studio_context_id,
                        customizations: item.customizations
                            ? {
                                template_name: item.customizations.template_name || "",
                                fields: item.customizations.fields || [],
                                _id: item.customizations._id,
                                image_url: item.customizations.image_url,
                            }
                            : undefined,
                    } as SelectedProduct;
                });

                const orderItemIds = new Set(
                    (orderItems || [])
                        .map((item: any) => normalizeOrderLineObjectId(item?._id) || normalizeOrderLineItemId(item) || "")
                        .filter(Boolean)
                );
                const userAddedRows = (prev || []).filter((r) => {
                    const id = salesLineIdentityKey(r);
                    if (!id) return true;
                    return !orderItemIds.has(id);
                });

                const prevHasCatalog = (prev || []).some(
                    (r) =>
                        String(r.productId ?? "").trim() &&
                        String(r.variantId ?? "").trim()
                );

                // No catalog rows on screen yet (initial load / race) → take orderItems.
                // Removed lines are dropped from orderItems in removeProductRow, so they stay gone.
                if (!prevHasCatalog) {
                    return [...mappedProducts, ...userAddedRows];
                }

                // Enrich rows still on screen; never re-append orderItems missing from prev.
                const mappedByKey = new Map<string, SelectedProduct>();
                (mappedProducts || []).forEach((row) => {
                    const id = salesLineIdentityKey(row);
                    if (id) mappedByKey.set(id, row as SelectedProduct);
                });
                return (prev || []).map((r) => {
                    const mapped = mappedByKey.get(salesLineIdentityKey(r));
                    if (!mapped) return r;
                    return {
                        ...mapped,
                        quantity: r.quantity ?? mapped.quantity,
                        unitPrice: r.unitPrice ?? mapped.unitPrice,
                        total: r.total ?? mapped.total,
                        _id: r._id || mapped._id,
                        line_item_id: r.line_item_id ?? mapped.line_item_id,
                        design_id: r.design_id || mapped.design_id,
                        design_name: r.design_name || mapped.design_name,
                        preview_url: r.preview_url || mapped.preview_url,
                        studio_design_id: r.studio_design_id ?? mapped.studio_design_id,
                        studio_preview_id: r.studio_preview_id ?? mapped.studio_preview_id,
                        studio_design_name: r.studio_design_name ?? mapped.studio_design_name,
                        studio_context_id: r.studio_context_id ?? mapped.studio_context_id,
                        attributesList: r.attributesList?.length
                            ? r.attributesList
                            : mapped.attributesList,
                        customizations: r.customizations ?? mapped.customizations,
                    };
                });
            });
        }
    }, [orderItems, ProductObjs]);

    const shippingAddressSig = React.useMemo(() => {
        const a = selectedShippingAddress as { address_details?: Record<string, unknown> } | null;
        const d = a?.address_details;
        if (!d) return "";
        return [
            d.addressLine1,
            d.addressLine2,
            d.city,
            d.stateCode ?? d.state,
            d.zip,
        ]
            .map((x) => String(x ?? "").trim())
            .join("|");
    }, [selectedShippingAddress]);

    React.useEffect(() => {
        if (hydratingRef.current) return;
        if (!shippingAddressSig) return;
        const hasLineItems =
            (selectedProducts || []).some((p) => p.productId && p.variantId) ||
            (customproducts || []).length > 0;
        if (!hasLineItems) return;
        scheduleSalesRecomputeRef.current?.();
    }, [
        shippingAddressSig,
        selectedProducts?.length,
        customproducts?.length,
        (customproducts || [])
            .map((cp) => `${cp.product_id}:${cp.variant_id}:${cp.quantity}:${cp.Price}:${cp.is_taxable}`)
            .join("|"),
    ]);

    // Fetch general settings to show/hide Custom Item button
    useEffect(() => {
        if (!token) return;
        fetchGeneralSettings(token)
            .then((data: { results?: { key: string; enabled: boolean }[] }) => {
                const list = Array.isArray(data?.results) ? data.results : [];
                const custom = list.find((s) => s.key === GENERAL_SETTINGS_KEYS.CUSTOM_PRODUCT);
                setCustomProductSettingEnabled(custom?.enabled ?? false);
            })
            .catch(() => setCustomProductSettingEnabled(false));
    }, [token]);

    useEffect(() => {
        const { billingAddressId, shippingAddressId, isSameAsBilling } = formik.values;

        // Office vs Agent choice
        const isOffice = !selectedOfficeAgent && !selectedAgent && !!selectedOffice;

        const findById = (id?: string | null) => {
            if (!id) return null;
            return (
                addressObjects?.find(
                    (a) =>
                        String(a._id ?? "") === id ||
                        String(a.address_details?._id ?? "") === id
                ) ||
                (isSalesOrderSavedAddress(order?.billing_address) &&
                resolveSalesOrderAddressId(order?.billing_address) === id
                    ? order?.billing_address
                    : null) ||
                (isSalesOrderSavedAddress(order?.shipping_address) &&
                resolveSalesOrderAddressId(order?.shipping_address) === id
                    ? order?.shipping_address
                    : null) ||
                null
            );
        };

        const nextBilling = findById(billingAddressId);
        const nextShipping = isSameAsBilling
            ? nextBilling
            : findById(shippingAddressId);

        // Update billing once (only if changed)
        if (nextBilling) {
            if (isOffice) {
                if (selectedOfficeBillingAddress?._id !== nextBilling._id) {
                    setSelectedOfficeBillingAddress(nextBilling);
                }
            } else {
                if (selectedBillingAddress?._id !== nextBilling._id) {
                    setSelectedBillingAddress(nextBilling);
                }
            }
        }

        // Update shipping once (only if changed)
        if (nextShipping) {
            if (isOffice) {
                if (selectedOfficeShippingAddress?._id !== nextShipping._id) {
                    setSelectedOfficeShippingAddress(nextShipping);
                }
            } else {
                if (selectedShippingAddress?._id !== nextShipping._id) {
                    setSelectedShippingAddress(nextShipping);
                }
            }
        }
    }, [
        formik.values.billingAddressId,
        formik.values.shippingAddressId,
        formik.values.isSameAsBilling,
        selectedAgent?.value,
        selectedOfficeAgent?.value,
        selectedOffice?.value,
        addressObjects,
        order?.billing_address,
        order?.shipping_address,
    ]);

    const selectedProductsWithDims = useMemo(() => {
        const catalogItems = (selectedProducts || []).map(sp => {
            const product = (ProductObjs || [])
                .find(po => String(po.product_id) === String(sp.productId))?.product;
            const variant = product?.variants?.find((v: any) =>
                String(v.variant_id) === String(sp.variantId)
            );

            const dims = variant?.package_dimensions || {
                length: 0, width: 0, height: 0, weight: 0, weight_type: "LBS",
            };

            const lineName =
                String(sp.ProductName || "").trim() ||
                String((product as any)?.name || (product as any)?.product_name || "").trim() ||
                String((variant as any)?.name || (variant as any)?.variant_name || "").trim() ||
                "Product";
            const lineSku =
                String(sp.sku || "").trim() ||
                String((variant as any)?.sku || (product as any)?.sku || "").trim();

            return {
                productId: sp.productId,
                variantId: sp.variantId,
                quantity: sp.quantity,
                name: lineName,
                sku: lineSku,
                package_dimensions: dims,
            };
        });

        const customItems = (customproducts || []).map((cp) => ({
            productId: cp.product_id,
            variantId: cp.variant_id,
            quantity: cp.quantity,
            name: String(cp.item_name || "").trim() || "Custom Item",
            sku: String(cp.sku || "").trim(),
            package_dimensions: {
                length: Number(cp.length) || 0,
                width: Number(cp.width) || 0,
                height: Number(cp.height) || 0,
                weight: Number(cp.weight) || 0,
                weight_type: "LBS" as const,
            },
        }));

        return [...catalogItems, ...customItems];
    }, [selectedProducts, ProductObjs, customproducts]);

    // ✅ Memoize totalQty to prevent unnecessary re-renders of ShippingOptions
    const totalQtyMemoized = useMemo(() => {
        const catalogQty = selectedProducts.reduce((n, r) => n + asNum(r.quantity), 0);
        const customQty = (customproducts || []).reduce((n, cp) => n + asNum(cp.quantity), 0);
        return catalogQty + customQty;
    }, [selectedProducts, customproducts]);

    // ✅ Memoize productsFull to prevent unnecessary re-renders of ShippingOptions
    const productsFullMemoized = useMemo(() => {
        return (ProductObjs ?? []).map((p: any) => ({ product_id: p.product_id, product: p.product }));
    }, [ProductObjs]);
    const currentOwner =
        selectedOfficeAgent
            ? { id: selectedOfficeAgent.value, name: selectedOfficeAgent.label, type: "agent" as const }
            : selectedAgent
                ? { id: selectedAgent.value, name: selectedAgent.label, type: "agent" as const }
                : selectedOffice
                    ? { id: selectedOffice.value, name: selectedOffice.label, type: "office" as const }
                    : null;
    // ✅ Separate logic for CouponDiscount createdById (different from currentOwner)
    const effectiveCouponCreatedById = React.useMemo(() => {
        let id = "";
        let source = "";

        // Priority: Office Agent > Team Member (for office path) > Direct Agent > Order data > Brokerage Firm
        if (selectedOfficeAgent?.value) {
            // Office Agent path: use agent ID
            id = selectedOfficeAgent.value;
            source = "Office Agent";
        } else if (selectedOffice?.value && selectedTeamMemberOption?.value) {
            // Office selected (without agent): use team member ID (NOT office ID)
            id = selectedTeamMemberOption.value;
            source = "Team Member (via Office)";
        } else if (selectedAgent?.value) {
            // Direct agent path: use agent ID
            id = selectedAgent.value;
            source = "Direct Agent";
        } else if (order?.agent_id && order?.customer_email) {
            // Fallback to order agent ID
            id = order.agent_id;
            source = "Order Agent";
        } else if (
            order?.inventory_source === "office" &&
            resolveSalesOrderTeamMemberMembershipId(order)
        ) {
            id = resolveSalesOrderTeamMemberMembershipId(order);
            source = "Order Team Member";
        } else if (selectedBrokerageFirm?.value) {
            // Brokerage Firm selected (no team member/office/agent)
            id = selectedBrokerageFirm.value;
            source = "Brokerage Firm";
        } else if (order?.brokerage_firm_id) {
            // Last fallback: order brokerage firm ID
            id = order.brokerage_firm_id;
            source = "Order Brokerage Firm";
        } else {
            id = "";
            source = "User (Fallback)";
        }

        // console.log(`🎟️ [SignageEdit] Coupon createdById: ${id} (from ${source})`);
        return id;
    }, [selectedOfficeAgent, selectedAgent, selectedOffice, selectedTeamMemberOption, selectedBrokerageFirm, order, userId]);

    const salesBuyerContext = React.useMemo(
        () =>
            pickSalesResolveBuyerContext({
                values: formik.values as any,
                selectedOfficeAgent,
                selectedAgent,
                selectedOffice,
                offices,
                selectedBrokerageFirm,
                selectedTeamMemberOption,
                selectedBillingAddress,
                selectedShippingAddress,
                selectedOfficeBillingAddress,
                selectedOfficeShippingAddress,
            }),
        [
            formik.values,
            selectedOfficeAgent,
            selectedAgent,
            selectedOffice,
            offices,
            selectedBrokerageFirm,
            selectedTeamMemberOption,
            selectedBillingAddress,
            selectedShippingAddress,
            selectedOfficeBillingAddress,
            selectedOfficeShippingAddress,
        ]
    );

    const effectiveBuyerMembershipId = React.useMemo(() => {
        const fromOrder = resolvePaymentMethodsMembershipId(order);
        if (fromOrder) return fromOrder;
        return (
            salesBuyerContext.customerId ||
            effectiveCouponCreatedById ||
            ""
        );
    }, [order, salesBuyerContext.customerId, effectiveCouponCreatedById]);

    const previousCouponCreatedById = React.useRef<string | null>(null);
    React.useEffect(() => {
        const currentId = effectiveCouponCreatedById || null;
        if (previousCouponCreatedById.current && previousCouponCreatedById.current !== currentId) {
            setFeePayload(createEmptyFeePayload());
            setLiveSpecialFee(0);
            setTaxBreakdown(prev => ({ ...prev, specialFee: 0 }));
            setBusyStates(prev => (prev.specialFee ? { ...prev, specialFee: false } : prev));
        }
        previousCouponCreatedById.current = currentId;
    }, [effectiveCouponCreatedById, createEmptyFeePayload]);

    const autoPatchRef = React.useRef<{
        inited: boolean;
        timer: ReturnType<typeof setTimeout> | null;
        inflight: boolean;
        lastHash: string;
    }>({
        inited: false,
        timer: null,
        inflight: false,
        lastHash: "",
    });

    const shippingAddrForTax =
        selectedShippingAddress ||
        selectedOfficeShippingAddress ||
        selectedBillingAddress ||
        selectedOfficeBillingAddress ||
        null;

    const round2 = (n: number) => Math.round(asNum(n) * 100) / 100;

    const mapUiShipToApi = (r: any) => {
        const id = String(r.shipping__id ?? r.shipping_id ?? "");
        const cost = shippingCosts.find((c) => String((c as any)._id) === id) as
            | { is_customer_pickup?: boolean; shipping_type?: string }
            | undefined;
        const fromRowPickup = r?.is_customer_pickup === true;
        const fromCostPickup = cost?.is_customer_pickup === true;
        const rowType = String(r.shipping_type ?? "");
        const costType = String(cost?.shipping_type ?? "");
        return {
            shipping_id: id,
            shipping_name: String(r.shipping_name ?? ""),
            shipping_type: rowType || costType || (id === "custom-manual" ? "MANUAL" : ""),
            shipping_amount: asNum(r.shipping_amount),
            shipping_tax_percentage: asNum(r.shipping_tax_percentage),
            shipping_tax_amount: asNum(r.shipping_tax_amount),
            shipping_taxable: resolveShippingTaxableForPayload(r as Record<string, unknown>, shippingCosts),
            shipping_delivery_date: String(r.shipping_delivarydate ?? r.shipping_delivery_date ?? ""),
            shipping_delivery_time: String(r.shipping_delivery_time ?? ""),
            is_customer_pickup: fromRowPickup || fromCostPickup,
        };
    };

    function buildItemTaxInfoForPatch(
        row: { productId: string; variantId: string; sku: string; unitPrice: number; quantity: number },
        productTaxMap: Record<string, number>,
        shippingAddress: any,
        isTaxable: boolean  // pass GET value as-is: true → true, false → false
    ) {
        const k = keyForRow(row);
        const lineTax = asNum(productTaxMap[k] || 0);
        const qty = Math.max(1, asNum(row.quantity || 1));
        const unitTax = qty > 0 ? lineTax / qty : 0;
        const base = asNum(row.unitPrice || 0) * qty;
        const ratePct = base > 0 ? (lineTax / base) * 100 : 0;
        const appliedState = String(
            shippingAddress?.address_details?.stateCode ||
            shippingAddress?.address_details?.state || ""
        );
        return {
            tax_percentage: round2(ratePct),
            unit_product_tax_amount: round2(unitTax),
            total_product_tax_amount: round2(lineTax),
            tax_details: {
                tax_rate: round2(ratePct),
                tax_amount: round2(lineTax),
                applied_state: appliedState,
            },
            taxable: isTaxable,  // always pass GET value, do not derive
        };
    }

    // ✅ Extract shipping selection from order (runs once when order loads)
    useEffect(() => {
        if (!order?.id) return;

        const so = order?.order_charges?.[0]?.shipping_option?.[0];
        // console.log("[EDIT:SHIP] Extracting shipping from order:", so);

        if (!so) {
            // console.log("[EDIT:SHIP] No shipping option found in order");
            setInitialSelectedShipIds(null);
            return;
        }

        // ✅ Order data from server uses shipping_id (single underscore)
        const rawId = String((so as any).shipping_id || (so as any).shipping__id || "");
        // console.log("[EDIT:SHIP] Raw shipping ID:", rawId);

        // simple heuristic: short code like "03"/"2DA" => UPS; long hexy id => manual
        const isUps = /^[0-9A-Z]{2,3}$/.test(rawId) && rawId.length <= 3;
        // console.log("[EDIT:SHIP] Is UPS?", isUps);

        const shipIds = {
            manual_shipping_id: isUps ? null : rawId,
            ups_service_code: isUps ? rawId : null,
            integration_id: null,
            shipper_address_id: null,
        };

        // console.log("[EDIT:SHIP] Setting initialSelectedShipIds:", shipIds);
        setInitialSelectedShipIds(shipIds);
    }, [order?.id]);

    // in SignageEditOrder.tsx
    const initialSelectedShipIdsOnce = React.useRef<ShipSelectedIds | undefined>(undefined);
    if (!initialSelectedShipIdsOnce.current && initialSelectedShipIds) {
        initialSelectedShipIdsOnce.current = initialSelectedShipIds;
    }


    useEffect(() => {
        if (!order) return;

        // brokerage firm (label only—admin not in payload)
        if (order.brokerage_firm_id && order.brokerage_firm_name) {
            setSelectedBrokerageFirm({
                value: order.brokerage_firm_id,
                label: order.brokerage_firm_name,
                firstName: "",
                lastName: "",
                email: "",
                phone: "",
            });
        }

        // agent present
        if (order?.agent_id && !order?.office?._id) {
            const first = order.customer_first_name || order.agent_name?.split(" ")?.[0] || "";
            const last = order.customer_last_name || (order.agent_name?.split(" ")?.slice(1)?.join(" ") || "");
            setSelectedAgent({
                value: order.agent_id,
                label: order.agent_name || `${first} ${last}`.trim(),
                firstName: first,
                lastName: last,
                email: order.customer_email || "",
                phone: order.customer_phone || "",
            });
        }
        // officeagent present
        if (order?.agent_id && order?.office?._id) {
            const first = order.customer_first_name || order.agent_name?.split(" ")?.[0] || "";
            const last = order.customer_last_name || (order.agent_name?.split(" ")?.slice(1)?.join(" ") || "");
            setSelectedOfficeAgent({
                value: order.agent_id,
                label: order.agent_name || `${first} ${last}`.trim(),
                firstName: first,
                lastName: last,
                email: order.customer_email || "",
                phone: order.customer_phone || "",
            });
        }

        // team member (if any)
        const savedTeamMemberId = resolveSalesOrderTeamMemberMembershipId(order);
        if (savedTeamMemberId) {
            const tmFirst = order.team_member_name?.split(" ")?.[0] || "";
            const tmLast = (order.team_member_name?.split(" ")?.slice(1)?.join(" ") || "");
            setSelectedTeamMemberOption({
                value: savedTeamMemberId,
                label: order.team_member_name || "",
                firstName: tmFirst,
                lastName: tmLast,
                email: order.customer_email || "",
                phone: order.customer_phone || "",
                offices: order.office ? [order.office] : [],
            });
        }

        // office (if any)
        if (order?.office?._id) {
            setSelectedOffice({
                value: order.office._id,
                label: order.office.officeName || "",
                firstName: order.office.officeName || "",
                lastName: "",
                email: "",
                phone: "",
            });
        }

    }, [order]);

    const [notes, setNotes] = React.useState<string>(order?.notes ?? "");
    const [instructionFileUrlMap, setInstructionFileUrlMap] = useState<Record<string, string>>({});

    // Keep local state in sync if order prop changes later
    React.useEffect(() => {
        setNotes(order?.notes ?? "");
    }, [order?.id, order?.notes]);

    useEffect(() => {
        let cancelled = false;
        const run = async () => {
            const files = Array.isArray((order as any)?.uploaded_files) ? (order as any).uploaded_files : [];
            const fileIds = files
                .map((f: any) => String(f?.file_id || "").trim())
                .filter((id: string) => /^[0-9a-f]{24}$/i.test(id));
            if (!token || fileIds.length === 0) {
                if (!cancelled) setInstructionFileUrlMap({});
                return;
            }
            try {
                const map = await batchPresignUrls(fileIds, token);
                if (!cancelled) setInstructionFileUrlMap(map || {});
            } catch {
                if (!cancelled) setInstructionFileUrlMap({});
            }
        };
        run();
        return () => {
            cancelled = true;
        };
    }, [(order as any)?.uploaded_files, token]);

    const instructionAttachments = useMemo(() => {
        const files = Array.isArray((order as any)?.uploaded_files) ? (order as any).uploaded_files : [];
        return files.map((f: any, index: number) => {
            const fileId = String(f?.file_id || "").trim();
            const href = (fileId && instructionFileUrlMap[fileId]) || String(f?.file_url || "").trim();
            return {
                id: fileId || `uploaded-${index}`,
                name: String(f?.file_name || "Attachment"),
                href,
                previewUrl: href,
                dateLabel: f?.uploaded_date ? new Date(f.uploaded_date).toLocaleDateString() : undefined,
            };
        });
    }, [(order as any)?.uploaded_files, instructionFileUrlMap]);

    // lightweight notifiers (fallback to console if your app’s toasts aren’t available)
    const showSuccess = (msg: string) => {
        // @ts-ignore
        if (typeof window !== "undefined" && window?.toastr?.success) { /* global toast */
            // @ts-ignore
            window.toastr.success(msg);
        } else {
            // console.log("✅", msg);
        }
    };
    const showError = (msg: string) => {
        // @ts-ignore
        if (typeof window !== "undefined" && window?.toastr?.error) {
            // @ts-ignore
            window.toastr.error(msg);
        } else {
            safeConsole.error("❌", msg);
        }
    };

    // Save handler the <Instructions /> will call (text first; pending files upload after via upload-documents-to-order)
    const handleSaveInstructionsOnly = async (
        newNotes: string,
        context?: { hasPendingFiles: boolean; pendingFiles?: File[] }
    ) => {
        const orderUpdateId = String((order as any)?._id || order?.id || orderId || "").trim();
        if (!orderUpdateId) {
            showError("Missing order id");
            return;
        }

        try {
            const response = await patchOrderTax(token, orderUpdateId, {
                version: (order as any)?.version,
                notes: newNotes,
            });

            if (response.ok) {
                showSuccess("Instructions updated successfully!");
                setNotes(newNotes);
                if (!context?.hasPendingFiles) {
                    await fetchOrderData();
                }
            } else {
                showError("Failed to update instructions");
            }
        } catch (error: any) {
            showError(`Error updating instructions: ${error?.message || "Unknown error"}`);
            await handleApiError(error, {
                module: "sales_order",
                action: "update_instructions",
                userId: userId || undefined,
                licenseType: licenseType || undefined,
                userName: userName || undefined,
                url: `${SALES_ORDER_API_BASE}${salesOrderUrls.orderUpdateTax(orderUpdateId)}`,
                method: "PATCH",
            }, token);
        }

        if (context?.hasPendingFiles && context.pendingFiles?.length && token) {
            await uploadOrderDocumentsToOrder(token, orderUpdateId, context.pendingFiles);
        }
    };


    React.useEffect(() => {
        const rows = Array.isArray(shipPayload?.shipping_option) ? shipPayload.shipping_option : [];
        const amt = rows.reduce((s: number, r: any) => s + Number(r?.shipping_amount || 0), 0);
        const tax = rows.reduce((s: number, r: any) => s + Number(r?.shipping_tax_amount || 0), 0);
        setLiveShipping(amt);

        // ✅ Switch to live totals when shipping changes (so Order Totals updates immediately)
        if (!hydratingRef.current && rows.length > 0) {
            const savedShippingAmt = n(
                (order?.order_charges?.[0]?.shipping_option || []).reduce((s: number, r: any) => s + n(r?.shipping_amount), 0)
            );

            if (amt !== savedShippingAmt) {
                // console.log("[EDIT:SHIPPING] Switching to live totals because shipping changed:", { amt, savedShippingAmt });
                setUseLiveTotals(true); // ✅ FIXED: Should be true, not false!
            }
        }
    }, [shipPayload?.shipping_option, order?.order_charges]);

    // Keep refs in sync for "only update when user changes shipping option" logic
    React.useEffect(() => {
        const rows = shipPayload?.shipping_option || [];
        const savedRows = order?.order_charges?.[0]?.shipping_option || [];
        const payloadSum = rows.reduce((s: number, r: any) => s + n(r?.shipping_amount), 0);
        const savedSum = savedRows.reduce((s: number, r: any) => s + n(r?.shipping_amount), 0);
        lastKnownShippingAmountRef.current = rows.length > 0 && payloadSum > 0 ? payloadSum : savedSum;
        const ids = rows.length > 0
            ? rows.map((r: any) => String(r?.shipping__id ?? r?.id ?? "")).filter(Boolean).sort().join(",")
            : savedRows.map((r: any) => String(r?.shipping_id ?? r?.shipping__id ?? r?.id ?? "")).filter(Boolean).sort().join(",");
        lastShipOptionIdsRef.current = ids;
    }, [shipPayload?.shipping_option, order?.order_charges]);

    // 🔄 Reflect SpecialFee payload into parent totals
    // Special-fee totals from fee payload
    React.useEffect(() => {
        const rows = Array.isArray(feePayload?.specialfee) ? feePayload.specialfee : [];
        const amt = rows.reduce((s: number, r: any) => s + Number(r?.specialfee_amount || 0), 0);
        const tax = rows.reduce((s: number, r: any) => s + Number(r?.specialfee_tax_amount || 0), 0);
        setLiveSpecialFee(amt);

        // Only switch to live totals if user has made changes (not during initial load)
        if (!hydratingRef.current) {
            // Calculate saved special fee amount
            const savedFeeAmt = n(
                (order?.order_charges?.[0]?.specialfee || []).reduce((s: number, r: any) => s + n(r?.specialfee_amount), 0)
            );

            if (amt !== savedFeeAmt) {
                // console.log("[EDIT:SPECIAL_FEE] Switching to live totals because special fee changed:", { amt, savedFeeAmt });
                setUseLiveTotals(true);
            }
        }
        // console.log("[EDIT:SPECIAL_FEE_TOTALS]", { amt, tax });
    }, [feePayload?.specialfee, order?.order_charges]); // use direct reference for better change detection


    // 🔄 Derive shipping & fee amounts/taxes directly from payloads
    const shippingAmount = React.useMemo(() => {
        return (shipPayload?.shipping_option || []).reduce(
            (s: number, r: any) => s + n(r?.shipping_amount),
            0
        );
    }, [shipPayload?.shipping_option]);

    const shippingTaxAmount = React.useMemo(() => {
        return (shipPayload?.shipping_option || []).reduce(
            (s: number, r: any) => s + n(r?.shipping_tax_amount),
            0
        );
    }, [shipPayload?.shipping_option]);

    const specialFeeAmount = React.useMemo(() => {
        return (feePayload?.specialfee || []).reduce(
            (s: number, r: any) => s + n(r?.specialfee_amount),
            0
        );
    }, [feePayload?.specialfee]);

    const specialFeeTaxAmount = React.useMemo(() => {
        return (feePayload?.specialfee || []).reduce(
            (s: number, r: any) => s + n(r?.specialfee_tax_amount),
            0
        );
    }, [feePayload?.specialfee]);

    // ✅ Saved amounts from the original order (used as initial fallback)
    const savedShippingAmount = React.useMemo(() => {
        const rows = order?.order_charges?.[0]?.shipping_option || [];
        return rows.reduce((s: number, r: any) => s + n(r?.shipping_amount), 0);
    }, [order?.order_charges]);

    const savedSpecialFeeAmount = React.useMemo(() => {
        const rows = order?.order_charges?.[0]?.specialfee || [];
        return rows.reduce((s: number, f: any) => s + n(f?.specialfee_amount), 0);
    }, [order?.order_charges]);

    // ✅ What the UI should show for shipping & fee:
    //    - If user has any shipping rows in payload → use them (even if 0)
    //    - Otherwise, use saved values from order
    const shippingAmountToShow = React.useMemo(() => {
        const rows = shipPayload?.shipping_option || [];
        if (rows.length > 0) {
            return rows.reduce((s: number, r: any) => s + n(r?.shipping_amount), 0);
        }
        return savedShippingAmount;
    }, [shipPayload?.shipping_option, savedShippingAmount]);

    const specialFeeAmountToShow = React.useMemo(() => {
        const rows = feePayload?.specialfee || [];
        if (rows.length > 0) {
            return rows.reduce((s: number, f: any) => s + n(f?.specialfee_amount), 0);
        }
        return savedSpecialFeeAmount;
    }, [feePayload?.specialfee, savedSpecialFeeAmount]);



    // Update tax breakdown when shipping or special fees change
    React.useEffect(() => {
        if (!useLiveTotals) return; // Only update when live totals are enabled

        const shippingTax = shippingTaxAmount;
        const specialFeeTax = specialFeeTaxAmount;

        setTaxBreakdown(prev => ({
            ...prev,
            shipping: shippingTax,
            specialFee: specialFeeTax
        }));
    }, [useLiveTotals, shippingTaxAmount, specialFeeTaxAmount]);

    const effectiveMapdata = useMemo(() => {
        const out: Record<string, number> = {};
        for (const row of (selectedProducts || [])) {
            const key = keyFor(row);
            const qty = Math.max(1, Number(row.quantity || 0));
            const baseUnit = getUnitPriceToUse(row); // ✅ Use manual or effective price
            const lineBase = baseUnit * qty;
            const rowDiscount = Number(perItemCouponMap[key] || 0);
            const lineAfter = Math.max(0, lineBase - rowDiscount);
            const changedUnit = lineAfter / Math.max(1, qty);
            out[key] = Math.round(changedUnit * 100) / 100;
        }
        return out;
    }, [selectedProducts, perItemCouponMap, getUnitPriceToUse]);

    // Any line net (after discount) that is <= 0?
    const hasZeroOrNegativeLine = React.useMemo(() => {
        return (selectedProducts || []).some((row) => {
            const key = keyFor(row);
            const qty = Number(row.quantity || 0);
            if (qty <= 0) return true;
            const base = getUnitPriceToUse(row) * qty; // ✅ Use manual or effective price
            const disc = Number(perItemCouponMap[key] || 0);
            const net = base - disc;
            return net <= 0;
        });
    }, [selectedProducts, perItemCouponMap, getUnitPriceToUse]);

    // console.log(hasZeroOrNegativeLine, "hasZeroOrNegativeLine");

    // Friendly message (shown under table) and you can also reuse this to disable submit
    const zeroOrNegativeMsg =
        hasZeroOrNegativeLine
            ? "Subtotal cannot be zero or negative after discount. Please adjust quantity or unit price, or remove the coupon."
            : null;

    const num = (v: any) => (Number.isFinite(Number(v)) ? Number(v) : 0);
    const markUserEdited = React.useCallback(() => {
        enableLiveTotals();
    }, [enableLiveTotals]);

    // ✅ Handle edit shipping amount - always use current payload value
    const handleEditShipping = () => {
        const currentAmount = n(shipPayload?.shipping_option?.reduce((s: number, r: any) => s + n(r.shipping_amount), 0));
        setManualShippingAmount(currentAmount.toString());
        setIsEditingShipping(true);
    };

    // ✅ Handle cancel edit
    const handleCancelEditShipping = () => {
        setIsEditingShipping(false);
        setManualShippingAmount("");
    };

    // ✅ Handle save edited shipping amount: keep user-selected option and apply custom amount so payload has both (same as Create)
    const handleSaveShipping = () => {
        const newAmount = Number(manualShippingAmount);

        if (isNaN(newAmount) || newAmount < 0) {
            showError("Please enter a valid shipping amount");
            return;
        }

        const currentRows = shipPayload?.shipping_option ?? [];

        if (currentRows.length > 0) {
            // Keep user-selected shipping option(s) and override amount on first row with custom value so submit passes both
            const updatedRows = currentRows.map((row: any, i: number) =>
                i === 0 ? { ...row, shipping_amount: newAmount, cost: newAmount } : row
            );
            const newPayload = {
                ...shipPayload,
                shipping_option: updatedRows,
            };
            shipPayloadRef.current = newPayload;
            setShipPayload(newPayload);
            lastShipJsonRef.current = JSON.stringify(updatedRows);
            userEditedShippingAmountRef.current = true;
            showSuccess("Shipping amount updated! Tax will recalculate.");
        } else {
            // No selection yet: create custom manual entry only
            const customShippingEntry = {
                shipping__id: "custom-manual",
                shipping_id: "custom-manual",
                shipping_name: "Custom Shipping (User Edited)",
                shipping_type: "MANUAL",
                is_customer_pickup: false,
                shipping_amount: newAmount,
                cost: newAmount,
                shipping_tax_percentage: 0,
                shipping_tax_amount: 0,
                shipping_taxable: true,
                shipping_delivarydate: "",
                shipping_delivery_time: "",
            };
            const newPayload = {
                ...shipPayload,
                shipping_option: [customShippingEntry],
            };
            shipPayloadRef.current = newPayload;
            setShipPayload(newPayload);
            lastShipJsonRef.current = JSON.stringify([customShippingEntry]);
            userEditedShippingAmountRef.current = true;
            showSuccess("Shipping amount updated.");
        }

        markUserEdited();
        setIsEditingShipping(false);
        setManualShippingAmount("");
    };

    const removeCustomProduct = (index: number) => {
        setCustomproducts((prev) => prev.filter((_, i) => i !== index));
        setPurchasedProductFieldErrors((prev) => {
            const next: Record<string, PurchasedProductFieldErrors> = {};
            Object.entries(prev).forEach(([key, value]) => {
                if (!key.startsWith("custom:")) {
                    next[key] = value;
                    return;
                }
                const customIdx = Number(key.slice("custom:".length));
                if (customIdx < index) next[key] = value;
                else if (customIdx > index) {
                    next[purchasedProductErrorKey("custom", String(customIdx - 1))] = value;
                }
            });
            return next;
        });
        markUserEdited();
        scheduleSalesRecomputeRef.current?.();
    };
    const updateCustomProduct = (index: number, updates: { quantity?: number; Price?: number }) => {
        setCustomproducts((prev) => prev.map((p, i) => (i === index ? { ...p, ...updates } : p)));
        markUserEdited();
        scheduleSalesRecomputeRef.current?.();
    };

    const validateAllPurchasedProductFields = React.useCallback((): boolean => {
        const nextErrors: Record<string, PurchasedProductFieldErrors> = {};
        const setErr = (key: string, field: keyof PurchasedProductFieldErrors, msg: string) => {
            nextErrors[key] = { ...nextErrors[key], [field]: msg };
        };

        for (const product of selectedProducts || []) {
            const rowKey = keyFor(product);
            const errorKey = purchasedProductErrorKey("catalog", rowKey);
            const qtyErr = validatePurchasedQuantityValue(product.quantity);
            if (qtyErr) setErr(errorKey, "quantity", qtyErr);
            const unitRaw =
                manualUnitPriceMap[rowKey] !== undefined
                    ? String(manualUnitPriceMap[rowKey])
                    : Number(product.unitPrice || 0) > 0
                        ? String(product.unitPrice)
                        : "";
            const unitErr = validatePurchasedUnitPriceValue(unitRaw);
            if (unitErr) setErr(errorKey, "unitPrice", unitErr);
        }

        (customproducts || []).forEach((cp, idx) => {
            const errorKey = purchasedProductErrorKey("custom", String(idx));
            const qtyErr = validatePurchasedQuantityValue(cp.quantity);
            if (qtyErr) setErr(errorKey, "quantity", qtyErr);
            const unitErr = validatePurchasedUnitPriceValue(cp.Price);
            if (unitErr) setErr(errorKey, "unitPrice", unitErr);
        });

        setPurchasedProductFieldErrors(nextErrors);
        return Object.keys(nextErrors).length === 0;
    }, [selectedProducts, customproducts, manualUnitPriceMap]);

    validateAllPurchasedProductFieldsRef.current = validateAllPurchasedProductFields;

    // ✅ Removed duplicate server calculations - using direct order values where needed




    // Prefer explicit discount fields; else infer from unit vs changed unit
    const getRowDiscount = (it: any): number => {
        const d1 = n(it?.discount_on_total_quantity);
        // console.log("d1", d1);
        if (d1 > 0) return d1;

        // fallback inference
        const qty = n(it?.quantity);
        const unit = n(it?.unit_retail_price ?? it?.unit_price ?? it?.unit_purchase_price);
        const changed = n(it?.changed_unit_price || unit);
        if (qty > 0 && unit >= changed) return r2(qty * (unit - changed));
        return 0;
    };

    // Prefer explicit post-discount pre-tax subtotal
    const getItemSubTotal = (it: any): number => {
        // console.log("itedit", it);
        const explicit = n(it?.total_price);
        if (explicit > 0) return explicit;
        const qty = n(it?.quantity);
        const changed = n(it?.changed_unit_price);
        if (qty > 0 && changed > 0) return r2(qty * changed);
        return n(it?.total_price); // last fallback
    };

    // From backend - ✅ Calculate subtotal from item_sub_total (changed_unit_price * quantity)
    const initialSubTotal = React.useMemo(() => {
        const items = Array.isArray(order?.items) ? (order?.items ?? []) : [];
        return r2(items.reduce((s: number, it: any) => s + n(it?.subtotal_amount), 0));
    }, [order?.items]);

    // Subtotal to show (products subtotal after discount, pre-tax)
    const subTotalToShow = React.useMemo(() => {
        const rows = Array.isArray(selectedProducts) ? selectedProducts : [];
        let sum = 0;
        for (const row of rows) {
            const qty = Math.max(1, Number(row?.quantity ?? 0));
            const unit = getUnitPriceToUse(row);
            sum += qty * unit;
        }
        const customSum = (customproducts || []).reduce(
            (s, cp) => s + Number(cp.Price || 0) * Math.max(0, Number(cp.quantity || 1)),
            0
        );
        return Math.round((sum + customSum) * 100) / 100;
    }, [selectedProducts, customproducts, getUnitPriceToUse]);



    // Grand total to show



    // tiny helpers (if you don't already have them here)
    // ------- SAVED TAX (from server response) -------
    const savedProductTax = React.useMemo(() => {
        const items = Array.isArray(order?.items) ? order!.items! : [];
        return r2((items || []).reduce((s: number, it: any) => s + n(it?.item_tax_info?.total_product_tax_amount), 0));
    }, [order?.items]);
    // console.log("savedProductTax", savedProductTax);

    const savedShippingTax = React.useMemo(() => {
        const rows = order?.order_charges?.[0]?.shipping_option || [];
        return r2(rows.reduce((s: number, r: any) => s + n(r?.shipping_tax_amount), 0));
    }, [order?.order_charges]);

    const savedFeeTax = React.useMemo(() => {
        const rows = order?.order_charges?.[0]?.specialfee || [];
        return r2(rows.reduce((s: number, r: any) => s + n(r?.specialfee_tax_amount), 0));
    }, [order?.order_charges]);
    // console.log("savedFeeTax", savedFeeTax);

    // ------- LIVE TAX (current UI/engines) -------
    const liveProductTax = n(productTotals?.productTax ?? 0);
    // console.log("liveProductTax", liveProductTax);
    const liveShippingTax = n(shippingTaxAmount ?? 0);
    const liveFeeTax = n(specialFeeTaxAmount ?? 0);

    // ------- Which to show? Default = saved. Flip to live after user edits. -------
    const productTaxToShow = useLiveTotals ? liveProductTax : savedProductTax;
    const shippingTaxToShow = useLiveTotals ? liveShippingTax : savedShippingTax;
    const feeTaxToShow = useLiveTotals ? liveFeeTax : savedFeeTax;

    // total tax in the card header
    const totalTaxAll = r2(productTaxToShow + shippingTaxToShow + feeTaxToShow);

    // ✅ Track when order data is first loaded
    const hydratedOnceRef = React.useRef(false);
    const orderLoadedRef = React.useRef(false);

    // Mark order as loaded once we have it
    React.useEffect(() => {
        if (order && !orderLoadedRef.current) {
            orderLoadedRef.current = true;
            hydratedOnceRef.current = false; // Reset so next change triggers live mode
        }
    }, [order?.id]);

    // ✅ Use stable keys to prevent triggering on every render
    const selectedProductsKey = useMemo(() => JSON.stringify(selectedProducts), [selectedProducts]);
    const shipPayloadKey = useMemo(() => JSON.stringify(shipPayload), [shipPayload]);
    const feePayloadKey = useMemo(() => JSON.stringify(feePayload), [feePayload]);

    // values from the server (fallbacks)
    const storedDiscountAmount = Number(
        order?.discount_amount ??
        0
    );

    const storedDiscountCode =
        order?.discount_code ??
        "";

    const salesDiscountDisplay = useLiveTotals
        ? liveDiscountDisplay
        : getSalesOrderDiscountFieldDisplay({
            liveCode: couponInput,
            resolved: lastResolvedSalesRef.current,
            savedCode: storedDiscountCode,
            savedAmount: storedDiscountAmount,
            userClearedCoupon: userClearedCouponRef.current,
        });
    const discountToShow = salesDiscountDisplay.amount;
    const codeToShow = salesDiscountDisplay.code;

    const storedPerItemDiscountMap = React.useMemo(() => {
        const rows = selectedProducts || [];
        const bases = rows.map((r) => {
            const qty = Math.max(1, num(r.quantity));
            const base = num(r.unitPrice) * qty;
            return { key: keyFor(r), base };
        });

        const subtotal = bases.reduce((s, b) => s + b.base, 0);
        const totalDisc = Math.min(storedDiscountAmount, subtotal);

        if (subtotal <= 0 || totalDisc <= 0) return {} as Record<string, number>;

        // proportional allocation + rounding fix on the last line
        const out: Record<string, number> = {};
        let allocated = 0;

        bases.forEach((b, idx) => {
            const isLast = idx === bases.length - 1;
            let share = totalDisc * (b.base / subtotal);
            share = Math.min(share, b.base); // never exceed the row base
            let assign = isLast ? round2(totalDisc - allocated) : round2(share);
            allocated = round2(allocated + assign);
            out[b.key] = assign;
        });

        return out;
    }, [selectedProducts, storedDiscountAmount]);
    const perRowDiscountMapToShow: Record<string, number> = React.useMemo(() => {
        return userAppliedCoupon ? (perItemCouponMap || {}) : storedPerItemDiscountMap;
    }, [userAppliedCoupon, perItemCouponMap, storedPerItemDiscountMap]);

    const formikValuesForStudioSnapshotRef = React.useRef(formik.values);
    formikValuesForStudioSnapshotRef.current = formik.values;

    buildEditOrderSnapshotForStudioRef.current = (values: any) =>
        buildSignageEditOrderStudioSnapshot({
            values,
            order,
            selectedProducts,
            customproducts,
            ProductObjs,
            shipPayload,
            feePayload,
            selectedBillingAddress,
            selectedShippingAddress,
            selectedOfficeAgent,
            selectedAgent,
            selectedOffice,
            offices,
            selectedOfficeBillingAddress,
            selectedOfficeShippingAddress,
            selectedBrokerageFirm,
            shippingCosts,
            productTaxMap,
            taxBreakdown,
            perRowDiscountMapToShow,
            getUnitPriceToUse,
            getCustomProductKey,
            userAppliedCoupon,
            couponDiscounts: couponInput.trim() ? [{ id: couponInput.trim() }] : [],
            selectedShippingMethod,
            linkedProjectId,
            linkedProjectNumber,
            isSignageTeammember,
            effectiveSignageOrgId,
            effectiveSignageOrgName,
            userId,
            userName,
        });

    const editOrderStudioCustomization = React.useMemo(
        (): StudioCustomizationFlowProps => ({
            source: "signage_create_order",
            existingBatchId: studioReturnBatchId.trim() || undefined,
            getOrderSnapshot: async () =>
                buildEditOrderSnapshotForStudioRef.current?.(formikValuesForStudioSnapshotRef.current) ?? {},
        }),
        [studioReturnBatchId]
    );

    React.useEffect(() => {
        const key = `${studioReturnBatchId}|${studioReturnContextId}`;
        if (!studioReturnBatchId || !studioReturnContextId || !token) return;
        if (restoredStudioReturnKeyRef.current === key) return;
        if (!(selectedProducts?.length > 0)) return;

        let cancelled = false;

        const mergeStudioContextsIntoRows = (contexts: ReturnType<typeof normalizeCustomizationBatchContexts>) => {
            setSelectedProducts((prev) => {
                if (!prev.length) return prev;
                let changed = false;
                const updated = prev.map((row) => {
                    const ctx = contexts.find(
                        (c) =>
                            (studioReturnContextId && c.context_id === studioReturnContextId) ||
                            c.line_item_key === row.variantId ||
                            c.variant_id === row.variantId ||
                            c.product_id === row.productId
                    );
                    if (!ctx || !ctx.design_id) return row;
                    changed = true;
                    return {
                        ...row,
                        studio_design_id: ctx.design_id,
                        studio_preview_id: ctx.preview_id,
                        studio_design_name: ctx.design_name,
                        studio_context_id: ctx.context_id,
                        design_id: ctx.design_id,
                        design_name: String(ctx.design_name ?? ""),
                        preview_url: String(ctx.preview_id ?? ""),
                    };
                });
                return changed ? updated : prev;
            });
        };

        (async () => {
            studioRestoreGuardRef.current = true;
            try {
                const batch = await getCustomizationBatch(studioReturnBatchId);
                if (cancelled) return;

                const contexts = normalizeCustomizationBatchContexts(batch);
                const batchOrder = (batch as { order?: Record<string, any> }).order;

                mergeStudioContextsIntoRows(contexts);
                setStudioBatchVariantPreviews(studioBatchVariantPreviewsFromBatchItems(batch as { items?: unknown[] }));

                if (batchOrder && typeof batchOrder === "object") {
                    const waitForAddressList = async () => {
                        for (let i = 0; i < 100; i++) {
                            if (cancelled) return [];
                            const a = addressObjectsRef.current;
                            if (Array.isArray(a) && a.length) return a;
                            await new Promise((r) => window.setTimeout(r, 100));
                        }
                        return Array.isArray(addressObjectsRef.current) ? addressObjectsRef.current : [];
                    };

                    const addrList = await waitForAddressList();
                    if (cancelled) return;

                    const addrBind = studioRestoreAddressBindings(batchOrder, addrList);
                    formik.setFieldValue("isSameAsBilling", addrBind.formik.isSameAsBilling);
                    formik.setFieldValue("billingAddressId", addrBind.formik.billingAddressId);
                    formik.setFieldValue("shippingAddressId", addrBind.formik.shippingAddressId);
                    formik.setFieldValue("notes", addrBind.formik.notes);

                    if (addrBind.useOfficeBilling) {
                        setSelectedOfficeBillingAddress(addrBind.billingAddressObj);
                        setSelectedOfficeShippingAddress(addrBind.shippingAddressObj);
                    } else {
                        setSelectedBillingAddress(addrBind.billingAddressObj);
                        setSelectedShippingAddress(addrBind.shippingAddressObj);
                    }
                }

                mergeStudioContextsIntoRows(contexts);

                if (!cancelled) {
                    restoredStudioReturnKeyRef.current = key;
                }
            } catch (err) {
                safeConsole.warn("Studio batch restore failed (edit order):", err);
            } finally {
                studioRestoreGuardRef.current = false;
            }
        })();

        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps -- restore when URL + auth + lines are ready only
    }, [studioReturnBatchId, studioReturnContextId, token, selectedProducts.length, order?.id]);

    // Saved totals from GET (fallbacks)
    const savedOverallTax = n(order?.order_tax_summary?.over_all_tax_amount);

    // If we ever need to recompute saved buckets again:
    const savedProductTaxFromOrder = React.useMemo(() => {
        return (order?.items || []).reduce(
            (s: number, it: any) => s + n(it?.item_tax_info?.total_product_tax_amount),
            0
        );
    }, [order]);

    const savedShipTaxFromOrder = React.useMemo(() => {
        const rows = order?.order_charges?.[0]?.shipping_option ?? [];
        return rows.reduce((s: number, r: any) => s + n(r?.shipping_tax_amount), 0);
    }, [order]);

    const savedFeeTaxFromOrder = React.useMemo(() => {
        const rows = order?.order_charges?.[0]?.specialfee ?? [];
        return rows.reduce((s: number, f: any) => s + n(f?.specialfee_tax_amount), 0);
    }, [order]);

    // Calculate saved row tax for all products to avoid using hooks inside JSX
    const savedRowTaxMap = React.useMemo(() => {
        const map: Record<string, number> = {};
        if (order?.items) {
            let customIdx = 0;
            order.items.forEach((item: any) => {
                const isCustom =
                    item?.inventory_source === "custom" || !item?.product_id;
                const key = isCustom
                    ? getCustomProductKey(
                          {
                              _id: normalizeOrderLineItemId(item),
                              line_item_id: normalizeOrderLineItemId(item),
                              product_id: String(item?.product_id || ""),
                              variant_id: String(item?.variant_id || ""),
                          },
                          customIdx++
                      )
                    : salesLineIdentityKey(item);
                map[key] = Number(item?.item_tax_info?.total_product_tax_amount || 0);
            });
        }
        return map;
    }, [order]);

    // What the UI should show right now:
    const uiProductTax = useLiveTotals ? n(taxBreakdown.product) : n(savedProductTaxFromOrder);
    const uiShipTax = useLiveTotals ? n(taxBreakdown.shipping) : n(savedShipTaxFromOrder);
    const uiFeeTax = useLiveTotals ? n(taxBreakdown.specialFee) : n(savedFeeTaxFromOrder);

    // Grand tax shown in "Order Totals"
    const uiTotalTax = React.useMemo(() => {
        void resolvedTotalsTick;
        if (useLiveTotals) {
            const fromResolved = lastResolvedSalesRef.current?.order_tax_summary?.over_all_tax_amount;
            if (fromResolved != null && Number.isFinite(Number(fromResolved))) {
                return r2(Number(fromResolved));
            }
        }
        if (useLiveTotals) {
            return r2(uiProductTax + uiShipTax + uiFeeTax);
        }
        return savedOverallTax > 0
            ? r2(savedOverallTax)
            : r2(uiProductTax + uiShipTax + uiFeeTax);
    }, [
        useLiveTotals,
        uiProductTax,
        uiShipTax,
        uiFeeTax,
        savedOverallTax,
        resolvedTotalsTick,
    ]);

    // ✅ Calculate current shipping and special fee amounts to display
    // const currentShippingAmount = useLiveTotals 
    //     ? shippingAmount 
    //     : n((order?.order_charges?.[0]?.shipping_option || []).reduce((s: number, r: any) => s + n(r.shipping_amount), 0));

    // const currentSpecialFeeAmount = useLiveTotals 
    //     ? specialFeeAmount 
    //     : n((order?.order_charges?.[0]?.specialfee || []).reduce((s: number, f: any) => s + n(f.specialfee_amount), 0));

    // // ✅ Calculate eligibility total for coupon validation (products + charges + taxes)
    // const eligibilityTotal = r2(subTotalToShow + currentShippingAmount + currentSpecialFeeAmount + uiTotalTax);

    // // ✅ Grand total calculation
    // const grandTotal = r2(subTotalToShow + currentShippingAmount + currentSpecialFeeAmount + uiTotalTax - discountToShow);

    // ✅ Use the same values that the UI shows
    const currentShippingAmount = shippingAmountToShow;
    const currentSpecialFeeAmount = specialFeeAmountToShow;

    // ✅ Calculate eligibility total for coupon validation (products + charges + taxes)
    const eligibilityTotal = r2(
        subTotalToShow + currentShippingAmount + currentSpecialFeeAmount + uiTotalTax
    );

    // // ✅ Grand total calculation (what you show as Total (USD))
    // const grandTotal = r2(
    //     subTotalToShow + currentShippingAmount + currentSpecialFeeAmount + uiTotalTax - discountToShow
    // );
    // Total (USD) = subtotal − discount + shipping + special fee + tax
    const grandTotal = React.useMemo(() => {
        return r2(
            subTotalToShow + currentShippingAmount + currentSpecialFeeAmount + uiTotalTax - discountToShow
        );
    }, [
        subTotalToShow,
        currentShippingAmount,
        currentSpecialFeeAmount,
        uiTotalTax,
        discountToShow,
        resolvedTotalsTick,
    ]);
    

    const shippingRowsForTotalsLabel = React.useMemo(() => {
        const fromPayload = shipPayload?.shipping_option;
        if (Array.isArray(fromPayload) && fromPayload.length > 0) return fromPayload;
        const raw = order?.order_charges?.[0]?.shipping_option ?? [];
        return raw.map((r: any) => ({
            shipping__id: String(r.shipping_id ?? r.shipping__id ?? ""),
            shipping_id: String(r.shipping_id ?? r.shipping__id ?? ""),
            shipping_name: String(r.shipping_name ?? ""),
            shipping_type: String(r.shipping_type ?? ""),
            is_customer_pickup: r.is_customer_pickup === true,
            shipping_amount: Number(r.shipping_amount ?? 0),
        }));
    }, [shipPayload?.shipping_option, order?.order_charges]);

    const isUpsShippingForTotalsLabel = React.useMemo(() => {
        const rows = shipPayload?.shipping_option;
        if (Array.isArray(rows) && rows.length > 0) return !!shipPayload.isUps;
        const so = order?.order_charges?.[0]?.shipping_option?.[0] as any;
        if (!so) return false;
        const rawId = String(so.shipping_id ?? so.shipping__id ?? "");
        if (
            rawId.length > 0 &&
            /^[0-9A-Z]{2,3}$/.test(rawId) &&
            rawId.length <= 3 &&
            rawId !== "custom-manual"
        ) {
            return true;
        }
        return /^ups$/i.test(String(so.shipping_type ?? "").trim());
    }, [shipPayload?.shipping_option, shipPayload?.isUps, order?.order_charges]);

    const specialFeeRowsForTotalsLabel = React.useMemo(() => {
        const fromPayload = feePayload?.specialfee;
        if (Array.isArray(fromPayload) && fromPayload.length > 0) return fromPayload;
        const raw = order?.order_charges?.[0]?.specialfee ?? [];
        return Array.isArray(raw) ? raw : [];
    }, [feePayload?.specialfee, order?.order_charges]);
    const approvedProofCards = React.useMemo(() => {
        const approvedRoot = (approvedMessageData as any) || {};
        const approvedFiles = Array.isArray(approvedRoot?.approved_files) ? approvedRoot.approved_files : [];
        if (approvedFiles.length === 0) return [];

        const grouped = new Map<number, any[]>();
        approvedFiles.forEach((f: any) => {
            const version = Number(f?.version);
            const key = Number.isFinite(version) && version > 0 ? Math.floor(version) : 1;
            if (!grouped.has(key)) grouped.set(key, []);
            grouped.get(key)!.push(f);
        });

        return Array.from(grouped.entries())
            .sort((a, b) => a[0] - b[0])
            .map(([version, files]) => {
                const first = files[0] || {};
                const compactProofFiles = files.map((f: any, idx: number) => ({
                    id: String(f?.id ?? f?._id ?? idx),
                    name: String(f?.original_name ?? f?.file_name ?? f?.name ?? "File"),
                    href:
                        String(f?.preview_url ?? "").trim() ||
                        String(f?.file_url ?? f?.url ?? "").trim(),
                    previewUrl: String(f?.preview_url ?? f?.file_url ?? f?.url ?? "").trim(),
                    fileId: String(f?.file_id ?? "").trim(),
                    quantity: Number.isFinite(Number(f?.quantity)) ? Number(f?.quantity) : undefined,
                    sizeLabel: Number.isFinite(Number(f?.file_size)) ? `${Math.round(Number(f?.file_size) / 1024)} KB` : "—",
                }));
                const approvedByName = String(
                    first?.design_approved_by?.name ??
                    first?.updated_by?.name ??
                    approvedRoot?.design_approved_by?.name ??
                    approvedRoot?.approved_by_name ??
                    ""
                ).trim();
                const approvedAtRaw =
                    first?.design_approved_at ??
                    first?.updated_at ??
                    approvedRoot?.design_approved_at ??
                    approvedRoot?.approved_at;
                const approvedAtLabel = (() => {
                    if (!approvedAtRaw) return "";
                    const dt = new Date(approvedAtRaw);
                    return Number.isNaN(dt.getTime()) ? "" : dt.toLocaleString();
                })();
                const approvedQty = Number(first?.quantity ?? approvedRoot?.quantity ?? 1) || 1;
                const shippingName = String(first?.shipping_method?.name ?? approvedRoot?.shipping_method?.name ?? "").trim();
                const shippingDesc = String(first?.shipping_method?.description ?? approvedRoot?.shipping_method?.description ?? "").trim();
                const shippingAmountRaw = first?.shipping_amount ?? approvedRoot?.shipping_amount;
                const shippingAmountLabel = Number.isFinite(Number(shippingAmountRaw))
                    ? `$${Number(shippingAmountRaw).toFixed(2)}`
                    : "";
                const assignedToName = String(approvedRoot?.assigned_to_name ?? "").trim();
                const orderSummary = resolveApprovedProofVersionSummary(approvedRoot, version, first);
                return {
                    version,
                    files: compactProofFiles,
                    approvedByName,
                    approvedAtLabel,
                    assignedToName,
                    approvedQty,
                    shippingName,
                    shippingDesc,
                    shippingAmountLabel,
                    orderSummary,
                };
            });
    }, [approvedMessageData]);

    // useEffect(() => {
    //     (async () => {
    //       if (!token || !order) return;

    //       const orderdata = (await GetVersionOrdernumber(
    //         token,
    //         order.order_number,
    //         order?.order_type || "sales"
    //       )) || null;
    //       const orderId = (orderdata as any)?.full_number || (orderdata as any)?.order_number || "";
    //     //   setOrderNumber(orderId);
    //     })();
    //   }, [token, order]);


    return (
        <>


            <div className="content container-fluid container-1440">
                <div className="">
                    <h3 className="m-0 fw-bold addheaders mb-3">Edit Order</h3>
                </div>
                <div className="row ">
                    <div className="col-lg-8" >
                        <div className=" p-1">
                            <div className="">
                                <form
                                    onSubmit={(e: React.FormEvent<HTMLFormElement>) => {
                                        const submitter = (e.nativeEvent as SubmitEvent).submitter as HTMLElement | null;
                                        const isSaveButton = !!submitter && submitter.getAttribute?.("data-save-button") === "true";
                                        if (!isSaveButton) {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            return;
                                        }
                                        formik.handleSubmit(e);
                                    }}
                                    onKeyDown={(e: React.KeyboardEvent<HTMLFormElement>) => {
                                        if (e.key !== "Enter") return;
                                        const target = e.target as HTMLElement | null;
                                        if (!target) return;
                                        // Allow Enter in textareas, but prevent Enter from submitting the form
                                        if (target.tagName.toUpperCase() !== "TEXTAREA") {
                                            e.preventDefault();
                                        }
                                    }}
                                >
                                    <div className="">
                                        <div className="box-shadow shadow-border-3  px-3 mb-5">
                                            <div className="">
                                                <div className="row ms-2">
                                                    <div id="row-overlap">
                                                        <h6 className="mb-0 d-flex align-items-center order-tittle ms-0 mt-3 mb-3">
                                                            <span className="icon-chip chip-blue me-2">
                                                                <i className="bi bi-card-checklist" />
                                                            </span>
                                                            <span>Order Details</span>
                                                        </h6>
                                                    </div>
                                                    {formik.values.userType === "brokerageFirm" ? (
                                                        <>
                                                            {/* ✅ NEW FLOW: Brokerage → Office → Team Member → Agent */}

                                                            {/* 1. Brokerage Firm */}
                                                            <div className="col-md-6 mb-3" id="row-overlap">
                                                                <UIReactSelectPro
                                                                    formik={formik}
                                                                    name="brokerageFirmIddd"
                                                                    label=" Selected Brokerage Firm"
                                                                    required
                                                                    options={brokerageOptions as any}
                                                                    value={
                                                                        brokerageOptions?.find(
                                                                            (option) =>
                                                                                option.value ===
                                                                                formik.values.brokerageFirmIddd
                                                                        ) || null
                                                                    }
                                                                    onChange={(selected) => {
                                                                        formik.setFieldValue(
                                                                            "brokerageFirmIddd",
                                                                            selected?.value
                                                                        );
                                                                        fetchBrokerageOffices(selected);
                                                                    }}
                                                                    isDisabled={true}
                                                                />
                                                            </div>

                                                            {/* 2. Office (NEW FLOW - comes after brokerage) */}
                                                            <div className="col-md-6 mb-3" id="row-overlap">
                                                                <UIReactSelectPro
                                                                    formik={formik}
                                                                    name="officeIddd"
                                                                    label="Selected Office"
                                                                    required
                                                                    options={officeOptions}
                                                                    value={
                                                                        officeOptions?.find(
                                                                            (option) =>
                                                                                option.value === formik.values.officeIddd
                                                                        ) || null
                                                                    }
                                                                    onChange={(selected) => {
                                                                        formik.setFieldValue(
                                                                            "officeIddd",
                                                                            selected?.value
                                                                        );
                                                                        fetchOfficeAgents(selected);

                                                                        formik.setFieldValue(
                                                                            "userFirstName",
                                                                            selected?.firstName || ""
                                                                        );
                                                                        formik.setFieldValue(
                                                                            "userLastName",
                                                                            selected?.lastName || ""
                                                                        );
                                                                        formik.setFieldValue(
                                                                            "userEmail",
                                                                            selected?.email || ""
                                                                        );
                                                                        formik.setFieldValue(
                                                                            "userPhone",
                                                                            selected?.phone || ""
                                                                        );
                                                                    }}
                                                                    isDisabled={true}
                                                                />
                                                            </div>

                                                            {/* 3. Team Member (NEW FLOW - comes after office) */}
                                                            <div className="col-md-6 mb-3" id="row-overlap">
                                                                <label className="form-label">Selected Team Member</label>
                                                                <AsyncPaginate
                                                                    key={order?.brokerage_firm_id || "no-brokerage"}
                                                                    classNamePrefix="rs"
                                                                    placeholder={order?.brokerage_firm_id ? "Select team member…" : "No brokerage firm"}
                                                                    isDisabled={true}
                                                                    value={selectedTeamMemberOption ? {
                                                                        value: selectedTeamMemberOption.value,
                                                                        label: selectedTeamMemberOption.label,
                                                                        firstName: selectedTeamMemberOption.firstName,
                                                                        lastName: selectedTeamMemberOption.lastName,
                                                                        email: selectedTeamMemberOption.email,
                                                                        phone: selectedTeamMemberOption.phone,
                                                                        offices: selectedTeamMemberOption.offices
                                                                    } : null}
                                                                    loadOptions={loadTeamMemberOptions}
                                                                    onChange={(selected) => {
                                                                        if (selected) {
                                                                            setSelectedTeamMemberOption(selected);
                                                                            formik.setFieldValue("teamMemberId", selected.value);
                                                                            formik.setFieldValue("teamMemberName", selected.label);
                                                                        } else {
                                                                            setSelectedTeamMemberOption(null);
                                                                            formik.setFieldValue("teamMemberId", "");
                                                                            formik.setFieldValue("teamMemberName", "");
                                                                        }
                                                                    }}
                                                                    onBlur={() => formik.setFieldTouched("teamMemberId", true)}
                                                                    additional={{ page: 1 }}
                                                                    defaultOptions={!!order?.brokerage_firm_id}
                                                                    debounceTimeout={300}
                                                                    isClearable
                                                                    components={{ DropdownIndicator }}
                                                                    styles={customSelectStyles}
                                                                />
                                                            </div>

                                                            {/* 4. Agent (NEW FLOW - comes after team member) */}
                                                            {(officeAgentOptions && formik.values.officeAgentId) && <div className="col-md-6 mb-3" id="row-overlap">
                                                                <UIReactSelectPro
                                                                    formik={formik}
                                                                    name="officeAgentId"
                                                                    label=" Selected Agent"
                                                                    required
                                                                    options={officeAgentOptions}
                                                                    value={officeAgentOptions?.find((option) => option.value === formik.values.officeAgentId) || null
                                                                    }
                                                                    onChange={(selected) => {
                                                                        formik.setFieldValue(
                                                                            "officeAgentId",
                                                                            selected?.value
                                                                        );
                                                                        setSelectedOfficeAgent(selected);
                                                                        formik.setFieldValue(
                                                                            "userFirstName",
                                                                            selected?.firstName || ""
                                                                        );
                                                                        formik.setFieldValue(
                                                                            "userLastName",
                                                                            selected?.lastName || ""
                                                                        );
                                                                        formik.setFieldValue(
                                                                            "userEmail",
                                                                            selected?.email || ""
                                                                        );
                                                                        formik.setFieldValue(
                                                                            "userPhone",
                                                                            selected?.phone || ""
                                                                        );
                                                                    }}
                                                                    isDisabled={true}
                                                                />
                                                            </div>
                                                            }
                                                        </>
                                                    ) : (
                                                        formik.values.userType === "agent" && (
                                                            <div className="col-md-6 mb-3" id="row-overlap">
                                                                <label className="form-label">
                                                                    Selected Agent <span className="text-danger">*</span>
                                                                </label>
                                                                <input
                                                                    type="text"
                                                                    className="form-control"
                                                                    value={formik.values.agentName}
                                                                    disabled
                                                                    readOnly
                                                                />
                                                            </div>
                                                        )
                                                    )}
                                                    <hr />

                                                    {/* First Name */}
                                                    <div className="col-md-6 mb-3" id="row-overlap">
                                                        <UIInputPro
                                                            formik={formik}
                                                            name="userFirstName"
                                                            label={<>First Name</>}
                                                            required
                                                            placeholder="Enter first name"
                                                            value={formik.values.userFirstName}
                                                            onChange={formik.handleChange}
                                                            onBlur={formik.handleBlur}
                                                            readOnly
                                                        />

                                                        {/* <label htmlFor="userFirstName" className="form-label">
                                                                First Name
                                                            </label>
                                                            <input
                                                                type="text"
                                                                className="form-control"
                                                                id="userFirstName"
                                                                name="userFirstName"
                                                                placeholder="Enter first name"
                                                                value={formik.values.userFirstName}
                                                                onChange={formik.handleChange}
                                                                onBlur={formik.handleBlur}
                                                                readOnly={
                                                                    !isFieldEditable(
                                                                        order?.order_status ?? 0,
                                                                        "BasicDetails"
                                                                    )
                                                                }
                                                            /> */}
                                                    </div>

                                                    {/* Last Name */}
                                                    <div className="col-md-6 mb-3" id="row-overlap">
                                                        <UIInputPro
                                                            formik={formik}
                                                            name="userLastName"
                                                            label={<>Last Name</>}
                                                            required
                                                            placeholder="Enter last name"
                                                            value={formik.values.userLastName}
                                                            onChange={formik.handleChange}
                                                            onBlur={formik.handleBlur}
                                                            readOnly
                                                        />
                                                        {/* <label htmlFor="userLastName" className="form-label">
                                                                Last Name
                                                            </label>
                                                            <input
                                                                type="text"
                                                                className="form-control"
                                                                id="userLastName"
                                                                name="userLastName"
                                                                placeholder="Enter last name"
                                                                value={formik.values.userLastName}
                                                                onChange={formik.handleChange}
                                                                onBlur={formik.handleBlur}
                                                                readOnly={
                                                                    !isFieldEditable(
                                                                        order?.order_status ?? 0,
                                                                        "BasicDetails"
                                                                    )
                                                                }
                                                            /> */}
                                                    </div>

                                                    {/* Email */}
                                                    <div className="col-md-6 mb-3" id="row-overlap">
                                                        <UIInputPro
                                                            formik={formik}
                                                            name="userEmail"
                                                            label={<>Email</>}
                                                            required
                                                            placeholder="Enter email"
                                                            value={formik.values.userEmail}
                                                            onChange={formik.handleChange}
                                                            onBlur={formik.handleBlur}
                                                            readOnly={
                                                                !isFieldEditable(
                                                                    order?.status ?? 0,
                                                                    "BasicDetails"
                                                                )
                                                            }
                                                        />

                                                        {/* <label htmlFor="userEmail" className="form-label">
                                                                Email
                                                            </label>
                                                            <input
                                                                type="email"
                                                                className="form-control"
                                                                id="userEmail"
                                                                name="userEmail"
                                                                placeholder="Enter email"
                                                                value={formik.values.userEmail}
                                                                onChange={formik.handleChange}
                                                                onBlur={formik.handleBlur}
                                                                readOnly={
                                                                    !isFieldEditable(
                                                                        order?.order_status ?? 0,
                                                                        "BasicDetails"
                                                                    )
                                                                }
                                                            /> */}
                                                    </div>

                                                    {/* Phone */}
                                                    <div className="col-md-6 mb-3" id="row-overlap">
                                                        <UIInputPro
                                                            formik={formik}
                                                            name="userPhone"
                                                            label={<>Phone Number</>}
                                                            required
                                                            placeholder="Enter phone"
                                                            value={formik.values.userPhone}
                                                            onChange={formik.handleChange}
                                                            onBlur={formik.handleBlur}
                                                            readOnly={
                                                                !isFieldEditable(
                                                                    order?.status ?? 0,
                                                                    "BasicDetails"
                                                                )
                                                            }
                                                        />
                                                        {/* <label htmlFor="userPhone" className="form-label">
                                                                Phone
                                                            </label>
                                                            <input
                                                                type="text"
                                                                className="form-control"
                                                                id="userPhone"
                                                                name="userPhone"
                                                                placeholder="Enter phone"
                                                                value={formik.values.userPhone}
                                                                onChange={formik.handleChange}
                                                                onBlur={formik.handleBlur}
                                                                readOnly={
                                                                    !isFieldEditable(
                                                                        order?.order_status ?? 0,
                                                                        "BasicDetails"
                                                                    )
                                                                }
                                                            /> */}
                                                    </div>
                                                </div>
                                            </div>
                                            <hr className="mt-3" />
                                            <div className="row mb-2 mt-2">
                                                <div className="my-2" id="row-overlap">
                                                    <h5 className="ms-3 table-headers">Billing Information</h5>
                                                    <div className="d-flex justify-content-between align-items-center">
                                                        <div className="form-check mb-0 ms-3">
                                                            <input
                                                                type="checkbox"
                                                                id="isSameAsBilling"
                                                                name="isSameAsBilling"
                                                                className="form-check-input me-2"
                                                                onChange={formik.handleChange}
                                                                onBlur={formik.handleBlur}
                                                                checked={formik.values.isSameAsBilling}
                                                            />
                                                            <label className="form-check-label" htmlFor="isSameAsBilling">
                                                                Is Billing and Shipping address the same
                                                            </label>
                                                        </div>

                                                        {/* <button
                                                                type="button"
                                                                className="btn btn-info"
                                                                onClick={() => setShowAddressForm(true)}
                                                                disabled={!!selectedBillingAddress || !!selectedOfficeBillingAddress}
                                                            >
                                                                Add New Address
                                                            </button> */}
                                                    </div>
                                                </div>

                                                <div className="col-md-6" id="row-overlap">
                                                    {/* <h5 className="ms-3 table-headers">Address Details </h5> */}
                                                    <div className="my-2 ms-3">
                                                        <div className="">
                                                            {/* <h5 className="mb-4 table-headers">Billing Information</h5> */}
                                                            <UIReactSelectPro
                                                                formik={formik}
                                                                name="billingAddressId"
                                                                label={
                                                                    <>
                                                                        Select Billing Address <span className="text-danger">*</span>
                                                                    </>
                                                                }
                                                                options={addressOptions}
                                                                value={
                                                                    // selectedBillingAddressOption
                                                                    addressOptions?.find(
                                                                        (option: any) => option.value === formik.values.billingAddressId
                                                                    ) || null
                                                                }
                                                                onChange={(selected) => {
                                                                    formik.setFieldValue("billingAddressId", selected?.value || "");
                                                                    const isOffice = !selectedOfficeAgent && !selectedAgent && selectedOffice;
                                                                    const selectedAddress =
                                                                        addressObjects?.find((addr) => addr._id === selected?.value) ??
                                                                        (selected && typeof selected === "object" && selected._id
                                                                            ? selected
                                                                            : null);
                                                                    if (isOffice) {
                                                                        setSelectedOfficeBillingAddress(selectedAddress || null);
                                                                    } else {
                                                                        setSelectedBillingAddress(selectedAddress || null);
                                                                    }
                                                                }}
                                                                isDisabled={!!order?.payment_info?.length}

                                                            />
                                                        </div>

                                                    </div>
                                                </div>
                                                {!formik.values.isSameAsBilling && (
                                                    <div className="col-md-6 mt-3 mb-2" id="row-overlap">
                                                        <h5 className="mt-2 table-headers"></h5>
                                                        <UIReactSelectPro
                                                            formik={formik}
                                                            name="shippingAddressId"
                                                            label={
                                                                <>
                                                                    Select Shipping Address <span className="text-danger">*</span>
                                                                </>
                                                            }
                                                            options={addressOptions}
                                                            value={addressOptions?.find(
                                                                (option: any) => option.value === formik.values.shippingAddressId
                                                            )}
                                                            onChange={(selected: any) => {
                                                                formik.setFieldValue("shippingAddressId", selected?.value);
                                                                const isOffice = !selectedOfficeAgent && !selectedAgent && selectedOffice;
                                                                const selectedAddress = addressObjects?.find((addr) => addr._id === selected?.value);
                                                                if (isOffice) {
                                                                    setSelectedOfficeShippingAddress(selectedAddress || null);
                                                                } else {
                                                                    setSelectedShippingAddress(selectedAddress || null);
                                                                }
                                                            }}

                                                        // isDisabled={!!order?.payment_info?.length}
                                                        />
                                                        {/* <label htmlFor="shippingAddressId" className="form-label">
                                                                Select Shipping Address
                                                            </label>
                                                            <Select
                                                                id="shippingAddressId"
                                                                name="shippingAddressId"
                                                                options={addressOptions}
                                                                value={addressOptions?.find(
                                                                    (option) => option.value === formik.values.shippingAddressId
                                                                )}
                                                                onChange={(selected: any) => {
                                                                    formik.setFieldValue("shippingAddressId", selected?.value);
                                                                    const isOffice = !selectedOfficeAgent && !selectedAgent && selectedOffice;
                                                                    const selectedAddress = addressObjects?.find((addr) => addr._id === selected?.value);
                                                                    if (isOffice) {
                                                                        setSelectedOfficeShippingAddress(selectedAddress || null);
                                                                    } else {
                                                                        safeConsole.log("setSelectedShippingAddress :", selectedAddress)
                                                                        setSelectedShippingAddress(selectedAddress || null);
                                                                    }
                                                                }}

                                                                unstyled
                                                                classNames={{
                                                                    control: () => 'my-select-control',
                                                                    menu: () => 'my-select-menu',
                                                                    option: (state) =>
                                                                        `my-select-option ${state.isSelected ? 'is-selected' : ''} ${state.isFocused ? 'my-option-focused' : ''}`,
                                                                    container: () => 'my-select-container',
                                                                }}
                                                                styles={{
                                                                    menu: (base) => ({
                                                                        ...base,
                                                                        maxHeight: '200px',
                                                                        overflowY: 'auto',
                                                                        zIndex: 5,
                                                                    }),
                                                                }}
                                                                placeholder="- Select -"
                                                            /> */}
                                                    </div>
                                                )}
                                            </div>
                                            <hr className="mt-3" />
                                            <>
                                            <div className="card-header bg-white rounded-top border-0">
                                                        {/* <div className="d-flex flex-column flex-md-row align-items-start align-items-md-center justify-content-between gap-2"> */}
                                                        <div className="align-items-md-center align-items-start d-lg-flex flex-column flex-md-row gap-2 justify-content-between">
                                                        <h6 className="align-items-center d-flex mb-0 mb-3 mb-lg-0 ms-1 order-tittle">
                                                                <span
                                                                    aria-hidden="true"
                                                                    className="image-icons"
                                                                    style={{
                                                                        backgroundColor: "#E17100",
                                                                        WebkitMaskImage: `url(${ProductSelection})`,
                                                                        maskImage: `url(${ProductSelection})`,
                                                                    }}
                                                                />
                                                                <span className="ms-2">Product Details</span>
                                                            </h6>
                                                            {/* <div className="d-flex flex-column flex-md-row align-items-start align-items-md-center justify-content-between gap-2"> */}
                                                                <button
                                                                    type="button"
                                                                     className="btn btn-sm btn-primary table-addbutton br-8 ms-md-auto me-2"
                                                                    onClick={() => setPickerOpen(true)}
                                                                >
                                                                    Add Product
                                                                    {/* <i className="ri-add-line text-success fs-18"></i> */}
                                                                </button>

                                                                {customProductSettingEnabled && (
                                                                    <button
                                                                        type="button"
                                                                        className="btn btn-sm btn-primary table-addbutton br-8"
                                                                        onClick={() => setShowCustomModal(true)}
                                                                    >
                                                                        Custom Item
                                                                    </button>
                                                                )}
                                                            {/* </div> */}
                                                        </div>
                                                    </div>
                                            <div
                                                className="table-responsive mt-3 signage-edit-order-products"
                                                style={{
                                                    maxHeight: "300px",
                                                    overflowY: "auto",
                                                    overflowX: "auto",
                                                    border: "1px solid #dee2e6",
                                                    borderRadius: "0.25rem",
                                                    WebkitOverflowScrolling: "touch"
                                                }}
                                            >
                                                <div style={{ maxHeight: "300px", overflowY: "auto", overflowX: "auto" }}>
                                                    <CustomItemModal
                                                        show={showCustomModal}
                                                        onClose={() => setShowCustomModal(false)}
                                                        onSubmit={async (data: any) => {
                                                            const t = token || "";
                                                            if (!t) {
                                                                showError("Session expired. Please log in again.");
                                                                return;
                                                            }
                                                            try {
                                                                const productId = await fetchNewUniqueId(t);
                                                                const variantId = await fetchNewUniqueId(t);
                                                                const qty = parseInt(String(data.quantity), 10) || 0;
                                                                const price = parseFloat(String(data.price)) || 0;
                                                                setCustomproducts((prev) => {
                                                                    const lineId = newOrderLineObjectId(collectOrderLineObjectIds(selectedProducts, prev));
                                                                    const next = [
                                                                        ...prev,
                                                                        {
                                                                            _id: lineId,
                                                                            line_item_id: lineId,
                                                                            product_id: productId,
                                                                            variant_id: variantId,
                                                                            item_name: String(data.item_name || "").trim() || "Custom Item",
                                                                            quantity: qty,
                                                                            Price: price,
                                                                            is_taxable:
                                                                                typeof data?.taxable === "boolean"
                                                                                    ? data.taxable
                                                                                    : undefined,
                                                                            item_tax_info: {
                                                                                ...(typeof data?.taxable === "boolean"
                                                                                    ? { taxable: data.taxable }
                                                                                    : {}),
                                                                            },
                                                                            custom_information: String(data.custom_info || "").trim(),
                                                                            length: parseFloat(String(data.length)) || 0,
                                                                            width: parseFloat(String(data.width)) || 0,
                                                                            height: parseFloat(String(data.height)) || 0,
                                                                            weight: parseFloat(String(data.weight)) || 0,
                                                                        },
                                                                    ];
                                                                    const idx = next.length - 1;
                                                                    queueMicrotask(() => {
                                                                        const errorKey = purchasedProductErrorKey("custom", String(idx));
                                                                        patchPurchasedProductFieldError(
                                                                            errorKey,
                                                                            "quantity",
                                                                            validatePurchasedQuantityValue(qty)
                                                                        );
                                                                        patchPurchasedProductFieldError(
                                                                            errorKey,
                                                                            "unitPrice",
                                                                            validatePurchasedUnitPriceValue(price)
                                                                        );
                                                                    });
                                                                    return next;
                                                                });
                                                                setShowCustomModal(false);
                                                                markUserEdited();
                                                            } catch (err) {
                                                                showError(err instanceof Error ? err.message : "Failed to add custom item");
                                                                throw err;
                                                            }
                                                        }}
                                                    />
                                                    <table className="table mb-0 align-middle signage-edit-order-products-table">
                                                        <colgroup>
                                                            <col className="seo-col-product" />
                                                            <col className="seo-col-sku" />
                                                            <col className="seo-col-qty" />
                                                            {/* <col className="seo-col-unit-price" /> */}
                                                            <col className="seo-col-changed-price" />
                                                            <col className="seo-col-discount" />
                                                            {/* <col className="seo-col-tax" /> */}
                                                            <col className="seo-col-total" />
                                                            <col className="seo-col-actions" />
                                                        </colgroup>
                                                        <thead className="table-light" style={{ position: "sticky", top: 0, zIndex: 1 }}>
                                                            <tr>
                                                                <th className="text-center table-headers seo-th-product">Product</th>
                                                                <th className="text-center table-headers seo-th-sku">SKU</th>
                                                                <th className="text-center table-headers seo-th-qty">Qty</th>
                                                                <th className="text-center table-headers seo-th-unit-price" title="Unit Price">
                                                                    Unit Price
                                                                </th>
                                                                {/* <th className="text-center table-headers seo-th-changed-price" title="Changed Price">
                                                                    Changed<br />Price
                                                                </th> */}
                                                                <th className="text-center table-headers seo-th-discount">Discount</th>
                                                                {/* <th className="text-center table-headers seo-th-tax">Tax</th> */}
                                                                <th className="text-center table-headers seo-th-total">Total</th>
                                                                <th className="text-center table-headers seo-th-actions"></th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {(selectedProducts && selectedProducts)?.map((product: any, index: number) => {
                                                                const isLast = index === selectedProducts.length - 1;
                                                                const firstId = idOf(product?.images?.[0]);
                                                                const designId = String(
                                                                    product?.design_id || product?.studio_design_id || ""
                                                                ).trim();
                                                                const previewId = String(
                                                                    product?.preview_url || product?.studio_preview_id || ""
                                                                ).trim();
                                                                const designThumb =
                                                                    designId && previewId
                                                                        ? getImageSrc(previewId, imageUrlMap) || buildImageUrl(previewId)
                                                                        : "";
                                                                const src =
                                                                    designThumb ||
                                                                    getImageSrc(firstId, imageUrlMap) ||
                                                                    getImageSrc(product?.image_url, imageUrlMap) ||
                                                                    buildImageUrl(firstId || product?.image_url);

                                                                const editCatalogProductId = String(
                                                                    product?.productId ?? product?.product_id ?? ""
                                                                ).trim();
                                                                const placeholderProductId = "000000000000000000000000";
                                                                const hasEditableCatalogProductId =
                                                                    !!editCatalogProductId &&
                                                                    editCatalogProductId !== placeholderProductId;
                                                                const poForCatalogEdit = (ProductObjs || []).find(
                                                                    (po: any) => String(po?.product_id) === editCatalogProductId
                                                                );
                                                                const rowProductTypeForEdit = String(
                                                                    product?.product_type ??
                                                                        poForCatalogEdit?.product?.product_type ??
                                                                        ""
                                                                ).toLowerCase();
                                                                const catalogProductEditHref = hasEditableCatalogProductId
                                                                    ? rowProductTypeForEdit === "variable"
                                                                        ? `${baseRoute}/editVariableProduct/${editCatalogProductId}`
                                                                        : `${baseRoute}/editProduct/${editCatalogProductId}`
                                                                    : null;

                                                                // ids & qty
                                                                const key = keyForRow(product);
                                                                const purchasedErrorKey = purchasedProductErrorKey("catalog", key);
                                                                const purchasedRowErrors = purchasedProductFieldErrors[purchasedErrorKey] || {};
                                                                const k = key;
                                                                const qtyNum = Math.max(1, num(product.quantity));

                                                                const catalogBaseUnitPrice = getBaseUnitPrice(product);
                                                                const changedUnitPrice = getUnitPriceToUse(product);
                                                                const unitPriceDisplay =
                                                                    manualUnitPriceMap[key] !== undefined
                                                                        ? Number(manualUnitPriceMap[key] || 0)
                                                                        : catalogBaseUnitPrice;

                                                                const savedRowTax = savedRowTaxMap?.[k] || 0;
                                                                const rowTaxNow = useLiveTotals
                                                                    ? Number(productTaxMap[k] ?? 0)
                                                                    : savedRowTax;

                                                                const { rowDiscount, lineTotal } = getSignageOrderLinePricing({
                                                                    actualUnitPrice: catalogBaseUnitPrice,
                                                                    changedUnitPrice,
                                                                    qty: qtyNum,
                                                                    tax: rowTaxNow,
                                                                    isManualUnitPrice: manualUnitPriceMap[key] !== undefined,
                                                                });

                                                                return (
                                                                    <tr key={product._id || product.line_item_id || `${product.productId}:${product.variantId}:${index}`}>
                                                                        <td className="table-cells align-middle" style={{ whiteSpace: "normal", wordWrap: "break-word", minWidth: "200px" }}>
                                                                            <div className="d-flex gap-3 align-items-start">
                                                                                {/* Product Image */}
                                                                                <div style={{ width: "80px", height: "95px", flexShrink: 0 }}>
                                                                                    <img
                                                                                        src={src || imagecustomRounded}
                                                                                        alt={product?.ProductName || product?.name || "Product"}
                                                                                        className="img-fluid rounded"
                                                                                        style={{ objectFit: "contain", width: "100%", height: "100%" }}
                                                                                        onError={(e) => {
                                                                                            (e.currentTarget as HTMLImageElement).src = imagecustomRounded;
                                                                                        }}
                                                                                    />
                                                                                </div>

                                                                                {/* Product Info */}
                                                                                <div style={{ minWidth: 0, flex: 1 }}>
                                                                                    <h5 className="fs-15 mb-1 wrap-anywhere">
                                                                                        {catalogProductEditHref ? (
                                                                                            <a
                                                                                                href={catalogProductEditHref}
                                                                                                className="link-primary wrap-anywhere"
                                                                                                target="_blank"
                                                                                                rel="noopener noreferrer"
                                                                                            >
                                                                                                {product.name || product.product_name || product.variant_name || product?.ProductName || "-"}
                                                                                            </a>
                                                                                        ) : (
                                                                                            <span className="wrap-anywhere">
                                                                                                {product.name || product.product_name || product.variant_name || product?.ProductName || "-"}
                                                                                            </span>
                                                                                        )}
                                                                                    </h5>
                                                                                    {(() => {
                                                                                        const fields = product?.customizations?.fields || [];
                                                                                        if (!fields.length) return null;

                                                                                        const isFile = (f: any) => String(f?.field_type || "").toLowerCase() === "file";
                                                                                        const firstFileVal = (raw: any): string => {
                                                                                            if (!raw) return "";
                                                                                            if (typeof raw === "string") return raw.trim();
                                                                                            if (raw && typeof raw === "object" && raw.url) return String(raw.url).trim();
                                                                                            return "";
                                                                                        };
                                                                                        const hasValue = (f: any): boolean => {
                                                                                            if (isFile(f)) return !!firstFileVal(f?.value);
                                                                                            return String(f?.value ?? "").trim().length > 0;
                                                                                        };
                                                                                        if (!fields.some(hasValue)) return null;

                                                                                        const textFields = fields.filter((f: any) => !isFile(f) && hasValue(f));
                                                                                        const fileFields = fields.filter((f: any) => isFile(f) && hasValue(f));

                                                                                        return (
                                                                                            <div className="custom-fields-container">
                                                                                                {textFields.map((field: any, idx: number) => (
                                                                                                    <div
                                                                                                        key={`${field?._id || field?.field_name || "cf"}-t-${idx}`}
                                                                                                        className="mb-2"
                                                                                                        style={{ wordBreak: "break-word", maxWidth: "100%" }}
                                                                                                    >
                                                                                                        <strong className="custom-cells">{field.field_name} :</strong>{" "}
                                                                                                        <span className="wrap-anywhere">{String(field?.value ?? "").trim()}</span>
                                                                                                    </div>
                                                                                                ))}
                                                                                                {fileFields.map((field: any, idx: number) => {
                                                                                                    const id = firstFileVal(field?.value);
                                                                                                    const href = getImageSrc(id, imageUrlMap) || buildImageUrl(id);
                                                                                                    return (
                                                                                                        <div
                                                                                                            key={`${field?._id || field?.field_name || "cf"}-file-${idx}`}
                                                                                                            className="mb-2"
                                                                                                            style={{ wordBreak: "break-word", maxWidth: "100%" }}
                                                                                                        >
                                                                                                            <div className="mb-1">{field.field_name}</div>
                                                                                                            <a href={href} download target="_blank" rel="noopener noreferrer">
                                                                                                                <img
                                                                                                                    src={href}
                                                                                                                    alt={field.field_name}
                                                                                                                    className="rounded"
                                                                                                                    style={{ maxWidth: 100, height: "auto", cursor: "pointer" }}
                                                                                                                />
                                                                                                            </a>
                                                                                                        </div>
                                                                                                    );
                                                                                                })}
                                                                                            </div>
                                                                                        );
                                                                                    })()}
                                                                                </div>
                                                                            </div>
                                                                        </td>

                                                                        <td style={{ minWidth: "80px" }}>
                                                                            <div className="fw-semibold text-center wrap-anywhere">
                                                                                {formatSkuDisplay(product?.sku)}
                                                                            </div>
                                                                        </td>
                                                                        {/* <td className="py-2">
                                                                                <input
                                                                                    type="text"
                                                                                    className="form-control text-center"
                                                                                    value={product?.sku || ""}
                                                                                    readOnly
                                                                                />
                                                                            </td> */}

                                                                        {/* Quantity */}
                                                                        <td className="py-2 seo-qty-cell">
                                                                            <input
                                                                                type="number"
                                                                                className={`form-control text-center seo-qty-input ${purchasedRowErrors.quantity ? "is-invalid" : ""}`}
                                                                                min={1}
                                                                                placeholder="1"
                                                                                value={(() => {
                                                                                    const qty = product?.quantity ?? 0;
                                                                                    return qty === 0 ? "" : qty;
                                                                                })()}
                                                                                onChange={(e) => {
                                                                                    handleQuantityChange(index, e);
                                                                                    patchPurchasedProductFieldError(
                                                                                        purchasedErrorKey,
                                                                                        "quantity",
                                                                                        validatePurchasedQuantityValue(e.target.value)
                                                                                    );
                                                                                }}
                                                                                onBlur={(e) => {
                                                                                    patchPurchasedProductFieldError(
                                                                                        purchasedErrorKey,
                                                                                        "quantity",
                                                                                        validatePurchasedQuantityValue(e.target.value)
                                                                                    );
                                                                                }}
                                                                                onKeyDown={(e) => {
                                                                                    if (e.key === "-" || e.key === "e" || e.key === "E" || e.key === "+") {
                                                                                        e.preventDefault();
                                                                                    }
                                                                                }}
                                                                            />
                                                                            {purchasedRowErrors.quantity ? (
                                                                                <div className="text-danger small mt-1">{purchasedRowErrors.quantity}</div>
                                                                            ) : null}
                                                                        </td>

                                                                        {/* Unit Price — catalog base_price */}
                                                                        {/* <td className="py-2 text-center seo-unit-price-cell">
                                                                            <span className="fw-semibold fs-6 seo-unit-price-value">
                                                                                {catalogBaseUnitPrice.toFixed(2)}
                                                                            </span>
                                                                        </td> */}
                                                                        {/* Unit Price — base/original (manual override when user edits); qty changes update effective price for discount/total only */}
                                                                        <td className="py-2 seo-changed-price-cell">
                                                                            <input
                                                                                type="number"
                                                                                className={`form-control text-center seo-price-input ${purchasedRowErrors.unitPrice ? "is-invalid" : ""}`}
                                                                                min={1}
                                                                                step="0.01"
                                                                                placeholder="1"
                                                                                value={(() => {
                                                                                    const price = Number(unitPriceDisplay ?? 0);
                                                                                    return price === 0 ? "" : price;
                                                                                })()}
                                                                                onChange={(e) => {
                                                                                    const raw = e.target.value;
                                                                                    const nextPrice = raw === "" ? 0 : Number(raw);

                                                                                    handleUnitPriceChange(index, {
                                                                                        ...e,
                                                                                        target: { ...e.target, value: String(Number.isFinite(nextPrice) ? nextPrice : 0) },
                                                                                    });
                                                                                    patchPurchasedProductFieldError(
                                                                                        purchasedErrorKey,
                                                                                        "unitPrice",
                                                                                        validatePurchasedUnitPriceValue(raw)
                                                                                    );
                                                                                }}
                                                                                onBlur={(e) => {
                                                                                    patchPurchasedProductFieldError(
                                                                                        purchasedErrorKey,
                                                                                        "unitPrice",
                                                                                        validatePurchasedUnitPriceValue(e.target.value)
                                                                                    );
                                                                                }}
                                                                                onKeyDown={(e) => {
                                                                                    if (e.key === "-" || e.key === "e" || e.key === "E" || e.key === "+") {
                                                                                        e.preventDefault();
                                                                                    }
                                                                                }}
                                                                            />
                                                                            {purchasedRowErrors.unitPrice ? (
                                                                                <div className="text-danger small mt-1">{purchasedRowErrors.unitPrice}</div>
                                                                            ) : null}
                                                                        </td>

                                                                        {/* <td className="text-success text-center 
                                                                        align-middle" style={{ color: "#00a63e" }}>
                                                                                {(() => {
                                                                                    const key = keyFor(product); // row = 
                                                                                    current product row object
                                                                                    const rowDiscount = num
                                                                                    (perRowDiscountMapToShow[key]);
                                                                                    return rowDiscount > 0 ? `-${money
                                                                                    (rowDiscount)}` : "–";
                                                                                })()}
                                                                            </td> */}

                                                                        {/* Discount column */}
                                                                        {/* <td className="text-success text-center align-middle" style={{ color: "#00a63e" }}>
                                                                                {displayedRowDiscount > 0 ? `-${money(displayedRowDiscount)}` : "–"}
                                                                            </td> */}

                                                                        <td className="text-center align-middle">
                                                                            <DiscountAmountDisplay amount={rowDiscount} />
                                                                        </td>



                                                                        {/* Tax column */}
                                                                        {/* <td className="text-center align-middle">
                                                                                {rowTaxAmt > 0 ? (
                                                                                    <>
                                                                                        ${rowTaxAmt.toFixed(2)}
                                                                                        <br />
                                                                                    </>
                                                                                ) : (
                                                                                    "–"
                                                                                )}
                                                                            </td> */}
                                                                        {/* <td className="text-center align-middle" style={{ minWidth: "70px" }}>
                                                                            {rowTaxNow > 0 ? `$${rowTaxNow.toFixed(2)}` : "-"}
                                                                        </td> */}

                                                                        {/* Total = changed unit price × qty + tax */}
                                                                        <td className="py-2 text-center align-middle seo-total-cell">
                                                                            <div className="fw-semibold fs-6 seo-total-value">${lineTotal.toFixed(2)}</div>
                                                                        </td>

                                                                        {/* Total (Auto-calculated) */}
                                                                        {/* <td className="py-2">
                                                                                <input
                                                                                    type="number"
                                                                                    className="form-control text-center"
                                                                                    value={product.total || 0}
                                                                                    readOnly
                                                                                    style={{ backgroundColor: '#f8f9fa' }}
                                                                                />
                                                                            </td> */}
                                                                        <td className="text-center" style={{ minWidth: "50px" }}>
                                                                            <div className="d-flex align-items-center justify-content-center gap-1">
                                                                                {/* {(selectedProducts.length === 0 || index === selectedProducts.length - 1) && (
                                                                                    <button
                                                                                        type="button"
                                                                                        className="border-0 p-0"
                                                                                        onClick={() => setPickerOpen(true)}
                                                                                    >
                                                                                        <i className="ri-add-line text-success fs-18"></i>
                                                                                    </button>
                                                                                )} */}

                                                                                {/* {selectedProducts.length > 1 && ( */}
                                                                                <button type="button" className="border-0 p-0" onClick={() => removeProductRow(index)}>
                                                                                    <i className="ri-close-line text-danger fs-18"></i>
                                                                                </button>
                                                                                {/* )} */}
                                                                            </div>
                                                                        </td>
                                                                        {/* <td className="text-center " style={{ minWidth: "20px" }}>
                                                                                <div className="d-flex align-items-center justify-content-between gap-2">

                                                                                    <div className="d-flex align-items-center gap-1">
                                                                                        {index === selectedProducts.length - 1 && (
                                                                                            <button
                                                                                                type="button"
                                                                                                className="border-0 p-0"
                                                                                                onClick={addProductRow}
                                                                                            >
                                                                                                <i className="ri-add-line text-success fs-18"></i>
                                                                                            </button>
                                                                                        )}
                                                                                        {selectedProducts.length > 1 && (
                                                                                            <button
                                                                                                type="button"
                                                                                                className="border-0 p-0"
                                                                                                onClick={() => removeProductRow(index)}
                                                                                            >
                                                                                                <i className="ri-close-line text-danger fs-18"></i>
                                                                                            </button>
                                                                                        )}
                                                                                    </div>
                                                                                </div>
                                                                            </td> */}

                                                                    </tr>
                                                                );
                                                            })}
                                                            {customproducts.length > 0 && (
                                                                <>
                                                                    <tr className="table-light">
                                                                        <td colSpan={8} className="py-2 px-3 fw-semibold" style={{ minWidth: "200px" }}>
                                                                            <span className="ms-2">Custom Products</span>
                                                                        </td>
                                                                    </tr>
                                                                    {customproducts.map((cp, idx) => {
                                                                        const customErrorKey = purchasedProductErrorKey("custom", String(idx));
                                                                        const customRowErrors = purchasedProductFieldErrors[customErrorKey] || {};
                                                                        const customKey = getCustomProductKey(cp, idx);
                                                                        const rowTax = Number(productTaxMap[customKey] ?? 0);
                                                                        const qtyNum = Number(cp.quantity || 1);
                                                                        const unitPrice = Number(cp.Price || 0);
                                                                        const taxable = cp.is_taxable !== false;
                                                                        const { lineTotal } = getSignageOrderLinePricing({
                                                                            actualUnitPrice: unitPrice,
                                                                            changedUnitPrice: unitPrice,
                                                                            qty: qtyNum,
                                                                            tax: taxable ? rowTax : 0,
                                                                            isManualUnitPrice: true,
                                                                        });
                                                                        return (
                                                                            <tr key={customKey}>
                                                                                <td className="table-cells align-middle" style={{ whiteSpace: "normal", wordWrap: "break-word", minWidth: "200px" }}>
                                                                                    <div className="d-flex gap-3 align-items-start">
                                                                                        <div style={{ width: 80, height: 80, flexShrink: 0 }} className="bg-light rounded overflow-hidden d-flex align-items-center justify-content-center">
                                                                                            <img
                                                                                                src={imagecustomRounded}
                                                                                                alt={cp.item_name || "Custom item"}
                                                                                                className="img-fluid rounded"
                                                                                                style={{ objectFit: "contain", width: "100%", height: "100%" }}
                                                                                                onError={(e) => {
                                                                                                    (e.currentTarget as HTMLImageElement).src = imagecustomRounded;
                                                                                                }}
                                                                                            />
                                                                                        </div>
                                                                                        <div>
                                                                                            <h5 className="fs-15 mb-1 wrap-anywhere">{cp.item_name || "Custom Item"}</h5>
                                                                                            {(() => {
                                                                                                const infoRows = getVisibleCustomInformationRowsForDisplay({
                                                                                                    inventory_source: "custom",
                                                                                                    custom_information:
                                                                                                        cp.custom_information ||
                                                                                                        (cp.custom_attributes as Record<string, unknown> | undefined)?.custom_information ||
                                                                                                        "",
                                                                                                });
                                                                                                if (infoRows.length === 0) return null;
                                                                                                return (
                                                                                                    <div className="custom-fields-container">
                                                                                                        {infoRows.map(({ key, value }, infoIdx) => (
                                                                                                            <div
                                                                                                                key={`${customKey}-ci-${key || infoIdx}`}
                                                                                                                className="mb-2"
                                                                                                                style={{ wordBreak: "break-word", maxWidth: "100%" }}
                                                                                                            >
                                                                                                                <strong className="custom-cells">{key || "Field"} :</strong>{" "}
                                                                                                                <span className="wrap-anywhere">{value}</span>
                                                                                                            </div>
                                                                                                        ))}
                                                                                                    </div>
                                                                                                );
                                                                                            })()}
                                                                                        </div>
                                                                                    </div>
                                                                                </td>
                                                                                <td className="py-2 text-center">{formatSkuDisplay(cp?.sku)}</td>
                                                                                <td className="py-2 seo-qty-cell">
                                                                                    <input
                                                                                        type="number"
                                                                                        className={`form-control text-center seo-qty-input ${customRowErrors.quantity ? "is-invalid" : ""}`}
                                                                                        min={1}
                                                                                        placeholder="1"
                                                                                        value={qtyNum === 0 ? "" : qtyNum}
                                                                                        onChange={(e) => {
                                                                                            const raw = e.target.value;
                                                                                            const qty = raw === "" ? 0 : Number(raw);
                                                                                            updateCustomProduct(idx, { quantity: Number.isFinite(qty) ? qty : 0 });
                                                                                            patchPurchasedProductFieldError(
                                                                                                customErrorKey,
                                                                                                "quantity",
                                                                                                validatePurchasedQuantityValue(raw)
                                                                                            );
                                                                                        }}
                                                                                        onBlur={(e) => {
                                                                                            patchPurchasedProductFieldError(
                                                                                                customErrorKey,
                                                                                                "quantity",
                                                                                                validatePurchasedQuantityValue(e.target.value)
                                                                                            );
                                                                                        }}
                                                                                        onKeyDown={(e) => { if (e.key === "-" || e.key === "e" || e.key === "E") e.preventDefault(); }}
                                                                                    />
                                                                                    {customRowErrors.quantity ? (
                                                                                        <div className="text-danger small mt-1">{customRowErrors.quantity}</div>
                                                                                    ) : null}
                                                                                </td>
                                                                                {/* <td className="py-2 text-center seo-unit-price-cell">
                                                                                    <span className="fw-semibold fs-6 seo-unit-price-value">
                                                                                        {unitPrice.toFixed(2)}
                                                                                    </span>
                                                                                </td> */}
                                                                                <td className="py-2 seo-changed-price-cell">
                                                                                    <input
                                                                                        type="number"
                                                                                        className={`form-control text-center seo-price-input ${customRowErrors.unitPrice ? "is-invalid" : ""}`}
                                                                                        min={1}
                                                                                        step="0.01"
                                                                                        placeholder="1"
                                                                                        value={unitPrice === 0 ? "" : unitPrice}
                                                                                        onChange={(e) => {
                                                                                            const raw = e.target.value;
                                                                                            const price = raw === "" ? 0 : Number(raw);
                                                                                            updateCustomProduct(idx, { Price: Number.isFinite(price) ? price : 0 });
                                                                                            patchPurchasedProductFieldError(
                                                                                                customErrorKey,
                                                                                                "unitPrice",
                                                                                                validatePurchasedUnitPriceValue(raw)
                                                                                            );
                                                                                        }}
                                                                                        onBlur={(e) => {
                                                                                            patchPurchasedProductFieldError(
                                                                                                customErrorKey,
                                                                                                "unitPrice",
                                                                                                validatePurchasedUnitPriceValue(e.target.value)
                                                                                            );
                                                                                        }}
                                                                                        onKeyDown={(e) => { if (e.key === "-" || e.key === "e" || e.key === "E") e.preventDefault(); }}
                                                                                    />
                                                                                    {customRowErrors.unitPrice ? (
                                                                                        <div className="text-danger small mt-1">{customRowErrors.unitPrice}</div>
                                                                                    ) : null}
                                                                                </td>
                                                                                <td className="text-center align-middle">-</td>
                                                                                {/* <td className="text-center align-middle" style={{ minWidth: "70px" }}>
                                                                                    {taxable && rowTax > 0 ? (
                                                                                        <>${rowTax.toFixed(2)}</>
                                                                                    ) : (
                                                                                        "--"
                                                                                    )}
                                                                                </td> */}
                                                                                <td className="py-2 text-center align-middle seo-total-cell">
                                                                                    <div className="fw-semibold fs-6 seo-total-value">${lineTotal.toFixed(2)}</div>
                                                                                </td>
                                                                                <td className="text-center" style={{ minWidth: "50px" }}>
                                                                                    <button type="button" className="border-0 p-0" onClick={() => removeCustomProduct(idx)}>
                                                                                        <i className="ri-close-line text-danger fs-18" />
                                                                                    </button>
                                                                                </td>
                                                                            </tr>
                                                                        );
                                                                    })}
                                                                </>
                                                            )}
                                                        </tbody>
                                                    </table>
                                                </div>
                                            </div>
                                            </>
                                            {String(linkedProjectId || "").trim() && hasApprovedMessage ? (
                                                <div className="row col-lg-12 mb-3 ms-0 pt-4">
                                                    {approvedProofCards.map((card) => (
                                                        <CompactApprovedProofCard
                                                            key={`approved-proof-v-${card.version}`}
                                                            version={card.version}
                                                            files={card.files}
                                                            approvedBy={card.approvedByName}
                                                            approvedAt={card.approvedAtLabel}
                                                            footerBy={card.assignedToName}
                                                            // quantity={card.approvedQty}
                                                            shippingName={card.shippingName}
                                                            shippingDesc={card.shippingDesc}
                                                            shippingAmountLabel={card.shippingAmountLabel}
                                                            showOrderSummary={isSignagePortalRoute(location.pathname, baseRoute)}
                                                            orderSummary={card.orderSummary}
                                                            onDownloadByFileId={(fileId, fileName) => {
                                                                void downloadWorkDriveFile(fileId, fileName);
                                                            }}
                                                        />
                                                    ))}
                                                </div>
                                            ) : null}
                                            <SignageEditOrderInstructionsAndTotals
                                                notes={notes}
                                                onSaveInstructionsOnly={handleSaveInstructionsOnly}
                                                attachments={instructionAttachments}
                                                orderDocumentId={String((order as any)?._id || order?.id || orderId || "")}
                                                onAfterFilesUploaded={fetchOrderData}
                                                token={token!}
                                                supplierId={sellerOrgId}
                                                couponInput={couponInput}
                                                appliedCouponCode={codeToShow}
                                                appliedCouponAmount={discountToShow}
                                                voucherApplied={
                                                    discountToShow > 0 && Boolean(codeToShow.trim())
                                                }
                                                couponResolveDisabled={isRecomputingSales}
                                                couponError={couponResolveError}
                                                onCouponInputChange={setCouponInput}
                                                onCouponApply={(code) => {
                                                    const trimmed = code.trim();
                                                    if (!trimmed) return;
                                                    userClearedCouponRef.current = false;
                                                    setCouponInput(trimmed);
                                                    setLiveDiscountDisplay({ code: "", amount: 0 });
                                                    setPerItemCouponMap({});
                                                    setUserAppliedCoupon(true);
                                                    markUserEdited();
                                                    void recomputeSalesRef.current?.({ force: true, discountCode: trimmed });
                                                }}
                                                onCouponClear={() => {
                                                    setCouponInput("");
                                                    setCouponResolveError("");
                                                    userClearedCouponRef.current = true;
                                                    setUserAppliedCoupon(true);
                                                    setPerItemCouponMap({});
                                                    setLiveDiscountDisplay({ code: "", amount: 0 });
                                                    markUserEdited();
                                                    void recomputeSalesRef.current?.({ force: true, discountCode: "" });
                                                }}
                                                zeroOrNegativeMsg={zeroOrNegativeMsg}
                                                shippingOptionRows={shippingRowsForTotalsLabel}
                                                isUpsShipping={isUpsShippingForTotalsLabel}
                                                specialFeeRows={specialFeeRowsForTotalsLabel}
                                                subTotalToShow={subTotalToShow}
                                                discountToShow={discountToShow}
                                                codeToShow={codeToShow}
                                                isEditingShipping={isEditingShipping}
                                                manualShippingAmount={manualShippingAmount}
                                                onManualShippingAmountChange={setManualShippingAmount}
                                                onSaveShipping={handleSaveShipping}
                                                onCancelEditShipping={handleCancelEditShipping}
                                                onEditShipping={handleEditShipping}
                                                shippingAmountToShow={shippingAmountToShow}
                                                showShippingEditButton={!order?.payment_info?.length}
                                                currentSpecialFeeAmount={currentSpecialFeeAmount}
                                                uiTotalTax={uiTotalTax}
                                                grandTotal={grandTotal}
                                            />

                                            <div className="col-12 mb-2" id="row-overlap">
                                                <hr className="ms-4 me-3" />
                                            </div>
                                            <div className="row mb-3 rcust-margin-4 mb-4">
                                                <div className="align-items-end col-md-12 d-flex justify-content-between">
                                                    <button
                                                        className="btn back-btn surface-btn fw-bold fs-16"
                                                        type="button"
                                                        onClick={() => safeNavigate(`${baseRoute}/orderDetails/${order?.id}`)}
                                                    >
                                                        <i className="bi bi-chevron-left me-2" />
                                                        Back
                                                    </button>

                                                    {!(order?.status === 400) &&

                                                        < div className="">
                                                            <button
                                                                className="btn prim-btnary save-btn surface-btn fw-bold fs-16"
                                                                type="submit"
                                                                data-save-button="true"
                                                                disabled={isAnythingBusy || !canProceedToSave}
                                                                title={
                                                                    !initialLoadComplete
                                                                        ? "Loading order data..."
                                                                        : isAnythingBusy && !formSubmitting
                                                                        ? "Processing..."
                                                                        : !canProceedToSave
                                                                            ? "Please select shipping address"
                                                                            : ""
                                                                }
                                                            >
                                                                {formSubmitting
                                                                    ? "Updating..."
                                                                    : !initialLoadComplete
                                                                        ? "Loading..."
                                                                        : isAnythingBusy
                                                                            ? "Processing..."
                                                                            : "Save"}
                                                            </button>
                                                        </div>
                                                    }
                                                </div>
                                            </div>
                                            <ProductPickerModal
                                                open={pickerOpen}
                                                onClose={closePicker}
                                                supplierId={sellerOrgId}
                                                createdById={effectiveCouponCreatedById || ""}
                                                onAdd={handleAddFromPicker}
                                                existingLineKeys={pickerExistingLineKeys}
                                                studioCustomization={editOrderStudioCustomization}
                                                studioBatchVariantPreviews={
                                                    Object.keys(pickerStudioBatchVariantPreviews).length
                                                        ? pickerStudioBatchVariantPreviews
                                                        : undefined
                                                }
                                            />
                                        </div>
                                    </div>
                                </form>

                                {/* Upload Progress Indicator */}
                                {formSubmitting && (
                                    <div className="mt-3">
                                        <div className="progress">
                                            <div
                                                className="progress-bar progress-bar-striped progress-bar-animated"
                                                role="progressbar"
                                                style={{ width: `${uploadProgress}%` }}
                                                aria-valuenow={uploadProgress}
                                                aria-valuemin={0}
                                                aria-valuemax={100}
                                            >
                                                {uploadProgress}%
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                    <div className="col-lg-4">
                        <PONumber
                            title="PO Number"
                            value={String((order as any)?.po_number ?? (order as any)?.poNumber ?? "")}
                            placeholder="e.g. PO-2026-00124"
                            className="mb-3"
                            orderId={String((order as any)?._id || order?.id || "")}
                            orderType="sales"
                            onSaved={async () => {
                                await fetchOrderData();
                            }}
                        />

                        {order && (() => {
                            // Map order shipping data to ShippingRow format for savedShippingRows
                            const savedRows = (order?.order_charges?.[0]?.shipping_option ?? []).map((r: any) => ({
                                shipping__id: String(r.shipping_id ?? r.shipping__id ?? ""),
                                shipping_name: String(r.shipping_name ?? ""),
                                shipping_type: String(r.shipping_type ?? ""),
                                is_customer_pickup: r.is_customer_pickup === true,
                                shipping_amount: Number(r.shipping_amount ?? 0),
                                cost: Number(r.cost ?? r.shipping_amount ?? 0),
                                shipping_tax_percentage: Number(r.shipping_tax_percentage ?? 0),
                                shipping_tax_amount: Number(r.shipping_tax_amount ?? 0),
                                shipping_taxable: readResolveTaxable(r, ["shipping_taxable", "taxable", "is_taxable"], true),
                                shipping_delivarydate: String(r.shipping_delivarydate ?? r.shipping_delivery_date ?? ""),
                                shipping_delivery_time: String(r.shipping_delivery_time ?? ""),
                            }));

                            // 🔑 If the only saved row is our manual override, don't feed it to ShippingOptions.
                            // That "custom-manual" row is handled only in the Order Totals card.
                            const hasCustomManual = savedRows.some(r => r.shipping__id === "custom-manual");
                            const savedRowsForComponent =
                                !hasCustomManual && savedRows.length > 0 ? (savedRows as any[]) : null;

                            return (
                                <ShippingOptions
                                    ref={shipRef}
                                    token={token!}
                                    createdById={sellerOrgId}
                                    supplierId={sellerOrgId}
                                    taxStatus={order?.order_tax_summary?.tax_status}
                                    shippingAddress={selectedShippingAddress}
                                    totalQty={totalQtyMemoized}
                                    selectedProducts={selectedProductsWithDims}
                                    productsFull={productsFullMemoized}
                                    initialSelectedIds={initialSelectedShipIdsOnce.current || undefined}
                                    savedShippingRows={savedRowsForComponent}
                                    // isDisabled={!!(order?.payment_info?.length)}
                                    isDisabled={false} // Always allow editing shipping, even after payment info is added
                                    onPayloadChange={(p) => {
                                        let newRows = p?.shipping_option ?? [];
                                        const newIds = shippingOptionIdsKey(newRows);
                                        const newSum = sumShippingAmount(newRows);

                                        if (newIds !== lastShipOptionIdsRef.current) {
                                            userEditedShippingAmountRef.current = false;
                                        }

                                        if (userEditedShippingAmountRef.current) {
                                            newRows = applyUserLockedShippingAmounts(
                                                newRows,
                                                shipPayloadRef.current?.shipping_option ?? []
                                            );
                                        }

                                        if (newRows.length === 0) {
                                            if (lastKnownShippingAmountRef.current > 0) return;
                                        }

                                        const j = JSON.stringify(newRows);
                                        if (j !== lastShipJsonRef.current) {
                                            lastShipJsonRef.current = j;
                                            lastKnownShippingAmountRef.current = newSum;
                                            lastShipOptionIdsRef.current = newIds;
                                            const nextShipPayload = {
                                                shipping_option: newRows,
                                                isUps: !!p?.isUps,
                                                isManual: !!p?.isManual,
                                            };
                                            shipPayloadRef.current = nextShipPayload;
                                            setShipPayload(nextShipPayload);
                                        }

                                        if (!order?.payment_info?.length &&
                                            !hydratingRef.current &&
                                            newRows.length > 0) {
                                            markUserEdited();
                                        }
                                        scheduleSalesRecompute();
                                    }}
                                    onBusyChange={handleShippingBusyChange}
                                />
                            );
                        })()}

                        <SpecialFeePicker
                            key={`fee-${effectiveBuyerMembershipId || "no-buyer"}-${(editSpecialFeeInitialIds || []).join("-") || "none"}`}
                            ref={feeRef}
                            token={token!}
                            supplierId={sellerOrgId}
                            personId={effectiveBuyerMembershipId || ""}
                            orderId={String((order as any)?._id || order?.id || orderId || "").trim()}
                            shippingAddress={shippingAddrForTax}
                            taxStatus={order?.order_tax_summary?.tax_status}
                            onPayloadChange={(p) => {
                                if (hydratingRef.current) return;
                                markUserEdited();
                                const j = JSON.stringify(p?.specialfee ?? []);
                                if (j !== lastFeeJsonRef.current) {
                                    lastFeeJsonRef.current = j;
                                    const nextFeePayload: FeePayload = {
                                        specialfee: p?.specialfee ?? [],
                                        selected_fee_ids: p?.selected_fee_ids ?? [],
                                        total_amount: asNum(p?.total_amount),
                                        total_tax_amount: asNum(p?.total_tax_amount),
                                        total_tax_percentage: asNum(p?.total_tax_percentage),
                                    };
                                    feePayloadRef.current = nextFeePayload;
                                    setFeePayload(nextFeePayload);
                                    scheduleSalesRecompute({ feePayload: nextFeePayload });
                                    // scheduleAutoPatch();
                                }
                            }}
                            initialSelectedIds={editSpecialFeeInitialIds}
                            onBusyChange={handleSpecialFeeBusyChange}
                            onSourceFeesLoaded={(fees) => {
                                specialFeeCatalogRef.current = fees;
                            }}
                        />
                        <ProjectGetCard
                            token={token!}
                            signageId={String(organizationId || sellerOrgId || "").trim()}
                            initialProjectId={
                                userClearedProjectRef.current
                                    ? undefined
                                    : linkedProjectId || projectIdFromQuery || (order as any)?.project_id || undefined
                            }
                            onSelectionChange={(sel) => {
                                if (!sel) userClearedProjectRef.current = true;
                                else userClearedProjectRef.current = false;
                                setLinkedProjectId(sel?.projectId ?? null);
                                setLinkedProjectNumber(sel?.projectNumber ?? null);
                            }}
                        />

                    </div>
                </div >
                <style>{`
.form-control.text-center::placeholder { text-align: center; }
.form-control.text-center::-webkit-input-placeholder { text-align: center; }
.form-control.text-center:-ms-input-placeholder { text-align: center; }

.signage-edit-order-products-table {
  table-layout: fixed;
  min-width: 1040px;
  width: 100%;
}

.signage-edit-order-products-table .seo-col-product { width: 28%; }
.signage-edit-order-products-table .seo-col-sku { width: 10%; }
.signage-edit-order-products-table .seo-col-qty { width: 7%; }
.signage-edit-order-products-table .seo-col-unit-price { width: 11%; }
.signage-edit-order-products-table .seo-col-changed-price { width: 12%; }
.signage-edit-order-products-table .seo-col-discount { width: 9%; }
.signage-edit-order-products-table .seo-col-tax { width: 8%; }
.signage-edit-order-products-table .seo-col-total { width: 11%; }
.signage-edit-order-products-table .seo-col-actions { width: 4%; }

.signage-edit-order-products-table th.table-headers {
  font-size: 0.78rem;
  line-height: 1.2;
  white-space: normal;
  vertical-align: middle;
  padding: 0.5rem 0.35rem;
}

.signage-edit-order-products-table .seo-unit-price-cell,
.signage-edit-order-products-table .seo-changed-price-cell,
.signage-edit-order-products-table .seo-qty-cell,
.signage-edit-order-products-table .seo-total-cell {
  padding-left: 0.35rem;
  padding-right: 0.35rem;
}

.signage-edit-order-products-table .seo-unit-price-value,
.signage-edit-order-products-table .seo-total-value {
  display: inline-block;
  white-space: nowrap;
}

.signage-edit-order-products-table .seo-price-input,
.signage-edit-order-products-table .seo-qty-input {
  display: block;
  width: 100%;
  min-width: 5.5rem;
  max-width: 7.5rem;
  margin: 0 auto;
  padding: 0.35rem 0.5rem;
  font-size: 0.875rem;
  box-sizing: border-box;
}

.signage-edit-order-products-table .seo-qty-input {
  min-width: 3.25rem;
  max-width: 4.5rem;
}

@media (max-width: 767.98px) {
  .signage-edit-order-products-table {
    min-width: 980px;
  }

  .signage-edit-order-products-table th.table-headers {
    font-size: 0.72rem;
    padding: 0.45rem 0.25rem;
  }

  .signage-edit-order-products-table .seo-price-input {
    min-width: 6.5rem;
    max-width: 8rem;
    padding: 0.4rem 0.55rem;
  }
}
                `}</style>
            </div >

        </>
    );
};


export default SignageEditOrder;























{/* <div className="d-flex justify-content-between py-1">
                                                                    <span className="order-card-title">Sub Total:</span>
                                                                    <span className="order-card-cells">${subTotalToShow.toFixed(2)}</span>
                                                                </div>

                                                                <div className="d-flex justify-content-between py-1">
                                                                    <span className="order-card-title">
                                                                        Discount {codeToShow ? <span className="text-muted">({codeToShow})</span> : null}:
                                                                    </span>
                                                                    <span
                                                                        className="order-card-cells"
                                                                        style={{ color: discountToShow > 0 ? "#00a63e" : "#6c757d" }}
                                                                    >
                                                                        {discountToShow > 0 ? `-$${discountToShow.toFixed(2)}` : "$0.00"}
                                                                    </span>
                                                                </div>


                                                                <div className="d-flex justify-content-between py-1 align-items-center">
                                                                    <span className="order-card-title">Shipping Charge:</span>
                                                                    {isEditingShipping ? (
                                                                        <div className="d-flex align-items-center gap-2">
                                                                            <input
                                                                                type="number"
                                                                                className="form-control form-control-sm"
                                                                                style={{ width: "100px" }}
                                                                                value={manualShippingAmount}
                                                                                onChange={(e) => setManualShippingAmount(e.target.value)}
                                                                                onKeyDown={(e) => {
                                                                                    if (e.key === "Enter") handleSaveShipping();
                                                                                    if (e.key === "Escape") handleCancelEditShipping();
                                                                                }}
                                                                                autoFocus
                                                                            />
                                                                            <button
                                                                                type="button"
                                                                                className="btn btn-sm btn-success p-1"
                                                                                onClick={handleSaveShipping}
                                                                                title="Save"
                                                                            >
                                                                                <i className="ri-check-line fs-14" />
                                                                            </button>
                                                                            <button
                                                                                type="button"
                                                                                className="btn btn-sm btn-secondary p-1"
                                                                                onClick={handleCancelEditShipping}
                                                                                title="Cancel"
                                                                            >
                                                                                <i className="ri-close-line fs-14" />
                                                                            </button>
                                                                        </div>
                                                                    ) : (
                                                                        <div className="d-flex align-items-center gap-2">
                                                                    <span className="order-card-cells">
                                                                                ${(n(shipPayload?.shipping_option?.reduce((s: number, r: any) => s + n(r.shipping_amount), 0)) || shippingAmountToShow).toFixed(2)}
                                                                    </span>
                                                                            {!order?.payment_info?.length && (
                                                                                <button
                                                                                    type="button"
                                                                                    className="btn btn-sm btn-outline-primary p-1"
                                                                                    onClick={handleEditShipping}
                                                                                    title="Edit shipping charge"
                                                                                    style={{ lineHeight: 1 }}
                                                                                >
                                                                                    <i className="ri-edit-line" />
                                                                                </button>
                                                                            )}
                                                                        </div>
                                                                    )}
                                                                </div>

                                                                <div className="d-flex justify-content-between py-1">
                                                                    <span className="order-card-title">Special Fee:</span>
                                                                    <span className="order-card-cells">
                                                                        {specialFeeAmountToShow > 0 ? `$${specialFeeAmountToShow.toFixed(2)}` : "TBD"}
                                                                    </span>
                                                                </div>
                                                                <div className="py-1">
                                                                    <div className="d-flex justify-content-between align-items-center">
                                                                        <button
                                                                            type="button"
                                                                            onClick={() => setShowTaxDetails(v => !v)}
                                                                            className="btn btn-link p-0 text-decoration-none d-inline-flex align-items-center"
                                                                            aria-expanded={showTaxDetails}
                                                                            aria-controls="tax-breakdown"
                                                                        >
                                                                            <span className="order-card-title">Tax :</span>
                                                                        </button>
                                                                        <span className="order-card-cells">
                                                                            <span>{money(totalTaxAll)}</span>
                                                                        </span>
                                                                    </div>

                                                                    {showTaxDetails && (
                                                                        <div id="tax-breakdown" className="border rounded-3 p-2 bg-light mt-1">
                                                                            <div className="d-flex justify-content-between small mb-1">
                                                                                <span>Products Tax</span>
                                                                                <span className="order-card-cells">${productTaxToShow.toFixed(2)}</span>
                                                                            </div>
                                                                            <div className="d-flex justify-content-between small mb-1">
                                                                                <span>Shipping Tax</span>
                                                                                <span className="order-card-cells">${shippingTaxToShow.toFixed(2)}</span>
                                                                            </div>
                                                                            <div className="d-flex justify-content-between small">
                                                                                <span>Special Fee Tax</span>
                                                                                <span className="order-card-cells">${feeTaxToShow.toFixed(2)}</span>
                                                                            </div>
                                                                        </div>
                                                                    )}
                                                                </div>
 */}

{/* <hr className="my-2" />
                                                                <div className="d-flex justify-content-between pt-1">
                                                                    <strong className="order-card-title fs-19">Total (USD):</strong>
                                                                    <span className="order-card-cells fw-semibold">${grandTotal.toFixed(2)}</span>
                                                                </div> */}



// user select flow


{/* <div className="col-md-6 mb-3" id="row-overlap"> */ }
{/* <label htmlFor="userType" className="form-label">
                                                                Buyer Type
                                                            </label> */}
{/* <UIReactSelectPro
                                                            formik={formik}
                                                            name="userType"
                                                            label="Buyer Type"
                                                            required
                                                            options={buyerOptions}
                                                            value={buyerOptions?.find(o => o.value === formik.values.userType) ?? null}
                                                            onChange={(opt) => fetchBrokerageFirms(opt?.value || "")}
                                                            onBlur={formik.handleBlur}  // now allowed
                                                            isDisabled={true}           // alias supported (you can also use `disabled`)
                                                        /> */}

{/* <Select
                                                                className="br-8"
                                                                id="userType"
                                                                name="userType"
                                                                options={buyerOptions}
                                                                value={buyerOptions?.find(
                                                                    (option) =>
                                                                        option.value === formik.values.userType
                                                                )}
                                                                onChange={(selectedOption) =>
                                                                    fetchBrokerageFirms(selectedOption?.value || "")
                                                                }
                                                                onBlur={formik.handleBlur}
                                                                isDisabled={true}
                                                            /> */}

{/* </div> */ }




// -----------------------------

{/* <AsyncPaginate
                                                                    classNamePrefix="rs"
                                                                    placeholder="Search agents..."
                                                                    value={selectedAgent ? {
                                                                        value: selectedAgent.value,
                                                                        label: selectedAgent.label,
                                                                        firstName: selectedAgent.firstName,
                                                                        lastName: selectedAgent.lastName,
                                                                        email: selectedAgent.email,
                                                                        phone: selectedAgent.phone,
                                                                    } : null}
                                                                    loadOptions={loadAgentOptions}
                                                                    onChange={(selected) => {
                                                                        if (selected) {
                                                                            setSelectedAgent(selected);
                                                                            formik.setFieldValue("userFirstName", selected.firstName || "");
                                                                            formik.setFieldValue("userLastName", selected.lastName || "");
                                                                            formik.setFieldValue("userEmail", selected.email || "");
                                                                            formik.setFieldValue("userPhone", selected.phone || "");
                                                                        } else {
                                                                            setSelectedAgent(null);
                                                                            formik.setFieldValue("userFirstName", "");
                                                                            formik.setFieldValue("userLastName", "");
                                                                            formik.setFieldValue("userEmail", "");
                                                                            formik.setFieldValue("userPhone", "");
                                                                        }
                                                                    }}
                                                                    onBlur={() => formik.setFieldTouched("agentSelection", true)}
                                                                    additional={{ page: 1 }}
                                                                    defaultOptions
                                                                    debounceTimeout={300}
                                                                    isClearable
                                                                    isDisabled={true}
                                                                    components={{ DropdownIndicator }}
                                                                    styles={customSelectStyles}
                                                                /> */}
// -------------------------------------------

// console.log("[ORDER:SHIPPING_TOTALS] amount:", couponItems, "tax:", effectiveMapdata);


// 🔄 whenever ShippingOptions payload changes, update amounts in parent
//   useEffect(() => {
//     const rows = Array.isArray(shipPayloadState?.shipping_option)
//       ? shipPayloadState.shipping_option
//       : [];

//     const amt = rows.reduce((s: number, r: any) => s + Number(r?.shipping_amount || 0), 0);
//     const tax = rows.reduce((s: number, r: any) => s + Number(r?.shipping_tax_amount || 0), 0);

//     setShippingAmount(amt);
//     setShippingTaxAmount(tax);
//     console.log("[ORDER:SHIPPING_TOTALS] amount:", amt, "tax:", tax);
//   }, [shipPayloadState]);

//   // 🔄 whenever SpecialFee payload changes, update amounts in parent
//   useEffect(() => {
//     const rows = Array.isArray(feePayloadState?.specialfee)
//       ? feePayloadState.specialfee
//       : [];
//     console.log("Special Fee Rows:", rows);
//     const amt = rows.reduce((s: number, r: any) => s + Number(r?.specialfee_amount || 0), 0);
//     const tax = rows.reduce((s: number, r: any) => s + Number(r?.specialfee_tax_amount || 0), 0);

//     setSpecialFeeAmount(amt);
//     setSpecialFeeTaxAmount(tax);
//     console.log("[ORDER:SPECIAL_FEE_TOTALS] amount:", amt, "tax:", tax);
//   }, [feePayloadState]);

// ✅ Removed unused otherCharges, totalDiscount, chargesWithoutTax, taxesAll - not needed with cleaner logic



// ----------------------------------------------------------------------------------------------------------


 {/* <label htmlFor="billingAddressId" className="form-label">
                                                                    Select Billing Address
                                                                </label>
                                                                <Select
                                                                    id="billingAddressId"
                                                                    name="billingAddressId"
                                                                    options={addressOptions}
                                                                    value={
                                                                        // selectedBillingAddressOption
                                                                        addressOptions?.find(
                                                                            (option) => option.value === formik.values.billingAddressId
                                                                        ) || null
                                                                    }
                                                                    onChange={(selected) => {
                                                                        formik.setFieldValue("billingAddressId", selected?.value || "");
                                                                        const isOffice = !selectedOfficeAgent && !selectedAgent && selectedOffice;
                                                                        const selectedAddress = addressObjects?.find((addr) => addr._id === selected?.value);
                                                                        if (isOffice) {
                                                                            setSelectedOfficeBillingAddress(selectedAddress || null);
                                                                        } else {
                                                                            setSelectedBillingAddress(selectedAddress || null);
                                                                        }
                                                                    }}
                                                                    isDisabled={!!order?.payment_info?.length}

                                                                    // onChange={(selected: any) => {
                                                                    //     // ✅ Set billingAddressId to value (NOT _id)
                                                                    //     formik.setFieldValue("billingAddressId", selected?.value);

                                                                    //     const isOffice = !selectedOfficeAgent && !selectedAgent && selectedOffice;

                                                                    //     if (isOffice) {
                                                                    //         const selectedAddress = addressObjects?.find(
                                                                    //             (addr) => addr._id === selected._id
                                                                    //         );
                                                                    //         setSelectedOfficeBillingAddress(selectedAddress || null);
                                                                    //     } else {
                                                                    //         const agentBillingAddress = addressObjects?.find(
                                                                    //             (addr) => addr._id === selected._id
                                                                    //         );
                                                                    //         setSelectedBillingAddress(agentBillingAddress.address_details || null);
                                                                    //     }
                                                                    // }}
                                                                    // isDisabled={["Paid", "Partial Payment", "Overpaid"].includes((order?.payment_status || "") as string)}
                                                                    unstyled
                                                                    classNames={{
                                                                        control: () => 'my-select-control',
                                                                        menu: () => 'my-select-menu',
                                                                        option: (state) =>
                                                                            `my-select-option ${state.isSelected ? 'is-selected' : ''} ${state.isFocused ? 'my-option-focused' : ''}`,
                                                                        container: () => 'my-select-container',
                                                                    }}
                                                                    styles={{
                                                                        menu: (base) => ({
                                                                            ...base,
                                                                            maxHeight: '200px',
                                                                            overflowY: 'auto',
                                                                            zIndex: 5,
                                                                        }),
                                                                    }}
                                                                    placeholder="- Select -"
                                                                /> */}
